import type { Locale } from '@/lib/i18n/dictionaries';

export type LegalBlock =
  | { type: 'p'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'h3'; text: string };

export interface LegalSection {
  title: string;
  blocks: LegalBlock[];
}

export interface LegalPageContent {
  kicker: string;
  title: string;
  updatedLabel: string;
  /** Fixed edition date of the document. When absent the page falls back to
   *  today's date — legal texts should always set it so the shown date is the
   *  real revision date, not the day the page was opened. */
  updatedValue?: string;
  sections: LegalSection[];
}

type LegalPages = Record<'terms' | 'privacy' | 'offer' | 'payment' | 'refund', Record<Locale, LegalPageContent>>;

export const legalPages: LegalPages = {
  "terms": {
    "en": {
      "kicker": "Legal",
      "title": "Terms of Service",
      "updatedLabel": "Last updated",
      "sections": [
        {
          "title": "1. Agreement to Terms",
          "blocks": [
            {
              "type": "p",
              "text": "By accessing or using CANNECT.AI, you agree to be bound by these Terms of Service. If you do not agree, you may not use the platform."
            },
            {
              "type": "p",
              "text": "These Terms form a legally binding agreement between you and CANNECT.AI for use of our video advertising platform and connected LED station network."
            },
            {
              "type": "p",
              "text": "The platform is operated by CANNECT.AI LLP (ТОО «CANNECT.AI»), BIN 251140014640, registered at: Republic of Kazakhstan, Almaty, Medeu district, ST Koktobe 1, building 48."
            }
          ]
        },
        {
          "title": "2. Description of Service",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI enables users to:"
            },
            {
              "type": "list",
              "items": [
                "Upload and manage video advertisements",
                "Generate AI-powered video content",
                "Display ads on connected CANNECT LED station screens",
                "Manage campaigns, placements, budgets, and analytics",
                "Use moderation and compliance review services"
              ]
            }
          ]
        },
        {
          "title": "3. Eligibility",
          "blocks": [
            {
              "type": "p",
              "text": "To use the platform, you must be at least 18 years old, have legal capacity to enter contracts, comply with applicable advertising laws, and not be prohibited from using the service."
            }
          ]
        },
        {
          "title": "4. Account Registration",
          "blocks": [
            {
              "type": "h3",
              "text": "4.1 Account Creation"
            },
            {
              "type": "p",
              "text": "You may create an account with email and password or supported OAuth providers."
            },
            {
              "type": "h3",
              "text": "4.2 Account Security"
            },
            {
              "type": "p",
              "text": "You are responsible for your credentials, all activity under your account, accurate account information, and prompt notice of unauthorized access."
            },
            {
              "type": "h3",
              "text": "4.3 Account Termination"
            },
            {
              "type": "p",
              "text": "We may suspend or terminate accounts that violate these Terms or engage in prohibited activity."
            }
          ]
        },
        {
          "title": "5. Subscription Plans and Billing",
          "blocks": [
            {
              "type": "p",
              "text": "Plans may include Free, Basic, Pro, or custom business tiers. Paid services may use subscriptions, tokens, or campaign-based pricing."
            },
            {
              "type": "list",
              "items": [
                "Payments from customers in Kazakhstan are processed online through the PayLink.kz acquiring service (Visa / Mastercard)",
                "All prices are shown in Kazakhstani tenge (₸)",
                "Subscriptions renew automatically unless canceled",
                "Fees are refundable in accordance with the laws of the Republic of Kazakhstan",
                "Prices may change with prior notice"
              ]
            },
            {
              "type": "p",
              "text": "Tokens may be used for video uploads, AI generation, campaign placement, and other platform features. Token rules may vary by plan."
            }
          ]
        },
        {
          "title": "6. Content Guidelines and Restrictions",
          "blocks": [
            {
              "type": "p",
              "text": "All content must comply with applicable laws, advertising rules, venue requirements, and CANNECT technical specifications."
            },
            {
              "type": "p",
              "text": "You may not upload content that is unlawful, misleading, infringing, explicit, offensive, targeted at minors, or that promotes prohibited goods, activities, or unsupported claims."
            },
            {
              "type": "p",
              "text": "All content may be reviewed through automated tools and human moderation before display."
            }
          ]
        },
        {
          "title": "7. Intellectual Property Rights",
          "blocks": [
            {
              "type": "p",
              "text": "You retain ownership of content you upload. You grant CANNECT.AI a non-exclusive license to store, process, review, display, distribute to connected stations, and create previews of your content for platform operation."
            },
            {
              "type": "p",
              "text": "The platform, software, designs, trademarks, and non-user content are owned by CANNECT.AI or its licensors."
            }
          ]
        },
        {
          "title": "8. User Responsibilities",
          "blocks": [
            {
              "type": "list",
              "items": [
                "Provide accurate information",
                "Comply with laws and platform rules",
                "Obtain all required content rights and permissions",
                "Avoid disrupting or reverse engineering the platform",
                "Do not attempt unauthorized access or impersonation"
              ]
            }
          ]
        },
        {
          "title": "9. Disclaimers and Limitations of Liability",
          "blocks": [
            {
              "type": "p",
              "text": "The platform is provided “as is” and “as available” without warranties of any kind to the maximum extent permitted by law."
            },
            {
              "type": "p",
              "text": "CANNECT.AI is not liable for indirect, incidental, special, consequential, or punitive damages arising from use of the platform."
            },
            {
              "type": "p",
              "text": "We do not guarantee specific campaign results, views, engagement, sales, or conversions."
            }
          ]
        },
        {
          "title": "10. Indemnification",
          "blocks": [
            {
              "type": "p",
              "text": "You agree to indemnify CANNECT.AI against claims, damages, losses, and expenses arising from your use of the platform, your content, your campaigns, or your violation of laws or third-party rights."
            }
          ]
        },
        {
          "title": "11. Termination",
          "blocks": [
            {
              "type": "p",
              "text": "Either party may terminate this agreement. After termination, access may end, unused subscription time may not be refunded, and content may be deleted after a retention period."
            }
          ]
        },
        {
          "title": "12. Modifications to Terms",
          "blocks": [
            {
              "type": "p",
              "text": "We may update these Terms. Material changes may be communicated by email, platform notice, or by posting the updated Terms. Continued use means acceptance of the updated Terms."
            }
          ]
        },
        {
          "title": "13. Governing Law and Disputes",
          "blocks": [
            {
              "type": "p",
              "text": "These Terms are governed by the laws of the Republic of Kazakhstan. Disputes should first be addressed through good-faith negotiation and, failing agreement, are resolved by the courts of the Republic of Kazakhstan at the location of CANNECT.AI LLP."
            }
          ]
        },
        {
          "title": "14. Miscellaneous",
          "blocks": [
            {
              "type": "p",
              "text": "These Terms are the entire agreement for platform use. If any provision is unenforceable, the remaining provisions continue. Failure to enforce a provision is not a waiver."
            }
          ]
        },
        {
          "title": "15. Contact Information",
          "blocks": [
            {
              "type": "p",
              "text": "For questions about these Terms, contact us:"
            },
            {
              "type": "list",
              "items": [
                "Email: legal@cannect.ai",
                "Support: support@cannect.ai",
                "Website: https://cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "ru": {
      "kicker": "Юридическая информация",
      "title": "Условия использования",
      "updatedLabel": "Обновлено",
      "sections": [
        {
          "title": "1. Принятие условий",
          "blocks": [
            {
              "type": "p",
              "text": "Используя CANNECT.AI, вы соглашаетесь соблюдать эти Условия использования. Если вы не согласны, вы не можете пользоваться платформой."
            },
            {
              "type": "p",
              "text": "Эти Условия являются юридически обязательным соглашением между вами и CANNECT.AI в отношении видеорекламной платформы и сети подключенных LED-станций."
            },
            {
              "type": "p",
              "text": "Платформой управляет ТОО «CANNECT.AI» (БИН 251140014640), юридический адрес: Республика Казахстан, г. Алматы, Медеуский район, СТ Коктобе 1, д. 48."
            }
          ]
        },
        {
          "title": "2. Описание сервиса",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI позволяет пользователям:"
            },
            {
              "type": "list",
              "items": [
                "Загружать и управлять видеорекламой",
                "Создавать видео с помощью AI",
                "Размещать рекламу на подключенных LED-экранах CANNECT",
                "Управлять кампаниями, размещениями, бюджетами и аналитикой",
                "Использовать модерацию и проверку соответствия требованиям"
              ]
            }
          ]
        },
        {
          "title": "3. Право на использование",
          "blocks": [
            {
              "type": "p",
              "text": "Для использования платформы вам должно быть не менее 18 лет, вы должны иметь право заключать договоры, соблюдать применимые рекламные законы и не иметь запрета на использование сервиса."
            }
          ]
        },
        {
          "title": "4. Регистрация аккаунта",
          "blocks": [
            {
              "type": "h3",
              "text": "4.1 Создание аккаунта"
            },
            {
              "type": "p",
              "text": "Вы можете создать аккаунт по email и паролю или через поддерживаемые OAuth-провайдеры."
            },
            {
              "type": "h3",
              "text": "4.2 Безопасность аккаунта"
            },
            {
              "type": "p",
              "text": "Вы отвечаете за сохранность учетных данных, действия в аккаунте, актуальность информации и своевременное уведомление о несанкционированном доступе."
            },
            {
              "type": "h3",
              "text": "4.3 Прекращение доступа"
            },
            {
              "type": "p",
              "text": "Мы можем приостановить или закрыть аккаунт при нарушении этих Условий или запрещенной активности."
            }
          ]
        },
        {
          "title": "5. Тарифы и оплата",
          "blocks": [
            {
              "type": "p",
              "text": "Тарифы могут включать Free, Basic, Pro или индивидуальные бизнес-планы. Платные услуги могут оплачиваться подпиской, токенами или по параметрам кампании."
            },
            {
              "type": "list",
              "items": [
                "Платежи клиентов из Казахстана обрабатываются онлайн через сервис эквайринга PayLink.kz (Visa / Mastercard)",
                "Все цены указаны в казахстанских тенге (₸)",
                "Подписки продлеваются автоматически, если не отменены",
                "Возврат средств осуществляется в соответствии с законодательством Республики Казахстан",
                "Цены могут меняться с предварительным уведомлением"
              ]
            },
            {
              "type": "p",
              "text": "Токены могут использоваться для загрузки видео, AI-генерации, размещения кампаний и других функций платформы. Правила токенов зависят от тарифа."
            }
          ]
        },
        {
          "title": "6. Требования к контенту",
          "blocks": [
            {
              "type": "p",
              "text": "Контент должен соответствовать законам, рекламным правилам, требованиям площадок и техническим спецификациям CANNECT."
            },
            {
              "type": "p",
              "text": "Запрещен незаконный, вводящий в заблуждение, нарушающий права, откровенный, оскорбительный, ориентированный на несовершеннолетних контент, а также продвижение запрещенных товаров, действий или неподтвержденных заявлений."
            },
            {
              "type": "p",
              "text": "Контент может проходить автоматическую и ручную модерацию перед показом."
            }
          ]
        },
        {
          "title": "7. Права интеллектуальной собственности",
          "blocks": [
            {
              "type": "p",
              "text": "Вы сохраняете права на загруженный контент. Вы предоставляете CANNECT.AI неисключительную лицензию на хранение, обработку, проверку, показ, доставку на станции и создание превью для работы платформы."
            },
            {
              "type": "p",
              "text": "Платформа, программное обеспечение, дизайн, товарные знаки и не пользовательский контент принадлежат CANNECT.AI или правообладателям."
            }
          ]
        },
        {
          "title": "8. Обязанности пользователя",
          "blocks": [
            {
              "type": "list",
              "items": [
                "Предоставлять точную информацию",
                "Соблюдать законы и правила платформы",
                "Получать необходимые права и разрешения на контент",
                "Не нарушать работу платформы и не выполнять обратную разработку",
                "Не пытаться получить несанкционированный доступ и не выдавать себя за других"
              ]
            }
          ]
        },
        {
          "title": "9. Отказ от гарантий и ограничение ответственности",
          "blocks": [
            {
              "type": "p",
              "text": "Платформа предоставляется “как есть” и “как доступно” без гарантий любого вида в максимально допустимой законом степени."
            },
            {
              "type": "p",
              "text": "CANNECT.AI не несет ответственности за косвенные, случайные, специальные, последующие или штрафные убытки, связанные с использованием платформы."
            },
            {
              "type": "p",
              "text": "Мы не гарантируем конкретные результаты кампаний, просмотры, вовлеченность, продажи или конверсии."
            }
          ]
        },
        {
          "title": "10. Возмещение убытков",
          "blocks": [
            {
              "type": "p",
              "text": "Вы соглашаетесь защищать CANNECT.AI от претензий, убытков, потерь и расходов, связанных с вашим использованием платформы, контентом, кампаниями или нарушением законов и прав третьих лиц."
            }
          ]
        },
        {
          "title": "11. Прекращение действия",
          "blocks": [
            {
              "type": "p",
              "text": "Любая сторона может прекратить соглашение. После прекращения доступ может быть закрыт, неиспользованное время подписки может не возвращаться, а контент может быть удален после периода хранения."
            }
          ]
        },
        {
          "title": "12. Изменение условий",
          "blocks": [
            {
              "type": "p",
              "text": "Мы можем обновлять эти Условия. Существенные изменения могут направляться по email, через уведомление в платформе или публикацией новой версии. Продолжение использования означает принятие обновленных Условий."
            }
          ]
        },
        {
          "title": "13. Применимое право и споры",
          "blocks": [
            {
              "type": "p",
              "text": "Эти Условия регулируются законодательством Республики Казахстан. Споры сначала решаются путём добросовестных переговоров, а при недостижении согласия — в судах Республики Казахстан по месту нахождения ТОО «CANNECT.AI»."
            }
          ]
        },
        {
          "title": "14. Прочие положения",
          "blocks": [
            {
              "type": "p",
              "text": "Эти Условия являются полным соглашением по использованию платформы. Если положение признано недействительным, остальные положения сохраняют силу. Неисполнение права не является отказом от него."
            }
          ]
        },
        {
          "title": "15. Контакты",
          "blocks": [
            {
              "type": "p",
              "text": "По вопросам этих Условий свяжитесь с нами:"
            },
            {
              "type": "list",
              "items": [
                "Email: legal@cannect.ai",
                "Поддержка: support@cannect.ai",
                "Сайт: https://cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "kk": {
      "kicker": "Құқықтық ақпарат",
      "title": "Қызмет көрсету шарттары",
      "updatedLabel": "Жаңартылған күні",
      "sections": [
        {
          "title": "1. Шарттарды қабылдау",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI платформасын пайдалану арқылы сіз осы Қызмет көрсету шарттарын сақтауға келісесіз. Келіспесеңіз, платформаны пайдалана алмайсыз."
            },
            {
              "type": "p",
              "text": "Бұл Шарттар сіз бен CANNECT.AI арасындағы видеожарнама платформасы және қосылған LED станциялар желісін пайдалану бойынша заңды келісім болып табылады."
            },
            {
              "type": "p",
              "text": "Платформаны «CANNECT.AI» ЖШС (БСН 251140014640) басқарады, заңды мекенжайы: Қазақстан Республикасы, Алматы қ., Медеу ауданы, Көктөбе 1 СА, 48-үй."
            }
          ]
        },
        {
          "title": "2. Қызмет сипаттамасы",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI пайдаланушыларға мыналарды жасауға мүмкіндік береді:"
            },
            {
              "type": "list",
              "items": [
                "Видеожарнамаларды жүктеу және басқару",
                "AI арқылы видео контент жасау",
                "Жарнаманы CANNECT LED экрандарында көрсету",
                "Кампанияларды, орналастыруларды, бюджеттерді және аналитиканы басқару",
                "Модерация және сәйкестік тексеру қызметтерін пайдалану"
              ]
            }
          ]
        },
        {
          "title": "3. Пайдалану құқығы",
          "blocks": [
            {
              "type": "p",
              "text": "Платформаны пайдалану үшін жасыңыз кемінде 18-де болуы, шарт жасасуға құқықтық қабілетіңіз болуы, қолданылатын жарнама заңдарын сақтауыңыз және сервиске кіруге тыйым салынбаған болуы керек."
            }
          ]
        },
        {
          "title": "4. Аккаунт тіркеу",
          "blocks": [
            {
              "type": "h3",
              "text": "4.1 Аккаунт жасау"
            },
            {
              "type": "p",
              "text": "Аккаунтты email және құпиясөз арқылы немесе қолдау көрсетілетін OAuth провайдерлері арқылы жасай аласыз."
            },
            {
              "type": "h3",
              "text": "4.2 Аккаунт қауіпсіздігі"
            },
            {
              "type": "p",
              "text": "Сіз тіркелгі деректерінің құпиялығына, аккаунттағы әрекеттерге, ақпараттың дұрыстығына және рұқсатсыз кіру туралы уақытылы хабарлауға жауаптысыз."
            },
            {
              "type": "h3",
              "text": "4.3 Аккаунтты тоқтату"
            },
            {
              "type": "p",
              "text": "Осы Шарттар бұзылса немесе тыйым салынған әрекет жасалса, біз аккаунтты уақытша тоқтата немесе жаба аламыз."
            }
          ]
        },
        {
          "title": "5. Тарифтер және төлем",
          "blocks": [
            {
              "type": "p",
              "text": "Тарифтер Free, Basic, Pro немесе жеке бизнес жоспарларды қамтуы мүмкін. Ақылы қызметтер жазылым, токендер немесе кампания параметрлері бойынша төленуі мүмкін."
            },
            {
              "type": "list",
              "items": [
                "Қазақстандағы клиенттердің төлемдері PayLink.kz эквайринг қызметі арқылы онлайн өңделеді (Visa / Mastercard)",
                "Барлық бағалар қазақстандық теңгемен (₸) көрсетілген",
                "Жазылымдар тоқтатылмаса автоматты түрде жаңарады",
                "Қаражатты қайтару Қазақстан Республикасының заңнамасына сәйкес жүргізіледі",
                "Бағалар алдын ала хабарламамен өзгеруі мүмкін"
              ]
            },
            {
              "type": "p",
              "text": "Токендер видео жүктеу, AI генерация, кампания орналастыру және басқа функциялар үшін қолданылуы мүмкін. Токен ережелері тарифке байланысты."
            }
          ]
        },
        {
          "title": "6. Контент талаптары",
          "blocks": [
            {
              "type": "p",
              "text": "Контент заңдарға, жарнама ережелеріне, орын талаптарына және CANNECT техникалық сипаттамаларына сәйкес болуы керек."
            },
            {
              "type": "p",
              "text": "Заңсыз, жаңылыстыратын, құқық бұзатын, ашық, қорлайтын, кәмелетке толмағандарға бағытталған контентке, сондай-ақ тыйым салынған тауарлар, әрекеттер немесе дәлелденбеген мәлімдемелерді ілгерілетуге болмайды."
            },
            {
              "type": "p",
              "text": "Контент көрсетілмес бұрын автоматты және адам модерациясынан өтуі мүмкін."
            }
          ]
        },
        {
          "title": "7. Зияткерлік меншік құқықтары",
          "blocks": [
            {
              "type": "p",
              "text": "Жүктеген контентке құқық сізде қалады. Платформаның жұмысы үшін сіз CANNECT.AI-ға контентті сақтау, өңдеу, тексеру, көрсету, станцияларға жеткізу және превью жасауға айрықша емес лицензия бересіз."
            },
            {
              "type": "p",
              "text": "Платформа, бағдарламалық жасақтама, дизайн, тауар белгілері және пайдаланушыға жатпайтын контент CANNECT.AI немесе құқық иелеріне тиесілі."
            }
          ]
        },
        {
          "title": "8. Пайдаланушы міндеттері",
          "blocks": [
            {
              "type": "list",
              "items": [
                "Дұрыс ақпарат беру",
                "Заңдар мен платформа ережелерін сақтау",
                "Контентке қажетті құқықтар мен рұқсаттарды алу",
                "Платформа жұмысын бұзбау және кері инженерия жасамау",
                "Рұқсатсыз кіруге немесе басқа адам болып көрінуге тырыспау"
              ]
            }
          ]
        },
        {
          "title": "9. Кепілдіктерден бас тарту және жауапкершілікті шектеу",
          "blocks": [
            {
              "type": "p",
              "text": "Платформа заң рұқсат ететін ең жоғары деңгейде ешқандай кепілдіксіз “сол күйінде” және “қолжетімді күйінде” беріледі."
            },
            {
              "type": "p",
              "text": "CANNECT.AI платформаны пайдаланудан туындайтын жанама, кездейсоқ, арнайы, салдарлық немесе айыппұлдық залалдарға жауапты емес."
            },
            {
              "type": "p",
              "text": "Біз кампанияның нақты нәтижелеріне, қаралымға, әрекеттесуге, сатылымға немесе конверсияға кепілдік бермейміз."
            }
          ]
        },
        {
          "title": "10. Залалды өтеу",
          "blocks": [
            {
              "type": "p",
              "text": "Сіз платформаны пайдалануыңыздан, контентіңізден, кампанияларыңыздан немесе заңдар мен үшінші тарап құқықтарын бұзудан туындайтын талаптар, залалдар және шығындар бойынша CANNECT.AI-ды қорғауға келісесіз."
            }
          ]
        },
        {
          "title": "11. Тоқтату",
          "blocks": [
            {
              "type": "p",
              "text": "Кез келген тарап келісімді тоқтата алады. Тоқтатылғаннан кейін қолжетімділік жабылуы мүмкін, пайдаланылмаған жазылым уақыты қайтарылмауы мүмкін, ал контент сақтау мерзімінен кейін жойылуы мүмкін."
            }
          ]
        },
        {
          "title": "12. Шарттарды өзгерту",
          "blocks": [
            {
              "type": "p",
              "text": "Біз осы Шарттарды жаңарта аламыз. Маңызды өзгерістер email, платформа хабарламасы немесе жаңартылған нұсқаны жариялау арқылы хабарлануы мүмкін. Әрі қарай пайдалану жаңартылған Шарттарды қабылдауды білдіреді."
            }
          ]
        },
        {
          "title": "13. Қолданылатын құқық және даулар",
          "blocks": [
            {
              "type": "p",
              "text": "Бұл Шарттар Қазақстан Республикасының заңнамасымен реттеледі. Даулар алдымен адал келіссөздер арқылы, ал келісімге қол жеткізілмеген жағдайда «CANNECT.AI» ЖШС орналасқан жер бойынша Қазақстан Республикасының соттарында шешіледі."
            }
          ]
        },
        {
          "title": "14. Өзге ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Бұл Шарттар платформаны пайдалану бойынша толық келісім болып табылады. Егер бір ереже орындалмайтын деп танылса, қалған ережелер күшінде қалады. Құқықты қолданбау одан бас тарту болып саналмайды."
            }
          ]
        },
        {
          "title": "15. Байланыс",
          "blocks": [
            {
              "type": "p",
              "text": "Осы Шарттар бойынша сұрақтар үшін бізге хабарласыңыз:"
            },
            {
              "type": "list",
              "items": [
                "Email: legal@cannect.ai",
                "Қолдау: support@cannect.ai",
                "Сайт: https://cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "tr": {
      "kicker": "Yasal",
      "title": "Hizmet Şartları",
      "updatedLabel": "Son güncelleme",
      "sections": [
        {
          "title": "1. Şartların Kabulü",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI'ya erişerek veya onu kullanarak bu Hizmet Şartları'na bağlı kalmayı kabul edersiniz. Kabul etmiyorsanız platformu kullanamazsınız."
            },
            {
              "type": "p",
              "text": "Bu Şartlar, video reklam platformumuzun ve bağlı LED istasyon ağımızın kullanımına ilişkin sizinle CANNECT.AI arasında yasal olarak bağlayıcı bir anlaşma oluşturur."
            },
            {
              "type": "p",
              "text": "Platform, CANNECT.AI LLP (ТОО «CANNECT.AI») tarafından işletilmektedir, BIN 251140014640, kayıtlı adres: Kazakistan Cumhuriyeti, Almatı, Medeu ilçesi, ST Koktobe 1, bina 48."
            }
          ]
        },
        {
          "title": "2. Hizmetin Tanımı",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI kullanıcılara şunları sağlar:"
            },
            {
              "type": "list",
              "items": [
                "Video reklamları yükleme ve yönetme",
                "Yapay zeka destekli video içeriği oluşturma",
                "Bağlı CANNECT LED istasyon ekranlarında reklam gösterme",
                "Kampanyaları, yerleşimleri, bütçeleri ve analitiği yönetme",
                "Moderasyon ve uyumluluk inceleme hizmetlerini kullanma"
              ]
            }
          ]
        },
        {
          "title": "3. Uygunluk",
          "blocks": [
            {
              "type": "p",
              "text": "Platformu kullanmak için en az 18 yaşında olmalı, sözleşme yapma ehliyetine sahip olmalı, geçerli reklam yasalarına uymalı ve hizmeti kullanmanız yasaklanmamış olmalıdır."
            }
          ]
        },
        {
          "title": "4. Hesap Kaydı",
          "blocks": [
            {
              "type": "h3",
              "text": "4.1 Hesap Oluşturma"
            },
            {
              "type": "p",
              "text": "E-posta ve parola ile veya desteklenen OAuth sağlayıcıları ile bir hesap oluşturabilirsiniz."
            },
            {
              "type": "h3",
              "text": "4.2 Hesap Güvenliği"
            },
            {
              "type": "p",
              "text": "Kimlik bilgilerinizden, hesabınız altındaki tüm faaliyetlerden, hesap bilgilerinin doğruluğundan ve yetkisiz erişimin derhal bildirilmesinden siz sorumlusunuz."
            },
            {
              "type": "h3",
              "text": "4.3 Hesabın Sonlandırılması"
            },
            {
              "type": "p",
              "text": "Bu Şartları ihlal eden veya yasaklı faaliyette bulunan hesapları askıya alabilir veya sonlandırabiliriz."
            }
          ]
        },
        {
          "title": "5. Abonelik Planları ve Faturalama",
          "blocks": [
            {
              "type": "p",
              "text": "Planlar Free, Basic, Pro veya özel kurumsal kademeleri içerebilir. Ücretli hizmetler abonelik, token veya kampanya bazlı fiyatlandırma kullanabilir."
            },
            {
              "type": "list",
              "items": [
                "Kazakistan'daki müşterilerden alınan ödemeler PayLink.kz acquiring hizmeti aracılığıyla çevrimiçi işlenir (Visa / Mastercard)",
                "Tüm fiyatlar Kazakistan tengesi (₸) cinsinden gösterilir",
                "Abonelikler iptal edilmedikçe otomatik olarak yenilenir",
                "Ücretler Kazakistan Cumhuriyeti yasalarına uygun olarak iade edilir",
                "Fiyatlar önceden bildirimde bulunularak değişebilir"
              ]
            },
            {
              "type": "p",
              "text": "Token'lar video yüklemeleri, yapay zeka üretimi, kampanya yerleşimi ve diğer platform özellikleri için kullanılabilir. Token kuralları plana göre değişebilir."
            }
          ]
        },
        {
          "title": "6. İçerik Kuralları ve Kısıtlamalar",
          "blocks": [
            {
              "type": "p",
              "text": "Tüm içerikler geçerli yasalara, reklam kurallarına, mekan gereksinimlerine ve CANNECT teknik şartnamelerine uygun olmalıdır."
            },
            {
              "type": "p",
              "text": "Yasa dışı, yanıltıcı, ihlal edici, müstehcen, saldırgan, reşit olmayanlara yönelik veya yasaklı mal, faaliyet ya da desteklenmeyen iddiaları teşvik eden içerik yükleyemezsiniz."
            },
            {
              "type": "p",
              "text": "Tüm içerikler gösterilmeden önce otomatik araçlar ve insan moderasyonu yoluyla incelenebilir."
            }
          ]
        },
        {
          "title": "7. Fikri Mülkiyet Hakları",
          "blocks": [
            {
              "type": "p",
              "text": "Yüklediğiniz içeriğin mülkiyeti sizde kalır. CANNECT.AI'ya, platform işletimi için içeriğinizi saklama, işleme, inceleme, gösterme, bağlı istasyonlara dağıtma ve önizlemelerini oluşturma konusunda münhasır olmayan bir lisans verirsiniz."
            },
            {
              "type": "p",
              "text": "Platform, yazılım, tasarımlar, ticari markalar ve kullanıcıya ait olmayan içerikler CANNECT.AI'ya veya lisans verenlerine aittir."
            }
          ]
        },
        {
          "title": "8. Kullanıcı Sorumlulukları",
          "blocks": [
            {
              "type": "list",
              "items": [
                "Doğru bilgi sağlamak",
                "Yasalara ve platform kurallarına uymak",
                "Gerekli tüm içerik haklarını ve izinlerini almak",
                "Platformu kesintiye uğratmaktan veya tersine mühendislik yapmaktan kaçınmak",
                "Yetkisiz erişim veya kimlik taklidi girişiminde bulunmamak"
              ]
            }
          ]
        },
        {
          "title": "9. Sorumluluk Reddi ve Sorumluluk Sınırlamaları",
          "blocks": [
            {
              "type": "p",
              "text": "Platform, yasaların izin verdiği azami ölçüde herhangi bir garanti olmaksızın “olduğu gibi” ve “mevcut olduğu şekilde” sağlanır."
            },
            {
              "type": "p",
              "text": "CANNECT.AI, platformun kullanımından kaynaklanan dolaylı, arızi, özel, sonuçsal veya cezai zararlardan sorumlu değildir."
            },
            {
              "type": "p",
              "text": "Belirli kampanya sonuçlarını, görüntülenmeleri, etkileşimi, satışları veya dönüşümleri garanti etmiyoruz."
            }
          ]
        },
        {
          "title": "10. Tazminat",
          "blocks": [
            {
              "type": "p",
              "text": "Platformu kullanımınızdan, içeriğinizden, kampanyalarınızdan ya da yasaları veya üçüncü taraf haklarını ihlalinizden kaynaklanan talepler, zararlar, kayıplar ve masraflara karşı CANNECT.AI'yı tazmin etmeyi kabul edersiniz."
            }
          ]
        },
        {
          "title": "11. Sonlandırma",
          "blocks": [
            {
              "type": "p",
              "text": "Taraflardan biri bu anlaşmayı sonlandırabilir. Sonlandırma sonrasında erişim sona erebilir, kullanılmayan abonelik süresi iade edilmeyebilir ve içerik bir saklama süresinin ardından silinebilir."
            }
          ]
        },
        {
          "title": "12. Şartlarda Değişiklik",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Şartları güncelleyebiliriz. Önemli değişiklikler e-posta, platform bildirimi yoluyla veya güncellenmiş Şartların yayımlanmasıyla bildirilebilir. Kullanımın devam etmesi güncellenmiş Şartların kabulü anlamına gelir."
            }
          ]
        },
        {
          "title": "13. Uygulanacak Hukuk ve Uyuşmazlıklar",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Şartlar Kazakistan Cumhuriyeti yasalarına tabidir. Uyuşmazlıklar öncelikle iyi niyetli müzakere yoluyla ele alınmalı, anlaşmaya varılamaması halinde CANNECT.AI LLP'nin bulunduğu yerdeki Kazakistan Cumhuriyeti mahkemelerinde çözülmelidir."
            }
          ]
        },
        {
          "title": "14. Çeşitli Hükümler",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Şartlar platform kullanımına ilişkin anlaşmanın tamamını oluşturur. Herhangi bir hüküm uygulanamaz hale gelirse, kalan hükümler yürürlükte kalır. Bir hükmün uygulanmaması ondan feragat anlamına gelmez."
            }
          ]
        },
        {
          "title": "15. İletişim Bilgileri",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Şartlarla ilgili sorularınız için bize ulaşın:"
            },
            {
              "type": "list",
              "items": [
                "E-posta: legal@cannect.ai",
                "Destek: support@cannect.ai",
                "Web sitesi: https://cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "zh": {
      "kicker": "法律信息",
      "title": "服务条款",
      "updatedLabel": "最后更新",
      "sections": [
        {
          "title": "1. 接受条款",
          "blocks": [
            {
              "type": "p",
              "text": "访问或使用 CANNECT.AI 即表示您同意受本服务条款约束。如不同意，则不得使用本平台。"
            },
            {
              "type": "p",
              "text": "本条款构成您与 CANNECT.AI 之间就使用我们的视频广告平台及互联 LED 站点网络所达成的具有法律约束力的协议。"
            },
            {
              "type": "p",
              "text": "本平台由 CANNECT.AI LLP（ТОО «CANNECT.AI»）运营，BIN 251140014640，注册地址：哈萨克斯坦共和国，阿拉木图市，麦杰乌区，СТ Коктобе 1，48号楼。"
            }
          ]
        },
        {
          "title": "2. 服务说明",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI 使用户能够："
            },
            {
              "type": "list",
              "items": [
                "上传和管理视频广告",
                "借助 AI 生成视频内容",
                "在互联的 CANNECT LED 站点屏幕上展示广告",
                "管理广告活动、投放、预算和数据分析",
                "使用审核和合规性审查服务"
              ]
            }
          ]
        },
        {
          "title": "3. 使用资格",
          "blocks": [
            {
              "type": "p",
              "text": "使用本平台，您须年满 18 周岁，具备订立合同的法律行为能力，遵守适用的广告法律法规，且未被禁止使用本服务。"
            }
          ]
        },
        {
          "title": "4. 账户注册",
          "blocks": [
            {
              "type": "h3",
              "text": "4.1 账户创建"
            },
            {
              "type": "p",
              "text": "您可以通过电子邮箱和密码，或受支持的 OAuth 提供商创建账户。"
            },
            {
              "type": "h3",
              "text": "4.2 账户安全"
            },
            {
              "type": "p",
              "text": "您应对自己的登录凭证、账户下的所有活动、账户信息的准确性以及及时通知未经授权的访问承担责任。"
            },
            {
              "type": "h3",
              "text": "4.3 账户终止"
            },
            {
              "type": "p",
              "text": "对于违反本条款或从事禁止活动的账户，我们有权暂停或终止。"
            }
          ]
        },
        {
          "title": "5. 订阅套餐与计费",
          "blocks": [
            {
              "type": "p",
              "text": "套餐可包括 Free、Basic、Pro 或定制企业层级。付费服务可采用订阅、代币或基于广告活动的定价方式。"
            },
            {
              "type": "list",
              "items": [
                "哈萨克斯坦境内客户的付款通过 PayLink.kz 收单服务在线处理（Visa / Mastercard）",
                "所有价格均以哈萨克斯坦坚戈（₸）显示",
                "除非取消，订阅将自动续期",
                "费用退还按照哈萨克斯坦共和国法律办理",
                "价格可能在提前通知后进行调整"
              ]
            },
            {
              "type": "p",
              "text": "代币可用于视频上传、AI 生成、广告活动投放及其他平台功能。代币规则可能因套餐而异。"
            }
          ]
        },
        {
          "title": "6. 内容规范与限制",
          "blocks": [
            {
              "type": "p",
              "text": "所有内容必须符合适用法律、广告规则、场地要求及 CANNECT 技术规格。"
            },
            {
              "type": "p",
              "text": "您不得上传违法、误导、侵权、露骨、冒犯、面向未成年人的内容，亦不得上传宣传禁止商品、禁止活动或无依据宣称的内容。"
            },
            {
              "type": "p",
              "text": "所有内容在展示前均可通过自动化工具和人工审核进行审查。"
            }
          ]
        },
        {
          "title": "7. 知识产权",
          "blocks": [
            {
              "type": "p",
              "text": "您保留所上传内容的所有权。您授予 CANNECT.AI 一项非排他性许可，以便为平台运营对您的内容进行存储、处理、审查、展示、分发至互联站点并生成预览。"
            },
            {
              "type": "p",
              "text": "本平台、软件、设计、商标及非用户内容归 CANNECT.AI 或其许可方所有。"
            }
          ]
        },
        {
          "title": "8. 用户责任",
          "blocks": [
            {
              "type": "list",
              "items": [
                "提供准确的信息",
                "遵守法律及平台规则",
                "取得所有必需的内容权利和授权",
                "不得扰乱平台运行或对平台进行逆向工程",
                "不得试图进行未经授权的访问或冒充他人"
              ]
            }
          ]
        },
        {
          "title": "9. 免责声明与责任限制",
          "blocks": [
            {
              "type": "p",
              "text": "在法律允许的最大范围内，本平台按“现状”和“现有”基础提供，不附带任何形式的保证。"
            },
            {
              "type": "p",
              "text": "对于因使用本平台而产生的间接、附带、特殊、后果性或惩罚性损害，CANNECT.AI 概不负责。"
            },
            {
              "type": "p",
              "text": "我们不保证特定的广告活动效果、观看量、互动、销售或转化。"
            }
          ]
        },
        {
          "title": "10. 赔偿",
          "blocks": [
            {
              "type": "p",
              "text": "您同意就因您使用本平台、您的内容、您的广告活动，或您违反法律或第三方权利而引起的索赔、损害、损失和费用，对 CANNECT.AI 作出赔偿并使其免受损害。"
            }
          ]
        },
        {
          "title": "11. 终止",
          "blocks": [
            {
              "type": "p",
              "text": "任何一方均可终止本协议。终止后，访问权限可能结束，未使用的订阅时间可能不予退还，内容可能在保留期满后被删除。"
            }
          ]
        },
        {
          "title": "12. 条款的修改",
          "blocks": [
            {
              "type": "p",
              "text": "我们可能会更新本条款。重大变更可通过电子邮件、平台通知或发布更新后的条款进行告知。继续使用即表示接受更新后的条款。"
            }
          ]
        },
        {
          "title": "13. 适用法律与争议",
          "blocks": [
            {
              "type": "p",
              "text": "本条款受哈萨克斯坦共和国法律管辖。争议应首先通过善意协商解决；协商未果的，由 CANNECT.AI LLP 所在地的哈萨克斯坦共和国法院裁决。"
            }
          ]
        },
        {
          "title": "14. 其他条款",
          "blocks": [
            {
              "type": "p",
              "text": "本条款构成有关平台使用的完整协议。若任何条款不可执行，其余条款仍然有效。未行使某项权利不构成对该权利的放弃。"
            }
          ]
        },
        {
          "title": "15. 联系方式",
          "blocks": [
            {
              "type": "p",
              "text": "如对本条款有任何疑问，请联系我们："
            },
            {
              "type": "list",
              "items": [
                "Email: legal@cannect.ai",
                "Support: support@cannect.ai",
                "Website: https://cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "ar": {
      "kicker": "قانوني",
      "title": "شروط الخدمة",
      "updatedLabel": "آخر تحديث",
      "sections": [
        {
          "title": "1. الموافقة على الشروط",
          "blocks": [
            {
              "type": "p",
              "text": "بدخولك إلى CANNECT.AI أو استخدامك لها، فإنك توافق على الالتزام بشروط الخدمة هذه. وإذا لم توافق، فلا يجوز لك استخدام المنصة."
            },
            {
              "type": "p",
              "text": "تشكّل هذه الشروط اتفاقًا مُلزمًا قانونًا بينك وبين CANNECT.AI لاستخدام منصة الإعلانات المرئية الخاصة بنا وشبكة محطات LED المتصلة."
            },
            {
              "type": "p",
              "text": "تُدار المنصة من قِبل CANNECT.AI LLP (ТОО «CANNECT.AI»)، رقم التعريف التجاري BIN 251140014640، المسجّلة في: جمهورية كازاخستان، ألماتي، منطقة ميديو، ST Koktobe 1، مبنى 48."
            }
          ]
        },
        {
          "title": "2. وصف الخدمة",
          "blocks": [
            {
              "type": "p",
              "text": "تُتيح CANNECT.AI للمستخدمين ما يلي:"
            },
            {
              "type": "list",
              "items": [
                "رفع الإعلانات المرئية وإدارتها",
                "إنشاء محتوى مرئي مدعوم بالذكاء الاصطناعي",
                "عرض الإعلانات على شاشات محطات CANNECT LED المتصلة",
                "إدارة الحملات والمواضع والميزانيات والتحليلات",
                "استخدام خدمات المراجعة والتحقق من الامتثال"
              ]
            }
          ]
        },
        {
          "title": "3. الأهلية",
          "blocks": [
            {
              "type": "p",
              "text": "لاستخدام المنصة، يجب ألا يقل عمرك عن 18 عامًا، وأن تتمتع بالأهلية القانونية لإبرام العقود، وأن تلتزم بقوانين الإعلان المعمول بها، وألا تكون ممنوعًا من استخدام الخدمة."
            }
          ]
        },
        {
          "title": "4. تسجيل الحساب",
          "blocks": [
            {
              "type": "h3",
              "text": "4.1 إنشاء الحساب"
            },
            {
              "type": "p",
              "text": "يمكنك إنشاء حساب باستخدام البريد الإلكتروني وكلمة المرور أو مزوّدي OAuth المدعومين."
            },
            {
              "type": "h3",
              "text": "4.2 أمان الحساب"
            },
            {
              "type": "p",
              "text": "أنت مسؤول عن بيانات اعتمادك، وعن جميع الأنشطة التي تتم عبر حسابك، وعن دقة معلومات الحساب، وعن الإخطار الفوري بأي وصول غير مُصرَّح به."
            },
            {
              "type": "h3",
              "text": "4.3 إنهاء الحساب"
            },
            {
              "type": "p",
              "text": "يجوز لنا تعليق أو إنهاء الحسابات التي تنتهك هذه الشروط أو تمارس نشاطًا محظورًا."
            }
          ]
        },
        {
          "title": "5. خطط الاشتراك والفوترة",
          "blocks": [
            {
              "type": "p",
              "text": "قد تشمل الخطط المستويات المجانية أو الأساسية أو الاحترافية أو مستويات الأعمال المخصّصة. وقد تعتمد الخدمات المدفوعة على الاشتراكات أو التوكنات أو التسعير القائم على الحملة."
            },
            {
              "type": "list",
              "items": [
                "تُعالَج مدفوعات العملاء في كازاخستان عبر الإنترنت من خلال خدمة الاستحواذ PayLink.kz (Visa / Mastercard)",
                "تُعرَض جميع الأسعار بالتنغي الكازاخستاني (₸)",
                "تتجدّد الاشتراكات تلقائيًا ما لم يتم إلغاؤها",
                "الرسوم قابلة للاسترداد وفقًا لقوانين جمهورية كازاخستان",
                "قد تتغيّر الأسعار بإشعار مسبق"
              ]
            },
            {
              "type": "p",
              "text": "يمكن استخدام التوكنات لرفع الفيديوهات والإنشاء بالذكاء الاصطناعي وإطلاق الحملات وغيرها من ميزات المنصة. وقد تختلف قواعد التوكنات باختلاف الخطة."
            }
          ]
        },
        {
          "title": "6. إرشادات المحتوى والقيود",
          "blocks": [
            {
              "type": "p",
              "text": "يجب أن يمتثل كل المحتوى للقوانين المعمول بها وقواعد الإعلان ومتطلبات الموقع والمواصفات التقنية لـ CANNECT."
            },
            {
              "type": "p",
              "text": "لا يجوز لك رفع محتوى غير قانوني أو مُضلِّل أو مُنتهِك للحقوق أو صريح أو مُسيء أو موجّه إلى القُصّر، أو يروّج لسلع أو أنشطة محظورة أو ادعاءات غير مُثبتة."
            },
            {
              "type": "p",
              "text": "قد يخضع كل المحتوى للمراجعة عبر أدوات آلية ومراجعة بشرية قبل العرض."
            }
          ]
        },
        {
          "title": "7. حقوق الملكية الفكرية",
          "blocks": [
            {
              "type": "p",
              "text": "تحتفظ بملكية المحتوى الذي ترفعه. وتمنح CANNECT.AI ترخيصًا غير حصري لتخزين محتواك ومعالجته ومراجعته وعرضه وتوزيعه على المحطات المتصلة وإنشاء معاينات له لأغراض تشغيل المنصة."
            },
            {
              "type": "p",
              "text": "المنصة والبرمجيات والتصاميم والعلامات التجارية والمحتوى غير الخاص بالمستخدم مملوكة لـ CANNECT.AI أو للجهات المرخِّصة لها."
            }
          ]
        },
        {
          "title": "8. مسؤوليات المستخدم",
          "blocks": [
            {
              "type": "list",
              "items": [
                "تقديم معلومات دقيقة",
                "الامتثال للقوانين وقواعد المنصة",
                "الحصول على جميع حقوق المحتوى والأذونات المطلوبة",
                "تجنّب تعطيل المنصة أو إجراء الهندسة العكسية عليها",
                "عدم محاولة الوصول غير المُصرَّح به أو انتحال الشخصية"
              ]
            }
          ]
        },
        {
          "title": "9. إخلاء المسؤولية وحدودها",
          "blocks": [
            {
              "type": "p",
              "text": "تُقدَّم المنصة «كما هي» و«حسب توافرها» دون أي ضمانات من أي نوع إلى أقصى حد يسمح به القانون."
            },
            {
              "type": "p",
              "text": "لا تتحمّل CANNECT.AI المسؤولية عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية تنشأ عن استخدام المنصة."
            },
            {
              "type": "p",
              "text": "نحن لا نضمن نتائج محددة للحملة أو عدد المشاهدات أو التفاعل أو المبيعات أو التحويلات."
            }
          ]
        },
        {
          "title": "10. التعويض",
          "blocks": [
            {
              "type": "p",
              "text": "توافق على تعويض CANNECT.AI عن أي مطالبات أو أضرار أو خسائر أو نفقات تنشأ عن استخدامك للمنصة أو عن محتواك أو حملاتك أو انتهاكك للقوانين أو لحقوق الغير."
            }
          ]
        },
        {
          "title": "11. الإنهاء",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز لأي من الطرفين إنهاء هذا الاتفاق. وبعد الإنهاء، قد ينتهي الوصول، وقد لا تُسترد مدة الاشتراك غير المستخدمة، وقد يُحذف المحتوى بعد فترة احتفاظ."
            }
          ]
        },
        {
          "title": "12. تعديلات الشروط",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز لنا تحديث هذه الشروط. وقد يتم الإبلاغ عن التغييرات الجوهرية عبر البريد الإلكتروني أو إشعار على المنصة أو بنشر الشروط المُحدَّثة. ويُعدّ استمرار الاستخدام قبولًا للشروط المُحدَّثة."
            }
          ]
        },
        {
          "title": "13. القانون الحاكم والنزاعات",
          "blocks": [
            {
              "type": "p",
              "text": "تخضع هذه الشروط لقوانين جمهورية كازاخستان. وتُعالَج النزاعات أولًا عبر التفاوض بحسن نية، وفي حال تعذّر الاتفاق تُحسم من قِبل محاكم جمهورية كازاخستان في موقع CANNECT.AI LLP."
            }
          ]
        },
        {
          "title": "14. أحكام متفرقة",
          "blocks": [
            {
              "type": "p",
              "text": "تشكّل هذه الشروط الاتفاق الكامل لاستخدام المنصة. وإذا تعذّر إنفاذ أي بند، تظل البنود المتبقية سارية. ولا يُعدّ عدم إنفاذ أحد البنود تنازلًا عنه."
            }
          ]
        },
        {
          "title": "15. معلومات الاتصال",
          "blocks": [
            {
              "type": "p",
              "text": "للاستفسارات حول هذه الشروط، تواصل معنا:"
            },
            {
              "type": "list",
              "items": [
                "البريد الإلكتروني: legal@cannect.ai",
                "الدعم: support@cannect.ai",
                "الموقع الإلكتروني: https://cannect.ai"
              ]
            }
          ]
        }
      ]
    }
  },
  "privacy": {
    "en": {
      "kicker": "Legal",
      "title": "Privacy Policy",
      "updatedLabel": "Last updated",
      "sections": [
        {
          "title": "1. General Provisions",
          "blocks": [
            {
              "type": "p",
              "text": "This Privacy Policy (the \"Policy\") defines how personal data of users, clients, advertisers, partners, contractors and other persons interacting with the platforms, services, software, digital solutions and other products of CANNECT.AI LLP (the \"Company\") is collected, processed, stored, protected and transferred."
            },
            {
              "type": "p",
              "text": "The Company processes personal data in accordance with the legislation of the Republic of Kazakhstan, including:"
            },
            {
              "type": "list",
              "items": [
                "Law of the RK No. 94-V dated 21 May 2013 \"On Personal Data and Its Protection\";",
                "Law of the RK \"On Online Platforms and Online Advertising\";",
                "Law of the RK \"On Informatization\";",
                "other regulatory legal acts of the Republic of Kazakhstan."
              ]
            },
            {
              "type": "p",
              "text": "Use of the website, platform, mobile application, smart/digital stations, advertising services and other CANNECT.AI solutions constitutes the user’s consent to this Policy."
            }
          ]
        },
        {
          "title": "2. Personal Data Operator",
          "blocks": [
            {
              "type": "p",
              "text": "The personal data operator is:"
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP (ТОО «CANNECT.AI»)",
                "BIN: 251140014640",
                "Registered address: Republic of Kazakhstan, Almaty, Medeu district, ST Koktobe 1, building 48"
              ]
            }
          ]
        },
        {
          "title": "3. What Data May Be Collected",
          "blocks": [
            {
              "type": "h3",
              "text": "3.1 Personal data"
            },
            {
              "type": "list",
              "items": [
                "full name",
                "phone number",
                "e-mail",
                "IIN (where required by contract, KYC or law)",
                "position",
                "company information (for B2B clients and advertisers)",
                "payment details",
                "other data voluntarily provided by the user"
              ]
            },
            {
              "type": "h3",
              "text": "3.2 Technical data"
            },
            {
              "type": "list",
              "items": [
                "IP address",
                "device type",
                "browser",
                "operating system",
                "cookies",
                "geolocation data (if access is allowed by the user)",
                "device ID",
                "activity log"
              ]
            },
            {
              "type": "h3",
              "text": "3.3 Commercial and operational data"
            },
            {
              "type": "list",
              "items": [
                "advertising requests",
                "order history",
                "support requests",
                "interaction data with digital/smart stations",
                "platform usage analytics"
              ]
            }
          ]
        },
        {
          "title": "4. Purposes of Processing Personal Data",
          "blocks": [
            {
              "type": "p",
              "text": "The Company processes data solely for the following purposes:"
            },
            {
              "type": "list",
              "items": [
                "user registration and account creation",
                "conclusion and performance of contracts",
                "provision of advertising services",
                "placement and management of digital/online advertising",
                "operation of smart stations and digital solutions",
                "acceptance of requests",
                "processing payments and integration with payment services",
                "KYC / AML / compliance checks",
                "customer support",
                "analytics and platform improvement",
                "security",
                "fraud prevention",
                "compliance with RK legislation"
              ]
            }
          ]
        },
        {
          "title": "5. Legal Grounds for Processing",
          "blocks": [
            {
              "type": "list",
              "items": [
                "consent of the data subject",
                "performance of a contract",
                "legitimate interests of the Company",
                "compliance with legal requirements",
                "protection of the rights of the Company and third parties"
              ]
            }
          ]
        },
        {
          "title": "6. Cookies and Digital Analytics",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI may use cookies and similar technologies for: authorization, storing user settings, analytics, security, statistics, improving the user interface, and optimizing advertising tools."
            },
            {
              "type": "p",
              "text": "The user may restrict cookies through browser settings."
            }
          ]
        },
        {
          "title": "7. Transfer of Data to Third Parties",
          "blocks": [
            {
              "type": "p",
              "text": "The Company may transfer data to:"
            },
            {
              "type": "list",
              "items": [
                "banks",
                "payment services",
                "acquiring providers",
                "KYC/AML partners",
                "contractors",
                "hosting providers",
                "IT integrators",
                "advertising contractors",
                "state bodies in cases provided for by law",
                "affiliated and partner structures"
              ]
            },
            {
              "type": "p",
              "text": "Transfer is carried out to the extent necessary to fulfil obligations."
            }
          ]
        },
        {
          "title": "8. Cross-Border Data Transfer",
          "blocks": [
            {
              "type": "p",
              "text": "The Company may transfer data to recipients located outside the Republic of Kazakhstan, in particular:"
            },
            {
              "type": "list",
              "items": [
                "Amazon Web Services — cloud hosting and AI processing, EU region (eu-central-1, Germany);",
                "payment, analytics and anti-fraud providers, which may process data in the European Union and the United States."
              ]
            },
            {
              "type": "p",
              "text": "Cross-border transfer is carried out only to countries that ensure the protection of personal data, on the basis of the data subject’s consent and contractual safeguards, in accordance with the Law of the RK No. 94-V dated 21 May 2013 \"On Personal Data and Its Protection\". Where required, the Company obtains the subject’s separate consent for cross-border transfer."
            }
          ]
        },
        {
          "title": "9. Data Retention",
          "blocks": [
            {
              "type": "p",
              "text": "Personal data is retained:"
            },
            {
              "type": "list",
              "items": [
                "for the duration of the contract",
                "for the period of platform use",
                "within the periods established by RK legislation",
                "until the purposes of processing are achieved",
                "until consent is withdrawn, unless otherwise required by law"
              ]
            }
          ]
        },
        {
          "title": "10. Data Protection",
          "blocks": [
            {
              "type": "p",
              "text": "The Company takes reasonable technical, organizational and legal measures, including:"
            },
            {
              "type": "list",
              "items": [
                "access restriction",
                "employee access control",
                "encryption",
                "server protection",
                "action logging",
                "backup",
                "protection against unauthorized access"
              ]
            }
          ]
        },
        {
          "title": "11. Rights of the Data Subject",
          "blocks": [
            {
              "type": "p",
              "text": "The user has the right to:"
            },
            {
              "type": "list",
              "items": [
                "know about data collection",
                "receive information about processing",
                "request correction",
                "withdraw consent",
                "request deletion, if not contrary to law",
                "restrict processing",
                "appeal the operator’s actions"
              ]
            }
          ]
        },
        {
          "title": "12. Electronic Consent",
          "blocks": [
            {
              "type": "p",
              "text": "The user confirms consent to the Policy by: registration, account creation, ticking a checkbox, using the platform, submitting a request, placing advertising, using the mobile application, using a smart/digital station, paying for services, or electronic acceptance."
            }
          ]
        },
        {
          "title": "13. Advertising and Online Platforms",
          "blocks": [
            {
              "type": "p",
              "text": "When using CANNECT.AI advertising tools, the Company may collect data necessary for: placing advertising, identifying the advertiser, fulfilling the requirements of the Law of the RK \"On Online Platforms and Online Advertising\", storing data on advertising campaigns, and complying with online advertising labelling requirements."
            }
          ]
        },
        {
          "title": "14. Minors",
          "blocks": [
            {
              "type": "p",
              "text": "The Company’s services are not intended for independent use by persons below the age established by RK legislation without the consent of their legal representatives."
            }
          ]
        },
        {
          "title": "15. Limitation of Liability",
          "blocks": [
            {
              "type": "p",
              "text": "The Company is not liable for: actions of third parties beyond the Company’s control; internet network failures; actions of payment providers; unlawful actions of users; consequences of data transfer by the user to third parties."
            }
          ]
        },
        {
          "title": "16. Changes to the Policy",
          "blocks": [
            {
              "type": "p",
              "text": "The Company may amend this Policy. The current version is posted on the official resources of CANNECT.AI. Continued use of the platform constitutes acceptance of the changes."
            }
          ]
        },
        {
          "title": "17. Contacts",
          "blocks": [
            {
              "type": "p",
              "text": "For matters concerning personal data, privacy and compliance:"
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP (ТОО «CANNECT.AI»)",
                "BIN: 251140014640",
                "Address: Republic of Kazakhstan, Almaty, Medeu district, ST Koktobe 1, building 48",
                "E-mail: info@cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "ru": {
      "kicker": "Юридическая информация",
      "title": "Политика конфиденциальности",
      "updatedLabel": "Обновлено",
      "sections": [
        {
          "title": "1. Общие положения",
          "blocks": [
            {
              "type": "p",
              "text": "Настоящая Политика конфиденциальности (далее — «Политика») определяет порядок сбора, обработки, хранения, защиты и передачи персональных данных пользователей, клиентов, рекламодателей, партнеров, подрядчиков и иных лиц, взаимодействующих с платформами, сервисами, программным обеспечением, цифровыми решениями и иными продуктами ТОО «CANNECT.AI» (далее — «Компания»)."
            },
            {
              "type": "p",
              "text": "Компания осуществляет обработку персональных данных в соответствии с законодательством Республики Казахстан, включая:"
            },
            {
              "type": "list",
              "items": [
                "Закон РК от 21 мая 2013 года № 94-V «О персональных данных и их защите»;",
                "Закон РК «Об онлайн-платформах и онлайн-рекламе»;",
                "Закон РК «Об информатизации»;",
                "иные нормативные правовые акты Республики Казахстан."
              ]
            },
            {
              "type": "p",
              "text": "Использование сайта, платформы, мобильного приложения, smart/digital stations, рекламных сервисов и иных решений CANNECT.AI означает согласие пользователя с настоящей Политикой."
            }
          ]
        },
        {
          "title": "2. Оператор персональных данных",
          "blocks": [
            {
              "type": "p",
              "text": "Оператором персональных данных является:"
            },
            {
              "type": "list",
              "items": [
                "Товарищество с ограниченной ответственностью «CANNECT.AI»",
                "БИН: 251140014640",
                "Юридический адрес: Республика Казахстан, г. Алматы, Медеуский район, СТ Коктобе 1, д. 48"
              ]
            }
          ]
        },
        {
          "title": "3. Какие данные могут собираться",
          "blocks": [
            {
              "type": "h3",
              "text": "3.1 Персональные данные"
            },
            {
              "type": "list",
              "items": [
                "ФИО",
                "номер телефона",
                "e-mail",
                "ИИН (если требуется по договору, KYC или законодательству)",
                "должность",
                "сведения о компании (для B2B клиентов и рекламодателей)",
                "платежные реквизиты",
                "иные данные, добровольно предоставленные пользователем"
              ]
            },
            {
              "type": "h3",
              "text": "3.2 Технические данные"
            },
            {
              "type": "list",
              "items": [
                "IP-адрес",
                "тип устройства",
                "браузер",
                "операционная система",
                "cookies",
                "геолокационные данные (если пользователь разрешил доступ)",
                "device ID",
                "журнал активности"
              ]
            },
            {
              "type": "h3",
              "text": "3.3 Коммерческие и операционные данные"
            },
            {
              "type": "list",
              "items": [
                "рекламные заявки",
                "история заказов",
                "обращения в поддержку",
                "данные взаимодействия с digital/smart stations",
                "аналитика использования платформы"
              ]
            }
          ]
        },
        {
          "title": "4. Цели обработки персональных данных",
          "blocks": [
            {
              "type": "p",
              "text": "Компания обрабатывает данные исключительно для следующих целей:"
            },
            {
              "type": "list",
              "items": [
                "регистрация пользователей и создание аккаунтов",
                "заключение и исполнение договоров",
                "предоставление рекламных услуг",
                "размещение и управление digital/online рекламой",
                "функционирование smart stations и цифровых решений",
                "прием заявок",
                "проведение платежей и интеграция с платежными сервисами",
                "KYC / AML / compliance-проверки",
                "клиентская поддержка",
                "аналитика и улучшение платформы",
                "обеспечение безопасности",
                "предотвращение мошенничества",
                "исполнение требований законодательства РК"
              ]
            }
          ]
        },
        {
          "title": "5. Правовые основания обработки",
          "blocks": [
            {
              "type": "list",
              "items": [
                "согласия субъекта данных",
                "исполнения договора",
                "законных интересов Компании",
                "исполнения требований законодательства",
                "защиты прав Компании и третьих лиц"
              ]
            }
          ]
        },
        {
          "title": "6. Cookies и цифровая аналитика",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI вправе использовать cookies и аналогичные технологии для: авторизации, хранения пользовательских настроек, аналитики, безопасности, статистики, улучшения пользовательского интерфейса, оптимизации рекламных инструментов."
            },
            {
              "type": "p",
              "text": "Пользователь вправе ограничить cookies через настройки браузера."
            }
          ]
        },
        {
          "title": "7. Передача данных третьим лицам",
          "blocks": [
            {
              "type": "p",
              "text": "Компания вправе передавать данные:"
            },
            {
              "type": "list",
              "items": [
                "банкам",
                "платежным сервисам",
                "эквайринговым провайдерам",
                "KYC/AML-партнерам",
                "подрядчикам",
                "хостинг-провайдерам",
                "IT-интеграторам",
                "рекламным подрядчикам",
                "государственным органам в случаях, предусмотренных законом",
                "аффилированным и партнерским структурам"
              ]
            },
            {
              "type": "p",
              "text": "Передача осуществляется в объеме, необходимом для исполнения обязательств."
            }
          ]
        },
        {
          "title": "8. Трансграничная передача данных",
          "blocks": [
            {
              "type": "p",
              "text": "Компания может передавать данные получателям, находящимся за пределами Республики Казахстан, в частности:"
            },
            {
              "type": "list",
              "items": [
                "Amazon Web Services — облачный хостинг и AI-обработка, регион ЕС (eu-central-1, Германия);",
                "провайдерам платежей, аналитики и антифрода, которые могут обрабатывать данные в Европейском союзе и США."
              ]
            },
            {
              "type": "p",
              "text": "Трансграничная передача осуществляется только в страны, обеспечивающие защиту персональных данных, на основании согласия субъекта данных и договорных гарантий, в соответствии с Законом РК от 21 мая 2013 года № 94-V «О персональных данных и их защите». При необходимости Компания получает отдельное согласие субъекта на трансграничную передачу."
            }
          ]
        },
        {
          "title": "9. Хранение данных",
          "blocks": [
            {
              "type": "p",
              "text": "Персональные данные хранятся:"
            },
            {
              "type": "list",
              "items": [
                "в течение срока действия договора",
                "в течение периода использования платформы",
                "в сроки, установленные законодательством РК",
                "до достижения целей обработки",
                "до отзыва согласия, если иное не требуется законом"
              ]
            }
          ]
        },
        {
          "title": "10. Защита данных",
          "blocks": [
            {
              "type": "p",
              "text": "Компания принимает разумные технические, организационные и правовые меры, включая:"
            },
            {
              "type": "list",
              "items": [
                "ограничение доступа",
                "контроль доступа сотрудников",
                "шифрование",
                "защиту серверов",
                "журналирование действий",
                "резервное копирование",
                "защиту от несанкционированного доступа"
              ]
            }
          ]
        },
        {
          "title": "11. Права субъекта персональных данных",
          "blocks": [
            {
              "type": "p",
              "text": "Пользователь имеет право:"
            },
            {
              "type": "list",
              "items": [
                "знать о сборе данных",
                "получать информацию об обработке",
                "требовать исправления",
                "отзывать согласие",
                "требовать удаления, если это не противоречит закону",
                "ограничивать обработку",
                "обжаловать действия оператора"
              ]
            }
          ]
        },
        {
          "title": "12. Электронное согласие",
          "blocks": [
            {
              "type": "p",
              "text": "Пользователь подтверждает согласие с Политикой путем: регистрации, создания аккаунта, проставления checkbox, использования платформы, подачи заявки, размещения рекламы, использования мобильного приложения, использования smart/digital station, оплаты услуг, электронного акцепта."
            }
          ]
        },
        {
          "title": "13. Реклама и онлайн-платформы",
          "blocks": [
            {
              "type": "p",
              "text": "При использовании рекламных инструментов CANNECT.AI Компания вправе собирать данные, необходимые для: размещения рекламы, идентификации рекламодателя, выполнения требований Закона РК «Об онлайн-платформах и онлайн-рекламе», хранения данных о рекламных кампаниях, соблюдения требований маркировки онлайн-рекламы."
            }
          ]
        },
        {
          "title": "14. Несовершеннолетние",
          "blocks": [
            {
              "type": "p",
              "text": "Сервисы Компании не предназначены для самостоятельного использования лицами, не достигшими возраста, установленного законодательством РК, без согласия законных представителей."
            }
          ]
        },
        {
          "title": "15. Ограничение ответственности",
          "blocks": [
            {
              "type": "p",
              "text": "Компания не несет ответственности за: действия третьих лиц вне контроля Компании; сбои интернет-сетей; действия платежных провайдеров; незаконные действия пользователей; последствия передачи данных самим пользователем третьим лицам."
            }
          ]
        },
        {
          "title": "16. Изменение Политики",
          "blocks": [
            {
              "type": "p",
              "text": "Компания вправе изменять настоящую Политику. Актуальная версия размещается на официальных ресурсах CANNECT.AI. Продолжение использования платформы означает принятие изменений."
            }
          ]
        },
        {
          "title": "17. Контакты",
          "blocks": [
            {
              "type": "p",
              "text": "По вопросам персональных данных, конфиденциальности и compliance:"
            },
            {
              "type": "list",
              "items": [
                "ТОО «CANNECT.AI»",
                "БИН: 251140014640",
                "Адрес: Республика Казахстан, г. Алматы, Медеуский район, СТ Коктобе 1, д. 48",
                "E-mail: info@cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "kk": {
      "kicker": "Құқықтық ақпарат",
      "title": "Құпиялылық саясаты",
      "updatedLabel": "Жаңартылған күні",
      "sections": [
        {
          "title": "1. Жалпы ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Осы Құпиялылық саясаты (бұдан әрі — «Саясат») «CANNECT.AI» ЖШС (бұдан әрі — «Компания») платформаларымен, сервистерімен, бағдарламалық жасақтамасымен, цифрлық шешімдерімен және өзге де өнімдерімен өзара әрекеттесетін пайдаланушылардың, клиенттердің, жарнама берушілердің, серіктестердің, мердігерлердің және өзге де тұлғалардың дербес деректерін жинау, өңдеу, сақтау, қорғау және беру тәртібін айқындайды."
            },
            {
              "type": "p",
              "text": "Компания дербес деректерді Қазақстан Республикасының заңнамасына сәйкес өңдейді, оның ішінде:"
            },
            {
              "type": "list",
              "items": [
                "ҚР 2013 жылғы 21 мамырдағы № 94-V «Дербес деректер және оларды қорғау туралы» Заңы;",
                "ҚР «Онлайн-платформалар және онлайн-жарнама туралы» Заңы;",
                "ҚР «Ақпараттандыру туралы» Заңы;",
                "Қазақстан Республикасының өзге де нормативтік құқықтық актілері."
              ]
            },
            {
              "type": "p",
              "text": "Сайтты, платформаны, мобильді қосымшаны, smart/digital stations, жарнама сервистерін және CANNECT.AI өзге де шешімдерін пайдалану пайдаланушының осы Саясатпен келісімін білдіреді."
            }
          ]
        },
        {
          "title": "2. Дербес деректер операторы",
          "blocks": [
            {
              "type": "p",
              "text": "Дербес деректер операторы:"
            },
            {
              "type": "list",
              "items": [
                "«CANNECT.AI» жауапкершілігі шектеулі серіктестігі",
                "БСН: 251140014640",
                "Заңды мекенжайы: Қазақстан Республикасы, Алматы қ., Медеу ауданы, Көктөбе 1 СА, 48-үй"
              ]
            }
          ]
        },
        {
          "title": "3. Қандай деректер жиналуы мүмкін",
          "blocks": [
            {
              "type": "h3",
              "text": "3.1 Жеке деректер"
            },
            {
              "type": "list",
              "items": [
                "аты-жөні",
                "телефон нөмірі",
                "e-mail",
                "ЖСН (шарт, KYC немесе заңнама бойынша талап етілсе)",
                "лауазымы",
                "компания туралы мәліметтер (B2B клиенттер мен жарнама берушілер үшін)",
                "төлем деректемелері",
                "пайдаланушы өз еркімен берген өзге деректер"
              ]
            },
            {
              "type": "h3",
              "text": "3.2 Техникалық деректер"
            },
            {
              "type": "list",
              "items": [
                "IP-мекенжай",
                "құрылғы түрі",
                "браузер",
                "операциялық жүйе",
                "cookies",
                "геолокация деректері (пайдаланушы рұқсат берсе)",
                "device ID",
                "белсенділік журналы"
              ]
            },
            {
              "type": "h3",
              "text": "3.3 Коммерциялық және операциялық деректер"
            },
            {
              "type": "list",
              "items": [
                "жарнама өтінімдері",
                "тапсырыстар тарихы",
                "қолдауға өтініштер",
                "digital/smart stations-пен өзара әрекеттесу деректері",
                "платформаны пайдалану аналитикасы"
              ]
            }
          ]
        },
        {
          "title": "4. Дербес деректерді өңдеу мақсаттары",
          "blocks": [
            {
              "type": "p",
              "text": "Компания деректерді тек мынадай мақсаттарда өңдейді:"
            },
            {
              "type": "list",
              "items": [
                "пайдаланушыларды тіркеу және аккаунт жасау",
                "шарттар жасасу және орындау",
                "жарнама қызметтерін көрсету",
                "digital/online жарнаманы орналастыру және басқару",
                "smart stations және цифрлық шешімдердің жұмысы",
                "өтінімдерді қабылдау",
                "төлемдерді жүргізу және төлем сервистерімен интеграция",
                "KYC / AML / compliance тексерістері",
                "клиенттік қолдау",
                "аналитика және платформаны жақсарту",
                "қауіпсіздікті қамтамасыз ету",
                "алаяқтықтың алдын алу",
                "ҚР заңнамасы талаптарын орындау"
              ]
            }
          ]
        },
        {
          "title": "5. Өңдеудің құқықтық негіздері",
          "blocks": [
            {
              "type": "list",
              "items": [
                "дерек субъектісінің келісімі",
                "шартты орындау",
                "Компанияның заңды мүдделері",
                "заңнама талаптарын орындау",
                "Компания мен үшінші тұлғалардың құқықтарын қорғау"
              ]
            }
          ]
        },
        {
          "title": "6. Cookies және цифрлық аналитика",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI cookies және ұқсас технологияларды мыналар үшін пайдалана алады: авторизация, пайдаланушы баптауларын сақтау, аналитика, қауіпсіздік, статистика, пайдаланушы интерфейсін жақсарту, жарнама құралдарын оңтайландыру."
            },
            {
              "type": "p",
              "text": "Пайдаланушы cookies-ті браузер баптаулары арқылы шектей алады."
            }
          ]
        },
        {
          "title": "7. Деректерді үшінші тұлғаларға беру",
          "blocks": [
            {
              "type": "p",
              "text": "Компания деректерді бере алады:"
            },
            {
              "type": "list",
              "items": [
                "банктерге",
                "төлем сервистеріне",
                "эквайринг провайдерлеріне",
                "KYC/AML серіктестеріне",
                "мердігерлерге",
                "хостинг-провайдерлерге",
                "IT-интеграторларға",
                "жарнама мердігерлеріне",
                "заңда көзделген жағдайларда мемлекеттік органдарға",
                "аффилиирленген және серіктес құрылымдарға"
              ]
            },
            {
              "type": "p",
              "text": "Беру міндеттемелерді орындау үшін қажетті көлемде жүзеге асырылады."
            }
          ]
        },
        {
          "title": "8. Деректерді трансшекаралық беру",
          "blocks": [
            {
              "type": "p",
              "text": "Компания деректерді Қазақстан Республикасынан тыс орналасқан алушыларға беруі мүмкін, атап айтқанда:"
            },
            {
              "type": "list",
              "items": [
                "Amazon Web Services — бұлттық хостинг және AI өңдеу, ЕО аймағы (eu-central-1, Германия);",
                "төлем, аналитика және алаяқтыққа қарсы провайдерлер, олар деректерді Еуропалық одақ пен АҚШ-та өңдеуі мүмкін."
              ]
            },
            {
              "type": "p",
              "text": "Трансшекаралық беру тек дербес деректерді қорғауды қамтамасыз ететін елдерге, дерек субъектісінің келісімі мен шарттық кепілдіктер негізінде, ҚР 2013 жылғы 21 мамырдағы № 94-V «Дербес деректер және оларды қорғау туралы» Заңына сәйкес жүзеге асырылады. Қажет болған жағдайда Компания трансшекаралық беруге субъектінің жеке келісімін алады."
            }
          ]
        },
        {
          "title": "9. Деректерді сақтау",
          "blocks": [
            {
              "type": "p",
              "text": "Дербес деректер сақталады:"
            },
            {
              "type": "list",
              "items": [
                "шарттың қолданылу мерзімі ішінде",
                "платформаны пайдалану кезеңінде",
                "ҚР заңнамасында белгіленген мерзімдерде",
                "өңдеу мақсаттарына қол жеткізілгенге дейін",
                "келісім қайтарып алынғанға дейін, егер заңда өзгеше талап етілмесе"
              ]
            }
          ]
        },
        {
          "title": "10. Деректерді қорғау",
          "blocks": [
            {
              "type": "p",
              "text": "Компания ақылға қонымды техникалық, ұйымдастырушылық және құқықтық шараларды қабылдайды, оның ішінде:"
            },
            {
              "type": "list",
              "items": [
                "қол жеткізуді шектеу",
                "қызметкерлердің қол жеткізуін бақылау",
                "шифрлау",
                "серверлерді қорғау",
                "әрекеттерді журналдау",
                "сақтық көшірмелеу",
                "рұқсатсыз қол жеткізуден қорғау"
              ]
            }
          ]
        },
        {
          "title": "11. Дербес деректер субъектісінің құқықтары",
          "blocks": [
            {
              "type": "p",
              "text": "Пайдаланушының құқығы бар:"
            },
            {
              "type": "list",
              "items": [
                "деректер жиналатынын білу",
                "өңдеу туралы ақпарат алу",
                "түзетуді талап ету",
                "келісімді қайтарып алу",
                "заңға қайшы келмесе, жоюды талап ету",
                "өңдеуді шектеу",
                "оператордың әрекеттеріне шағымдану"
              ]
            }
          ]
        },
        {
          "title": "12. Электрондық келісім",
          "blocks": [
            {
              "type": "p",
              "text": "Пайдаланушы Саясатпен келісімін мыналар арқылы растайды: тіркелу, аккаунт жасау, checkbox қою, платформаны пайдалану, өтінім беру, жарнама орналастыру, мобильді қосымшаны пайдалану, smart/digital station пайдалану, қызметтерді төлеу, электрондық акцепт."
            }
          ]
        },
        {
          "title": "13. Жарнама және онлайн-платформалар",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI жарнама құралдарын пайдалану кезінде Компания мыналар үшін қажетті деректерді жинай алады: жарнама орналастыру, жарнама берушіні сәйкестендіру, ҚР «Онлайн-платформалар және онлайн-жарнама туралы» Заңы талаптарын орындау, жарнама кампаниялары туралы деректерді сақтау, онлайн-жарнаманы таңбалау талаптарын сақтау."
            }
          ]
        },
        {
          "title": "14. Кәмелетке толмағандар",
          "blocks": [
            {
              "type": "p",
              "text": "Компания қызметтері ҚР заңнамасында белгіленген жасқа толмаған тұлғалардың заңды өкілдерінің келісімінсіз дербес пайдалануына арналмаған."
            }
          ]
        },
        {
          "title": "15. Жауапкершілікті шектеу",
          "blocks": [
            {
              "type": "p",
              "text": "Компания мыналар үшін жауап бермейді: Компанияның бақылауынан тыс үшінші тұлғалардың әрекеттері; интернет желілерінің ақаулары; төлем провайдерлерінің әрекеттері; пайдаланушылардың заңсыз әрекеттері; пайдаланушының деректерді үшінші тұлғаларға өзі беруінің салдары."
            }
          ]
        },
        {
          "title": "16. Саясатты өзгерту",
          "blocks": [
            {
              "type": "p",
              "text": "Компания осы Саясатты өзгертуге құқылы. Өзекті нұсқа CANNECT.AI ресми ресурстарында орналастырылады. Платформаны әрі қарай пайдалану өзгерістерді қабылдауды білдіреді."
            }
          ]
        },
        {
          "title": "17. Байланыс",
          "blocks": [
            {
              "type": "p",
              "text": "Дербес деректер, құпиялылық және compliance мәселелері бойынша:"
            },
            {
              "type": "list",
              "items": [
                "«CANNECT.AI» ЖШС",
                "БСН: 251140014640",
                "Мекенжайы: Қазақстан Республикасы, Алматы қ., Медеу ауданы, Көктөбе 1 СА, 48-үй",
                "E-mail: info@cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "tr": {
      "kicker": "Yasal",
      "title": "Gizlilik Politikası",
      "updatedLabel": "Son güncelleme",
      "sections": [
        {
          "title": "1. Genel Hükümler",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Gizlilik Politikası (“Politika”), CANNECT.AI LLP (“Şirket”) platformları, hizmetleri, yazılımı, dijital çözümleri ve diğer ürünleriyle etkileşimde bulunan kullanıcıların, müşterilerin, reklamverenlerin, ortakların, yüklenicilerin ve diğer kişilerin kişisel verilerinin nasıl toplandığını, işlendiğini, saklandığını, korunduğunu ve aktarıldığını tanımlar."
            },
            {
              "type": "p",
              "text": "Şirket, kişisel verileri Kazakistan Cumhuriyeti mevzuatına uygun olarak işler; buna şunlar dahildir:"
            },
            {
              "type": "list",
              "items": [
                "21 Mayıs 2013 tarihli ve 94-V sayılı “Kişisel Veriler ve Bunların Korunması Hakkında” KC Kanunu;",
                "“Çevrimiçi Platformlar ve Çevrimiçi Reklamcılık Hakkında” KC Kanunu;",
                "“Bilgilendirme Hakkında” KC Kanunu;",
                "Kazakistan Cumhuriyeti'nin diğer düzenleyici hukuki düzenlemeleri."
              ]
            },
            {
              "type": "p",
              "text": "Web sitesinin, platformun, mobil uygulamanın, akıllı/dijital istasyonların, reklam hizmetlerinin ve diğer CANNECT.AI çözümlerinin kullanımı, kullanıcının bu Politika'yı onayladığı anlamına gelir."
            }
          ]
        },
        {
          "title": "2. Kişisel Veri İşleyeni",
          "blocks": [
            {
              "type": "p",
              "text": "Kişisel veri işleyeni:"
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP (ТОО «CANNECT.AI»)",
                "BIN: 251140014640",
                "Kayıtlı adres: Kazakistan Cumhuriyeti, Almatı, Medeu ilçesi, ST Koktobe 1, bina 48"
              ]
            }
          ]
        },
        {
          "title": "3. Hangi Veriler Toplanabilir",
          "blocks": [
            {
              "type": "h3",
              "text": "3.1 Kişisel veriler"
            },
            {
              "type": "list",
              "items": [
                "ad soyad",
                "telefon numarası",
                "e-posta",
                "IIN (sözleşme, KYC veya yasa gereği gerektiğinde)",
                "pozisyon",
                "şirket bilgileri (B2B müşteriler ve reklamverenler için)",
                "ödeme bilgileri",
                "kullanıcı tarafından gönüllü olarak sağlanan diğer veriler"
              ]
            },
            {
              "type": "h3",
              "text": "3.2 Teknik veriler"
            },
            {
              "type": "list",
              "items": [
                "IP adresi",
                "cihaz türü",
                "tarayıcı",
                "işletim sistemi",
                "çerezler",
                "coğrafi konum verileri (kullanıcı erişime izin verirse)",
                "cihaz kimliği",
                "etkinlik günlüğü"
              ]
            },
            {
              "type": "h3",
              "text": "3.3 Ticari ve operasyonel veriler"
            },
            {
              "type": "list",
              "items": [
                "reklam talepleri",
                "sipariş geçmişi",
                "destek talepleri",
                "dijital/akıllı istasyonlarla etkileşim verileri",
                "platform kullanım analitiği"
              ]
            }
          ]
        },
        {
          "title": "4. Kişisel Verilerin İşlenme Amaçları",
          "blocks": [
            {
              "type": "p",
              "text": "Şirket verileri yalnızca aşağıdaki amaçlarla işler:"
            },
            {
              "type": "list",
              "items": [
                "kullanıcı kaydı ve hesap oluşturma",
                "sözleşmelerin akdedilmesi ve ifası",
                "reklam hizmetlerinin sağlanması",
                "dijital/çevrimiçi reklamların yerleştirilmesi ve yönetimi",
                "akıllı istasyonların ve dijital çözümlerin işletilmesi",
                "taleplerin kabulü",
                "ödemelerin işlenmesi ve ödeme hizmetleriyle entegrasyon",
                "KYC / AML / uyumluluk kontrolleri",
                "müşteri desteği",
                "analitik ve platformun iyileştirilmesi",
                "güvenlik",
                "dolandırıcılığın önlenmesi",
                "KC mevzuatına uyum"
              ]
            }
          ]
        },
        {
          "title": "5. İşlemenin Hukuki Dayanakları",
          "blocks": [
            {
              "type": "list",
              "items": [
                "veri sahibinin rızası",
                "bir sözleşmenin ifası",
                "Şirketin meşru menfaatleri",
                "yasal gerekliliklere uyum",
                "Şirketin ve üçüncü tarafların haklarının korunması"
              ]
            }
          ]
        },
        {
          "title": "6. Çerezler ve Dijital Analitik",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI; yetkilendirme, kullanıcı ayarlarının saklanması, analitik, güvenlik, istatistik, kullanıcı arayüzünün iyileştirilmesi ve reklam araçlarının optimizasyonu için çerezleri ve benzer teknolojileri kullanabilir."
            },
            {
              "type": "p",
              "text": "Kullanıcı çerezleri tarayıcı ayarları aracılığıyla kısıtlayabilir."
            }
          ]
        },
        {
          "title": "7. Verilerin Üçüncü Taraflara Aktarılması",
          "blocks": [
            {
              "type": "p",
              "text": "Şirket verileri şu taraflara aktarabilir:"
            },
            {
              "type": "list",
              "items": [
                "bankalar",
                "ödeme hizmetleri",
                "acquiring sağlayıcıları",
                "KYC/AML ortakları",
                "yükleniciler",
                "barındırma sağlayıcıları",
                "BT entegratörleri",
                "reklam yüklenicileri",
                "yasada öngörülen durumlarda devlet kurumları",
                "bağlı ve ortak yapılar"
              ]
            },
            {
              "type": "p",
              "text": "Aktarım, yükümlülüklerin yerine getirilmesi için gerekli ölçüde gerçekleştirilir."
            }
          ]
        },
        {
          "title": "8. Sınır Ötesi Veri Aktarımı",
          "blocks": [
            {
              "type": "p",
              "text": "Şirket, Kazakistan Cumhuriyeti dışında bulunan alıcılara veri aktarabilir, özellikle:"
            },
            {
              "type": "list",
              "items": [
                "Amazon Web Services — bulut barındırma ve yapay zeka işleme, AB bölgesi (eu-central-1, Almanya);",
                "verileri Avrupa Birliği ve Amerika Birleşik Devletleri'nde işleyebilen ödeme, analitik ve dolandırıcılık önleme sağlayıcıları."
              ]
            },
            {
              "type": "p",
              "text": "Sınır ötesi aktarım yalnızca kişisel verilerin korunmasını sağlayan ülkelere, veri sahibinin rızasına ve sözleşmesel güvencelere dayanarak, 21 Mayıs 2013 tarihli ve 94-V sayılı “Kişisel Veriler ve Bunların Korunması Hakkında” KC Kanunu'na uygun olarak gerçekleştirilir. Gerektiğinde Şirket, sınır ötesi aktarım için veri sahibinin ayrı rızasını alır."
            }
          ]
        },
        {
          "title": "9. Verilerin Saklanması",
          "blocks": [
            {
              "type": "p",
              "text": "Kişisel veriler şu süreler boyunca saklanır:"
            },
            {
              "type": "list",
              "items": [
                "sözleşme süresince",
                "platform kullanım süresince",
                "KC mevzuatında belirlenen süreler içinde",
                "işleme amaçlarına ulaşılana kadar",
                "yasa aksini gerektirmedikçe rıza geri çekilene kadar"
              ]
            }
          ]
        },
        {
          "title": "10. Verilerin Korunması",
          "blocks": [
            {
              "type": "p",
              "text": "Şirket, aşağıdakiler dahil makul teknik, organizasyonel ve hukuki önlemler alır:"
            },
            {
              "type": "list",
              "items": [
                "erişim kısıtlaması",
                "çalışan erişim kontrolü",
                "şifreleme",
                "sunucu koruması",
                "eylem günlüğü tutma",
                "yedekleme",
                "yetkisiz erişime karşı koruma"
              ]
            }
          ]
        },
        {
          "title": "11. Veri Sahibinin Hakları",
          "blocks": [
            {
              "type": "p",
              "text": "Kullanıcının şu hakları vardır:"
            },
            {
              "type": "list",
              "items": [
                "veri toplandığını bilme",
                "işleme hakkında bilgi alma",
                "düzeltme talep etme",
                "rızayı geri çekme",
                "yasaya aykırı değilse silme talep etme",
                "işlemeyi kısıtlama",
                "işleyenin eylemlerine itiraz etme"
              ]
            }
          ]
        },
        {
          "title": "12. Elektronik Rıza",
          "blocks": [
            {
              "type": "p",
              "text": "Kullanıcı, Politika'ya rızasını şu yollarla teyit eder: kayıt, hesap oluşturma, bir onay kutusunu işaretleme, platformu kullanma, talep gönderme, reklam yerleştirme, mobil uygulamayı kullanma, akıllı/dijital istasyon kullanma, hizmetlerin ödemesini yapma veya elektronik kabul."
            }
          ]
        },
        {
          "title": "13. Reklamcılık ve Çevrimiçi Platformlar",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI reklam araçlarını kullanırken Şirket; reklam yerleştirme, reklamvereni tanımlama, “Çevrimiçi Platformlar ve Çevrimiçi Reklamcılık Hakkında” KC Kanunu'nun gerekliliklerini yerine getirme, reklam kampanyalarına ilişkin verileri saklama ve çevrimiçi reklam etiketleme gerekliliklerine uyma için gerekli verileri toplayabilir."
            }
          ]
        },
        {
          "title": "14. Reşit Olmayanlar",
          "blocks": [
            {
              "type": "p",
              "text": "Şirketin hizmetleri, yasal temsilcilerinin rızası olmaksızın KC mevzuatında belirlenen yaşın altındaki kişilerin bağımsız kullanımı için tasarlanmamıştır."
            }
          ]
        },
        {
          "title": "15. Sorumluluğun Sınırlandırılması",
          "blocks": [
            {
              "type": "p",
              "text": "Şirket şunlardan sorumlu değildir: Şirketin kontrolü dışındaki üçüncü taraf eylemleri; internet ağı arızaları; ödeme sağlayıcılarının eylemleri; kullanıcıların yasa dışı eylemleri; kullanıcının verileri üçüncü taraflara aktarmasının sonuçları."
            }
          ]
        },
        {
          "title": "16. Politikadaki Değişiklikler",
          "blocks": [
            {
              "type": "p",
              "text": "Şirket bu Politika'yı değiştirebilir. Güncel sürüm CANNECT.AI'nın resmi kaynaklarında yayımlanır. Platformun kullanılmaya devam edilmesi değişikliklerin kabulü anlamına gelir."
            }
          ]
        },
        {
          "title": "17. İletişim",
          "blocks": [
            {
              "type": "p",
              "text": "Kişisel veriler, gizlilik ve uyumlulukla ilgili konular için:"
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP (ТОО «CANNECT.AI»)",
                "BIN: 251140014640",
                "Adres: Kazakistan Cumhuriyeti, Almatı, Medeu ilçesi, ST Koktobe 1, bina 48",
                "E-posta: info@cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "zh": {
      "kicker": "法律信息",
      "title": "隐私政策",
      "updatedLabel": "最后更新",
      "sections": [
        {
          "title": "1. 总则",
          "blocks": [
            {
              "type": "p",
              "text": "本隐私政策（以下简称“本政策”）规定了如何收集、处理、存储、保护和传输用户、客户、广告主、合作伙伴、承包商以及其他与 CANNECT.AI LLP（以下简称“本公司”）的平台、服务、软件、数字解决方案及其他产品进行交互的人员的个人数据。"
            },
            {
              "type": "p",
              "text": "本公司依据哈萨克斯坦共和国法律处理个人数据，包括："
            },
            {
              "type": "list",
              "items": [
                "哈萨克斯坦共和国 2013 年 5 月 21 日第 94-V 号《个人数据及其保护法》；",
                "哈萨克斯坦共和国《在线平台与在线广告法》；",
                "哈萨克斯坦共和国《信息化法》；",
                "哈萨克斯坦共和国其他规范性法律文件。"
              ]
            },
            {
              "type": "p",
              "text": "使用本网站、平台、移动应用程序、智能/数字站点、广告服务及其他 CANNECT.AI 解决方案，即表示用户同意本政策。"
            }
          ]
        },
        {
          "title": "2. 个人数据运营者",
          "blocks": [
            {
              "type": "p",
              "text": "个人数据运营者为："
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP（有限责任合伙企业 «CANNECT.AI»）",
                "BIN：251140014640",
                "注册地址：哈萨克斯坦共和国，阿拉木图市，梅杰乌区，ST Koktobe 1，48 号楼"
              ]
            }
          ]
        },
        {
          "title": "3. 可能收集的数据",
          "blocks": [
            {
              "type": "h3",
              "text": "3.1 个人数据"
            },
            {
              "type": "list",
              "items": [
                "姓名",
                "电话号码",
                "电子邮箱",
                "IIN（个人识别号，在合同、KYC 或法律要求时）",
                "职务",
                "公司信息（适用于 B2B 客户和广告主）",
                "支付信息",
                "用户自愿提供的其他数据"
              ]
            },
            {
              "type": "h3",
              "text": "3.2 技术数据"
            },
            {
              "type": "list",
              "items": [
                "IP 地址",
                "设备类型",
                "浏览器",
                "操作系统",
                "cookies",
                "地理位置数据（如用户允许访问）",
                "设备 ID",
                "活动日志"
              ]
            },
            {
              "type": "h3",
              "text": "3.3 商业与运营数据"
            },
            {
              "type": "list",
              "items": [
                "广告请求",
                "订单历史",
                "支持请求",
                "与数字/智能站点的交互数据",
                "平台使用数据分析"
              ]
            }
          ]
        },
        {
          "title": "4. 处理个人数据的目的",
          "blocks": [
            {
              "type": "p",
              "text": "本公司仅为下列目的处理数据："
            },
            {
              "type": "list",
              "items": [
                "用户注册与账户创建",
                "合同的订立与履行",
                "提供广告服务",
                "数字/在线广告的投放与管理",
                "智能站点与数字解决方案的运行",
                "受理请求",
                "处理支付及与支付服务的集成",
                "KYC / AML / 合规审查",
                "客户支持",
                "数据分析与平台改进",
                "安全保障",
                "欺诈防范",
                "遵守哈萨克斯坦共和国法律"
              ]
            }
          ]
        },
        {
          "title": "5. 处理的法律依据",
          "blocks": [
            {
              "type": "list",
              "items": [
                "数据主体的同意",
                "合同的履行",
                "本公司的合法利益",
                "遵守法律要求",
                "保护本公司及第三方的权利"
              ]
            }
          ]
        },
        {
          "title": "6. Cookies 与数字数据分析",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT.AI 可出于以下目的使用 cookies 及类似技术：授权、保存用户设置、数据分析、安全、统计、改进用户界面以及优化广告工具。"
            },
            {
              "type": "p",
              "text": "用户可通过浏览器设置限制 cookies。"
            }
          ]
        },
        {
          "title": "7. 向第三方传输数据",
          "blocks": [
            {
              "type": "p",
              "text": "本公司可将数据传输给："
            },
            {
              "type": "list",
              "items": [
                "银行",
                "支付服务",
                "收单服务提供商",
                "KYC/AML 合作伙伴",
                "承包商",
                "托管服务提供商",
                "IT 集成商",
                "广告承包商",
                "法律规定情形下的国家机关",
                "关联及合作机构"
              ]
            },
            {
              "type": "p",
              "text": "传输仅在履行义务所必需的范围内进行。"
            }
          ]
        },
        {
          "title": "8. 跨境数据传输",
          "blocks": [
            {
              "type": "p",
              "text": "本公司可将数据传输给位于哈萨克斯坦共和国境外的接收方，特别是："
            },
            {
              "type": "list",
              "items": [
                "Amazon Web Services — 云托管与 AI 处理，欧盟区域（eu-central-1，德国）；",
                "支付、数据分析及反欺诈服务提供商，其可能在欧盟和美国处理数据。"
              ]
            },
            {
              "type": "p",
              "text": "跨境传输仅向能够保障个人数据保护的国家进行，依据数据主体的同意及合同保障措施，并符合哈萨克斯坦共和国 2013 年 5 月 21 日第 94-V 号《个人数据及其保护法》。在必要时，本公司将就跨境传输取得数据主体的单独同意。"
            }
          ]
        },
        {
          "title": "9. 数据保留",
          "blocks": [
            {
              "type": "p",
              "text": "个人数据的保留期限为："
            },
            {
              "type": "list",
              "items": [
                "合同有效期内",
                "平台使用期间",
                "哈萨克斯坦共和国法律规定的期限内",
                "直至处理目的实现",
                "直至撤回同意，除非法律另有要求"
              ]
            }
          ]
        },
        {
          "title": "10. 数据保护",
          "blocks": [
            {
              "type": "p",
              "text": "本公司采取合理的技术、组织和法律措施，包括："
            },
            {
              "type": "list",
              "items": [
                "访问限制",
                "员工访问控制",
                "加密",
                "服务器防护",
                "操作日志记录",
                "备份",
                "防止未经授权的访问"
              ]
            }
          ]
        },
        {
          "title": "11. 数据主体的权利",
          "blocks": [
            {
              "type": "p",
              "text": "用户有权："
            },
            {
              "type": "list",
              "items": [
                "知晓数据的收集",
                "获取有关处理的信息",
                "要求更正",
                "撤回同意",
                "要求删除（如不违反法律）",
                "限制处理",
                "对运营者的行为提出申诉"
              ]
            }
          ]
        },
        {
          "title": "12. 电子同意",
          "blocks": [
            {
              "type": "p",
              "text": "用户通过以下方式确认同意本政策：注册、创建账户、勾选复选框、使用平台、提交请求、投放广告、使用移动应用程序、使用智能/数字站点、支付服务费用或电子承诺。"
            }
          ]
        },
        {
          "title": "13. 广告与在线平台",
          "blocks": [
            {
              "type": "p",
              "text": "在使用 CANNECT.AI 广告工具时，本公司可收集为下列目的所必需的数据：投放广告、识别广告主、履行哈萨克斯坦共和国《在线平台与在线广告法》的要求、存储有关广告活动的数据以及遵守在线广告标识要求。"
            }
          ]
        },
        {
          "title": "14. 未成年人",
          "blocks": [
            {
              "type": "p",
              "text": "本公司的服务不适用于未达到哈萨克斯坦共和国法律规定年龄的人员在未经其法定代理人同意的情况下独立使用。"
            }
          ]
        },
        {
          "title": "15. 责任限制",
          "blocks": [
            {
              "type": "p",
              "text": "本公司不对以下情形承担责任：本公司无法控制的第三方行为；互联网网络故障；支付服务提供商的行为；用户的违法行为；用户自行向第三方传输数据所产生的后果。"
            }
          ]
        },
        {
          "title": "16. 政策的变更",
          "blocks": [
            {
              "type": "p",
              "text": "本公司有权修订本政策。最新版本发布于 CANNECT.AI 的官方资源上。继续使用平台即表示接受相关变更。"
            }
          ]
        },
        {
          "title": "17. 联系方式",
          "blocks": [
            {
              "type": "p",
              "text": "如有关于个人数据、隐私和合规的事宜："
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP（有限责任合伙企业 «CANNECT.AI»）",
                "BIN：251140014640",
                "地址：哈萨克斯坦共和国，阿拉木图市，梅杰乌区，ST Koktobe 1，48 号楼",
                "E-mail：info@cannect.ai"
              ]
            }
          ]
        }
      ]
    },
    "ar": {
      "kicker": "قانوني",
      "title": "سياسة الخصوصية",
      "updatedLabel": "آخر تحديث",
      "sections": [
        {
          "title": "1. أحكام عامة",
          "blocks": [
            {
              "type": "p",
              "text": "تحدّد سياسة الخصوصية هذه (\"السياسة\") كيفية جمع ومعالجة وتخزين وحماية ونقل البيانات الشخصية للمستخدمين والعملاء والمعلنين والشركاء والمقاولين وغيرهم من الأشخاص الذين يتفاعلون مع منصات وخدمات وبرمجيات وحلول رقمية ومنتجات أخرى تابعة لـ CANNECT.AI LLP (\"الشركة\")."
            },
            {
              "type": "p",
              "text": "تعالج الشركة البيانات الشخصية وفقًا لتشريعات جمهورية كازاخستان، بما في ذلك:"
            },
            {
              "type": "list",
              "items": [
                "قانون جمهورية كازاخستان رقم 94-V الصادر في 21 مايو 2013 \"بشأن البيانات الشخصية وحمايتها\"؛",
                "قانون جمهورية كازاخستان \"بشأن المنصات الإلكترونية والإعلان الإلكتروني\"؛",
                "قانون جمهورية كازاخستان \"بشأن المعلوماتية\"؛",
                "وغيرها من الأنظمة القانونية المعمول بها في جمهورية كازاخستان."
              ]
            },
            {
              "type": "p",
              "text": "يُعدّ استخدام الموقع الإلكتروني والمنصة وتطبيق الهاتف المحمول والمحطات الذكية/الرقمية وخدمات الإعلان وغيرها من حلول CANNECT.AI موافقةً من المستخدم على هذه السياسة."
            }
          ]
        },
        {
          "title": "2. مشغّل البيانات الشخصية",
          "blocks": [
            {
              "type": "p",
              "text": "مشغّل البيانات الشخصية هو:"
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP (ТОО «CANNECT.AI»)",
                "رقم التعريف التجاري (BIN): 251140014640",
                "العنوان المسجّل: جمهورية كازاخستان، ألماتي، منطقة ميديو، ST Koktobe 1، مبنى 48"
              ]
            }
          ]
        },
        {
          "title": "3. البيانات التي قد يتم جمعها",
          "blocks": [
            {
              "type": "h3",
              "text": "3.1 البيانات الشخصية"
            },
            {
              "type": "list",
              "items": [
                "الاسم الكامل",
                "رقم الهاتف",
                "البريد الإلكتروني",
                "رقم التعريف الفردي (IIN) (عند طلبه بموجب العقد أو إجراءات KYC أو القانون)",
                "المنصب الوظيفي",
                "معلومات الشركة (لعملاء ومعلني قطاع الأعمال B2B)",
                "تفاصيل الدفع",
                "بيانات أخرى يقدّمها المستخدم طوعًا"
              ]
            },
            {
              "type": "h3",
              "text": "3.2 البيانات التقنية"
            },
            {
              "type": "list",
              "items": [
                "عنوان IP",
                "نوع الجهاز",
                "المتصفح",
                "نظام التشغيل",
                "ملفات تعريف الارتباط (الكوكيز)",
                "بيانات الموقع الجغرافي (إذا سمح المستخدم بالوصول إليها)",
                "معرّف الجهاز",
                "سجل النشاط"
              ]
            },
            {
              "type": "h3",
              "text": "3.3 البيانات التجارية والتشغيلية"
            },
            {
              "type": "list",
              "items": [
                "طلبات الإعلان",
                "سجل الطلبات",
                "طلبات الدعم",
                "بيانات التفاعل مع المحطات الرقمية/الذكية",
                "تحليلات استخدام المنصة"
              ]
            }
          ]
        },
        {
          "title": "4. أغراض معالجة البيانات الشخصية",
          "blocks": [
            {
              "type": "p",
              "text": "تعالج الشركة البيانات حصريًا للأغراض التالية:"
            },
            {
              "type": "list",
              "items": [
                "تسجيل المستخدم وإنشاء الحساب",
                "إبرام العقود وتنفيذها",
                "تقديم خدمات الإعلان",
                "إطلاق وإدارة الإعلانات الرقمية/الإلكترونية",
                "تشغيل المحطات الذكية والحلول الرقمية",
                "قبول الطلبات",
                "معالجة المدفوعات والتكامل مع خدمات الدفع",
                "فحوصات KYC / AML / الامتثال",
                "دعم العملاء",
                "التحليلات وتحسين المنصة",
                "الأمان",
                "منع الاحتيال",
                "الامتثال لتشريعات جمهورية كازاخستان"
              ]
            }
          ]
        },
        {
          "title": "5. الأسس القانونية للمعالجة",
          "blocks": [
            {
              "type": "list",
              "items": [
                "موافقة صاحب البيانات",
                "تنفيذ العقد",
                "المصالح المشروعة للشركة",
                "الامتثال للمتطلبات القانونية",
                "حماية حقوق الشركة والغير"
              ]
            }
          ]
        },
        {
          "title": "6. ملفات تعريف الارتباط والتحليلات الرقمية",
          "blocks": [
            {
              "type": "p",
              "text": "قد تستخدم CANNECT.AI ملفات تعريف الارتباط والتقنيات المماثلة لأغراض: التوثيق، وحفظ إعدادات المستخدم، والتحليلات، والأمان، والإحصاءات، وتحسين واجهة المستخدم، وتحسين أدوات الإعلان."
            },
            {
              "type": "p",
              "text": "يمكن للمستخدم تقييد ملفات تعريف الارتباط من خلال إعدادات المتصفح."
            }
          ]
        },
        {
          "title": "7. نقل البيانات إلى الغير",
          "blocks": [
            {
              "type": "p",
              "text": "قد تنقل الشركة البيانات إلى:"
            },
            {
              "type": "list",
              "items": [
                "البنوك",
                "خدمات الدفع",
                "مزوّدي خدمات الاستحواذ",
                "شركاء KYC/AML",
                "المقاولين",
                "مزوّدي الاستضافة",
                "مُكاملي تقنية المعلومات",
                "مقاولي الإعلان",
                "الجهات الحكومية في الحالات التي ينص عليها القانون",
                "الكيانات التابعة والشريكة"
              ]
            },
            {
              "type": "p",
              "text": "يتم النقل بالقدر اللازم للوفاء بالالتزامات."
            }
          ]
        },
        {
          "title": "8. نقل البيانات عبر الحدود",
          "blocks": [
            {
              "type": "p",
              "text": "قد تنقل الشركة البيانات إلى جهات مستقبِلة تقع خارج جمهورية كازاخستان، وعلى وجه الخصوص:"
            },
            {
              "type": "list",
              "items": [
                "Amazon Web Services — الاستضافة السحابية ومعالجة الذكاء الاصطناعي، منطقة الاتحاد الأوروبي (eu-central-1، ألمانيا)؛",
                "مزوّدو خدمات الدفع والتحليلات ومكافحة الاحتيال، الذين قد يعالجون البيانات في الاتحاد الأوروبي والولايات المتحدة."
              ]
            },
            {
              "type": "p",
              "text": "لا يتم النقل عبر الحدود إلا إلى الدول التي تضمن حماية البيانات الشخصية، استنادًا إلى موافقة صاحب البيانات والضمانات التعاقدية، ووفقًا لقانون جمهورية كازاخستان رقم 94-V الصادر في 21 مايو 2013 \"بشأن البيانات الشخصية وحمايتها\". وعند الاقتضاء، تحصل الشركة على موافقة منفصلة من صاحب البيانات على النقل عبر الحدود."
            }
          ]
        },
        {
          "title": "9. الاحتفاظ بالبيانات",
          "blocks": [
            {
              "type": "p",
              "text": "يُحتفظ بالبيانات الشخصية:"
            },
            {
              "type": "list",
              "items": [
                "طوال مدة العقد",
                "طوال فترة استخدام المنصة",
                "خلال المدد التي تحددها تشريعات جمهورية كازاخستان",
                "حتى تتحقق أغراض المعالجة",
                "حتى سحب الموافقة، ما لم يقتضِ القانون خلاف ذلك"
              ]
            }
          ]
        },
        {
          "title": "10. حماية البيانات",
          "blocks": [
            {
              "type": "p",
              "text": "تتخذ الشركة تدابير تقنية وتنظيمية وقانونية معقولة، بما في ذلك:"
            },
            {
              "type": "list",
              "items": [
                "تقييد الوصول",
                "التحكم في وصول الموظفين",
                "التشفير",
                "حماية الخوادم",
                "تسجيل الإجراءات",
                "النسخ الاحتياطي",
                "الحماية من الوصول غير المُصرَّح به"
              ]
            }
          ]
        },
        {
          "title": "11. حقوق صاحب البيانات",
          "blocks": [
            {
              "type": "p",
              "text": "يحق للمستخدم:"
            },
            {
              "type": "list",
              "items": [
                "معرفة جمع البيانات",
                "الحصول على معلومات حول المعالجة",
                "طلب التصحيح",
                "سحب الموافقة",
                "طلب الحذف، ما لم يكن مخالفًا للقانون",
                "تقييد المعالجة",
                "الطعن في إجراءات المشغّل"
              ]
            }
          ]
        },
        {
          "title": "12. الموافقة الإلكترونية",
          "blocks": [
            {
              "type": "p",
              "text": "يؤكّد المستخدم موافقته على السياسة من خلال: التسجيل، أو إنشاء الحساب، أو تحديد خانة الاختيار، أو استخدام المنصة، أو تقديم طلب، أو إطلاق إعلان، أو استخدام تطبيق الهاتف المحمول، أو استخدام محطة ذكية/رقمية، أو دفع مقابل الخدمات، أو القبول الإلكتروني."
            }
          ]
        },
        {
          "title": "13. الإعلان والمنصات الإلكترونية",
          "blocks": [
            {
              "type": "p",
              "text": "عند استخدام أدوات الإعلان الخاصة بـ CANNECT.AI، قد تجمع الشركة البيانات اللازمة من أجل: إطلاق الإعلان، وتحديد هوية المعلن، والوفاء بمتطلبات قانون جمهورية كازاخستان \"بشأن المنصات الإلكترونية والإعلان الإلكتروني\"، وتخزين بيانات الحملات الإعلانية، والامتثال لمتطلبات وسم الإعلان الإلكتروني."
            }
          ]
        },
        {
          "title": "14. القُصّر",
          "blocks": [
            {
              "type": "p",
              "text": "خدمات الشركة غير مُخصّصة للاستخدام المستقل من قِبل الأشخاص دون السن الذي تحدده تشريعات جمهورية كازاخستان دون موافقة ممثليهم القانونيين."
            }
          ]
        },
        {
          "title": "15. تحديد المسؤولية",
          "blocks": [
            {
              "type": "p",
              "text": "لا تتحمّل الشركة المسؤولية عن: أفعال الغير الخارجة عن سيطرة الشركة؛ أعطال شبكة الإنترنت؛ أفعال مزوّدي خدمات الدفع؛ الأفعال غير القانونية للمستخدمين؛ عواقب نقل المستخدم للبيانات إلى الغير."
            }
          ]
        },
        {
          "title": "16. التغييرات على السياسة",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز للشركة تعديل هذه السياسة. ويُنشر الإصدار الحالي على الموارد الرسمية لـ CANNECT.AI. ويُعدّ استمرار استخدام المنصة قبولًا للتغييرات."
            }
          ]
        },
        {
          "title": "17. جهات الاتصال",
          "blocks": [
            {
              "type": "p",
              "text": "للمسائل المتعلقة بالبيانات الشخصية والخصوصية والامتثال:"
            },
            {
              "type": "list",
              "items": [
                "CANNECT.AI LLP (ТОО «CANNECT.AI»)",
                "رقم التعريف التجاري (BIN): 251140014640",
                "العنوان: جمهورية كازاخستان، ألماتي، منطقة ميديو، ST Koktobe 1، مبنى 48",
                "البريد الإلكتروني: info@cannect.ai"
              ]
            }
          ]
        }
      ]
    }
  },
  "offer": {
    "en": {
      "kicker": "Legal",
      "title": "Public Offer",
      "updatedLabel": "Last updated",
      "sections": [
        {
          "title": "1. General Provisions",
          "blocks": [
            {
              "type": "p",
              "text": "This Public Offer (the \"Offer\") governs the relations between CANNECT.AI LLP (\"CANNECT AI\", the \"Company\", the \"Platform\") and individuals and/or legal entities (the \"Customer\", the \"Advertiser\") regarding the placement, production, distribution, storage, display and technical support of advertising and informational materials on the CANNECT AI online platform."
            },
            {
              "type": "p",
              "text": "CANNECT AI acts as: owner and/or operator of the online platform; technical operator of advertising placement; a digital/media platform; operator of AI tools, analytics and recommendation systems."
            },
            {
              "type": "p",
              "text": "This Offer is developed in accordance with the Civil Code of the RK, the Law of the RK \"On Advertising\", the Law of the RK \"On Online Platforms and Online Advertising\", the Law of the RK \"On Personal Data and Its Protection\", and other regulatory legal acts of the RK."
            },
            {
              "type": "p",
              "text": "Acceptance of this Offer is: registration on the platform; account creation; submitting a request; uploading advertising materials; signing electronic documents; paying for services; clicking the \"Agree\", \"Continue\", \"Pay\", \"Place\" buttons or other actions confirming agreement with the Offer."
            },
            {
              "type": "p",
              "text": "From the moment of acceptance, this Offer is deemed a contract concluded in electronic form."
            }
          ]
        },
        {
          "title": "2. Subject of the Offer",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI provides the Customer with services: online advertising placement and distribution; display of advertising materials; technical support of advertising campaigns; targeted advertising; sponsored content placement; advertising campaign analytics; production, adaptation and editing of advertising materials (upon separate order)."
            },
            {
              "type": "p",
              "text": "Advertising may be placed on websites, mobile applications, AI stations, digital screens, interactive devices, partner media platforms, and other online and offline media of CANNECT AI."
            },
            {
              "type": "p",
              "text": "Specific placement parameters are determined by a request, media plan, personal account, electronic agreement, invoice, or technical specification."
            },
            {
              "type": "p",
              "text": "Tariffs and token model. Upon registration, the User is automatically granted the Free plan, which includes 5 (five) complimentary tokens to explore the Platform's functionality. Complimentary tokens may be used solely within the CANNECT AI Platform and are not exchangeable for money, refundable, or transferable to third parties. Once the complimentary tokens are used, further use of the Platform's paid features requires purchasing tokens or subscribing to a corresponding plan. The plans, token prices, the rules for granting, using and deducting tokens, token validity periods and other terms published at cannect.kz/pricing form an integral part of this Offer. When registering and using the free plan, the User's personal data is processed in accordance with the CANNECT AI Privacy Policy."
            }
          ]
        },
        {
          "title": "3. Customer’s Responsibility for Advertising Materials",
          "blocks": [
            {
              "type": "p",
              "text": "The Customer is solely and fully responsible for: the content of advertising materials; accuracy of information; compliance of advertising with RK legislation; availability of required licenses, permits, certificates and approvals; compliance with advertising and online-advertising law; respect for the rights of third parties; copyright and related rights; the use of trademarks, brands, images, audio and video materials."
            },
            {
              "type": "p",
              "text": "The Customer warrants that advertising materials contain no false information, do not violate RK legislation, do not mislead users, do not infringe the rights of minors, contain no unlawful content, do not violate moral and ethical norms, and do not infringe intellectual property rights."
            },
            {
              "type": "p",
              "text": "The Customer independently obtains all permits and consents required by RK legislation and bears full responsibility for compliance with the Law \"On Advertising\", the Law \"On Online Platforms and Online Advertising\", consumer protection, personal data and intellectual property legislation."
            }
          ]
        },
        {
          "title": "4. Limitation of CANNECT AI Liability",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI acts solely as an online platform, technical operator, media/digital platform, advertising placement operator and technological infrastructure provider. CANNECT AI is not the author, initiator or customer of the Customer’s advertising materials."
            },
            {
              "type": "p",
              "text": "CANNECT AI is not liable for the content of advertising, accuracy of information, absence of licenses or permits, copyright or trademark infringement, the Customer’s violations of law, or claims from state bodies and third parties related to the Customer’s advertising materials."
            },
            {
              "type": "p",
              "text": "In the event of claims, lawsuits, fines, orders or demands from state bodies or third parties related to the Customer’s advertising materials, the Customer undertakes to independently settle such claims, compensate CANNECT AI for all losses, and reimburse fines, legal costs, compensation and other expenses."
            }
          ]
        },
        {
          "title": "5. Labelling of Online Advertising",
          "blocks": [
            {
              "type": "p",
              "text": "Online advertising must be identifiable and labelled in accordance with RK legislation. CANNECT AI may add technical designations, disclaimers and \"Advertising\", \"Sponsored\", \"Ad\" or other labels. The Customer undertakes not to remove or hide advertising designations and labels."
            },
            {
              "type": "p",
              "text": "Advertising materials placed on digital screens, AI stations and other CANNECT AI offline media within the Republic of Kazakhstan must comply with the requirements of Kazakhstan legislation on languages and advertising. Where required by law, advertising materials must contain information in the state language. Russian and other languages may additionally be used provided that the requirements of Kazakhstan legislation are met. The Customer (Advertiser) is responsible for compliance with these requirements."
            }
          ]
        },
        {
          "title": "6. Targeted Advertising and Profiling",
          "blocks": [
            {
              "type": "p",
              "text": "When placing targeted advertising, the Customer undertakes to comply with RK legislation. The Customer is prohibited from using data on racial or ethnic origin, political views, health condition, biometric data and other sensitive personal data."
            },
            {
              "type": "p",
              "text": "Targeted advertising must not be distributed to users identified as minors. CANNECT AI may use recommendation systems, AI algorithms and automated analytics systems to optimize advertising placement."
            }
          ]
        },
        {
          "title": "7. Prohibited Content",
          "blocks": [
            {
              "type": "p",
              "text": "The placement of advertising and content is prohibited if it contains false information, extremist materials, content related to narcotic substances, fraudulent schemes, pornographic content, content infringing the rights of minors or third parties, discrimination, violence or incitement of hatred, or otherwise violates RK legislation."
            },
            {
              "type": "p",
              "text": "CANNECT AI may independently determine the admissibility of placing advertising materials."
            }
          ]
        },
        {
          "title": "8. Moderation and Content Removal",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI may carry out automated, manual and AI moderation and review of advertising materials."
            },
            {
              "type": "p",
              "text": "CANNECT AI may restrict access to advertising, remove advertising materials, suspend placement, block the Customer’s account, or refuse placement. Grounds include: violation of law; an order from a state body; user complaints; signs of unlawful content; violation of the platform’s internal rules."
            }
          ]
        },
        {
          "title": "9. Information Storage and Analytics",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI may store advertising materials, logs, statistics, advertising placement data, and the change history of advertising campaigns. Online advertising information may be stored for at least one year after placement ends."
            },
            {
              "type": "p",
              "text": "CANNECT AI may use anonymized analytics and statistics to improve the platform, for analytics, to optimize advertising campaigns and to develop AI algorithms."
            }
          ]
        },
        {
          "title": "10. User Complaints",
          "blocks": [
            {
              "type": "p",
              "text": "Users may submit complaints about advertising materials. CANNECT AI may notify the Customer of complaints, require changes or removal of advertising, and restrict distribution of disputed content. The Customer must respond promptly to CANNECT AI’s requirements."
            }
          ]
        },
        {
          "title": "11. Interaction with State Bodies",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI may comply with mandatory requirements of the state bodies of the Republic of Kazakhstan, including restricting access to content, removing content, transferring information to authorized bodies, and enforcing court acts and orders."
            }
          ]
        },
        {
          "title": "12. Personal Data",
          "blocks": [
            {
              "type": "p",
              "text": "Processing of personal data is carried out in accordance with RK legislation. The Customer warrants the lawfulness of the transfer of personal data used in advertising campaigns. CANNECT AI may use cookies, device ID, analytical and technical tools within the framework of RK legislation."
            }
          ]
        },
        {
          "title": "13. Intellectual Property",
          "blocks": [
            {
              "type": "p",
              "text": "All rights to the CANNECT AI platform, software, AI algorithms, interfaces, design and analytics systems belong to CANNECT AI or the respective right holders. Placement of advertising does not grant the Customer any rights to CANNECT AI technologies and software."
            }
          ]
        },
        {
          "title": "14. Electronic Interaction",
          "blocks": [
            {
              "type": "p",
              "text": "The parties recognize the legal force of electronic requests, correspondence, actions in the personal account, digital logs, electronic confirmations and online payments. The Customer’s electronic actions are recognized as the equivalent of a handwritten signature."
            }
          ]
        },
        {
          "title": "15. Final Provisions",
          "blocks": [
            {
              "type": "p",
              "text": "This Offer is governed by the legislation of the Republic of Kazakhstan. All disputes are subject to resolution through negotiation, and, failing agreement, in court at the place of registration of CANNECT AI."
            },
            {
              "type": "p",
              "text": "CANNECT AI may unilaterally amend the terms of this Offer by publishing a new version on the platform or website. Continued use of the platform after publication of a new version constitutes the Customer’s agreement with the changes. The current version of the Offer is posted on the official CANNECT AI website."
            }
          ]
        }
      ],
      "updatedValue": "14 May 2026 (as amended and supplemented as of 16.07.2026)"
    },
    "ru": {
      "kicker": "Юридическая информация",
      "title": "Публичная оферта",
      "updatedLabel": "Редакция от",
      "sections": [
        {
          "title": "1. Общие положения",
          "blocks": [
            {
              "type": "p",
              "text": "Настоящая Публичная оферта (далее — «Оферта») регулирует отношения между ТОО «CANNECT.AI» (далее — «CANNECT AI», «Компания», «Платформа») и физическими и/или юридическими лицами (далее — «Заказчик», «Рекламодатель») по размещению, производству, распространению, хранению, показу и техническому сопровождению рекламных и информационных материалов на онлайн-платформе CANNECT AI."
            },
            {
              "type": "p",
              "text": "CANNECT AI является: собственником и/или оператором онлайн-платформы; техническим оператором размещения рекламы; digital/media платформой; оператором AI-инструментов, аналитики и рекомендательных систем."
            },
            {
              "type": "p",
              "text": "Настоящая Оферта разработана в соответствии с Гражданским кодексом РК, Законом РК «О рекламе», Законом РК «Об онлайн-платформах и онлайн-рекламе», Законом РК «О персональных данных и их защите» и иными нормативными правовыми актами РК."
            },
            {
              "type": "p",
              "text": "Акцептом настоящей Оферты является: регистрация на платформе; создание аккаунта; оформление заявки; загрузка рекламных материалов; подписание электронных документов; оплата услуг; нажатие кнопок «Согласен», «Продолжить», «Оплатить», «Разместить» либо совершение иных действий, подтверждающих согласие с Офертой."
            },
            {
              "type": "p",
              "text": "С момента акцепта настоящая Оферта считается заключенным договором в электронной форме."
            }
          ]
        },
        {
          "title": "2. Предмет Оферты",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI предоставляет Заказчику услуги: по размещению и распространению онлайн-рекламы; по показу рекламных материалов; по техническому обеспечению рекламных кампаний; по размещению таргетированной рекламы; по размещению спонсорского контента; по аналитике рекламных кампаний; по изготовлению, адаптации и монтажу рекламных материалов (при отдельном заказе)."
            },
            {
              "type": "p",
              "text": "Размещение рекламы может осуществляться на веб-сайтах, в мобильных приложениях, на AI-станциях, цифровых экранах, интерактивных устройствах, партнерских медиа-площадках и иных онлайн- и офлайн-носителях CANNECT AI."
            },
            {
              "type": "p",
              "text": "Конкретные параметры размещения определяются заявкой, медиапланом, личным кабинетом, электронным согласованием, счетом на оплату, техническим заданием."
            },
            {
              "type": "p",
              "text": "Тарифы и токенная модель. Пользователю после регистрации автоматически предоставляется тариф Free, в рамках которого начисляется 5 (пять) бесплатных токенов для ознакомления с функциональными возможностями Платформы. Бесплатные токены предоставляются исключительно для использования в рамках Платформы CANNECT AI, не подлежат обмену на денежные средства, возврату или передаче третьим лицам. После использования бесплатных токенов дальнейшее использование платных функций Платформы осуществляется путем приобретения токенов либо подключения соответствующего тарифного плана. Тарифные планы, стоимость токенов, порядок их начисления, использования, списания, сроки действия токенов и иные условия, опубликованные на странице cannect.kz/pricing, являются неотъемлемой частью настоящей Оферты. При регистрации и использовании бесплатного тарифа обработка персональных данных Пользователя осуществляется в соответствии с Политикой конфиденциальности CANNECT AI."
            }
          ]
        },
        {
          "title": "3. Ответственность Заказчика за рекламные материалы",
          "blocks": [
            {
              "type": "p",
              "text": "Заказчик самостоятельно и в полном объеме несет ответственность за: содержание рекламных материалов; достоверность информации; соответствие рекламы законодательству РК; наличие необходимых лицензий, разрешений, сертификатов и согласований; соблюдение законодательства о рекламе и об онлайн-рекламе; соблюдение прав третьих лиц; соблюдение авторских и смежных прав; использование товарных знаков, брендов, изображений, аудио- и видеоматериалов."
            },
            {
              "type": "p",
              "text": "Заказчик гарантирует, что рекламные материалы не содержат ложной информации, не нарушают законодательство РК, не вводят пользователей в заблуждение, не нарушают права несовершеннолетних, не содержат противоправного контента, не нарушают моральные и этические нормы и права интеллектуальной собственности."
            },
            {
              "type": "p",
              "text": "Заказчик самостоятельно получает все необходимые разрешения и согласия, предусмотренные законодательством РК, и несет полную ответственность за соответствие рекламы требованиям Закона «О рекламе», Закона «Об онлайн-платформах и онлайн-рекламе», законодательства о защите прав потребителей, о персональных данных и об интеллектуальной собственности."
            }
          ]
        },
        {
          "title": "4. Ограничение ответственности CANNECT AI",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI выступает исключительно как онлайн-платформа, технический оператор, media/digital платформа, оператор размещения рекламы и поставщик технологической инфраструктуры. CANNECT AI не является автором, инициатором либо заказчиком рекламных материалов Заказчика."
            },
            {
              "type": "p",
              "text": "CANNECT AI не несет ответственности за содержание рекламы, достоверность сведений, отсутствие лицензий либо разрешений, нарушение авторских прав и прав на товарные знаки, нарушения законодательства Заказчиком, претензии государственных органов и третьих лиц, связанные с рекламными материалами Заказчика."
            },
            {
              "type": "p",
              "text": "В случае предъявления претензий, исков, штрафов, предписаний либо требований со стороны государственных органов или третьих лиц, связанных с рекламными материалами Заказчика, Заказчик обязуется самостоятельно урегулировать такие претензии, компенсировать CANNECT AI все убытки, возместить штрафы, судебные расходы, компенсации и иные затраты."
            }
          ]
        },
        {
          "title": "5. Маркировка онлайн-рекламы",
          "blocks": [
            {
              "type": "p",
              "text": "Онлайн-реклама должна быть идентифицируема и маркирована в соответствии с законодательством РК. CANNECT AI вправе добавлять технические обозначения, дисклеймеры и маркировку «Реклама», «Sponsored», «Ad» и иные обозначения. Заказчик обязуется не удалять и не скрывать рекламные обозначения и маркировки."
            },
            {
              "type": "p",
              "text": "Рекламные материалы, размещаемые на цифровых экранах, AI-станциях и иных офлайн-носителях CANNECT AI на территории Республики Казахстан, должны соответствовать требованиям законодательства Республики Казахстан о языках и рекламе. В случаях, предусмотренных законодательством, рекламные материалы должны содержать информацию на государственном языке. Дополнительно допускается использование русского и иных языков при условии соблюдения требований законодательства Республики Казахстан. Ответственность за соблюдение указанных требований несет Заказчик (Рекламодатель)."
            }
          ]
        },
        {
          "title": "6. Таргетированная реклама и профайлинг",
          "blocks": [
            {
              "type": "p",
              "text": "При размещении таргетированной рекламы Заказчик обязуется соблюдать требования законодательства РК. Заказчику запрещается использовать данные о расовой или национальной принадлежности, политических взглядах, состоянии здоровья, биометрические данные и иные чувствительные персональные данные."
            },
            {
              "type": "p",
              "text": "Таргетированная реклама не должна распространяться на пользователей, идентифицированных как несовершеннолетние. CANNECT AI вправе использовать рекомендательные системы, AI-алгоритмы и автоматизированные системы аналитики для оптимизации размещения рекламы."
            }
          ]
        },
        {
          "title": "7. Запрещенный контент",
          "blocks": [
            {
              "type": "p",
              "text": "На платформе запрещается размещение рекламы и контента, содержащего ложную информацию, экстремистские материалы, связанного с наркотическими средствами, содержащего мошеннические схемы, порнографического характера, нарушающего права несовершеннолетних и третьих лиц, содержащего дискриминацию, насилие либо разжигание ненависти, либо нарушающего законодательство РК."
            },
            {
              "type": "p",
              "text": "CANNECT AI вправе самостоятельно определять допустимость размещения рекламных материалов."
            }
          ]
        },
        {
          "title": "8. Модерация и удаление контента",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI вправе осуществлять автоматизированную, ручную и AI-модерацию, а также проверку рекламных материалов."
            },
            {
              "type": "p",
              "text": "CANNECT AI вправе ограничить доступ к рекламе, удалить рекламные материалы, приостановить размещение, заблокировать аккаунт Заказчика либо отказать в размещении рекламы. Основаниями являются: нарушение законодательства; получение предписания государственного органа; жалобы пользователей; признаки противоправного контента; нарушение внутренних правил платформы."
            }
          ]
        },
        {
          "title": "9. Хранение информации и аналитика",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI вправе хранить рекламные материалы, логи, статистику, данные о размещении рекламы и историю изменений рекламных кампаний. Информация об онлайн-рекламе может храниться не менее одного года после завершения размещения."
            },
            {
              "type": "p",
              "text": "CANNECT AI вправе использовать обезличенную аналитику и статистику для улучшения платформы, аналитики, оптимизации рекламных кампаний и развития AI-алгоритмов."
            }
          ]
        },
        {
          "title": "10. Жалобы пользователей",
          "blocks": [
            {
              "type": "p",
              "text": "Пользователи вправе направлять жалобы на рекламные материалы. CANNECT AI вправе направлять Заказчику уведомления о жалобах, требовать изменения или удаления рекламы и ограничивать распространение спорного контента. Заказчик обязан незамедлительно реагировать на требования CANNECT AI."
            }
          ]
        },
        {
          "title": "11. Взаимодействие с государственными органами",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI вправе исполнять обязательные требования государственных органов Республики Казахстан, в том числе ограничивать доступ к контенту, удалять контент, передавать информацию уполномоченным органам и исполнять судебные акты и предписания."
            }
          ]
        },
        {
          "title": "12. Персональные данные",
          "blocks": [
            {
              "type": "p",
              "text": "Обработка персональных данных осуществляется в соответствии с законодательством РК. Заказчик гарантирует законность передачи персональных данных, используемых в рекламных кампаниях. CANNECT AI вправе использовать cookies, device ID, аналитические и технические инструменты в рамках законодательства РК."
            }
          ]
        },
        {
          "title": "13. Интеллектуальная собственность",
          "blocks": [
            {
              "type": "p",
              "text": "Все права на платформу CANNECT AI, программное обеспечение, AI-алгоритмы, интерфейсы, дизайн и аналитические системы принадлежат CANNECT AI либо соответствующим правообладателям. Размещение рекламы не предоставляет Заказчику прав на технологии и программное обеспечение CANNECT AI."
            }
          ]
        },
        {
          "title": "14. Электронное взаимодействие",
          "blocks": [
            {
              "type": "p",
              "text": "Стороны признают юридическую силу электронных заявок, переписки, действий в личном кабинете, цифровых логов, электронных подтверждений и онлайн-платежей. Электронные действия Заказчика признаются аналогом собственноручной подписи."
            }
          ]
        },
        {
          "title": "15. Заключительные положения",
          "blocks": [
            {
              "type": "p",
              "text": "Настоящая Оферта регулируется законодательством Республики Казахстан. Все споры подлежат разрешению путем переговоров, а при недостижении соглашения — в судебном порядке по месту регистрации CANNECT AI."
            },
            {
              "type": "p",
              "text": "CANNECT AI вправе в одностороннем порядке изменять условия настоящей Оферты путем публикации новой редакции на платформе или сайте. Продолжение использования платформы после публикации новой редакции означает согласие Заказчика с изменениями. Актуальная редакция Оферты размещается на официальном сайте CANNECT AI."
            }
          ]
        }
      ],
      "updatedValue": "14 мая 2026 г. (с изменениями и дополнениями по состоянию на 16.07.2026 г.)"
    },
    "kk": {
      "kicker": "Құқықтық ақпарат",
      "title": "Жария оферта",
      "updatedLabel": "Редакция",
      "sections": [
        {
          "title": "1. Жалпы ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Осы Жария оферта (бұдан әрі — «Оферта») «CANNECT.AI» ЖШС (бұдан әрі — «CANNECT AI», «Компания», «Платформа») мен жеке және/немесе заңды тұлғалар (бұдан әрі — «Тапсырыс беруші», «Жарнама беруші») арасындағы CANNECT AI онлайн-платформасында жарнамалық және ақпараттық материалдарды орналастыру, өндіру, тарату, сақтау, көрсету және техникалық қолдау бойынша қатынастарды реттейді."
            },
            {
              "type": "p",
              "text": "CANNECT AI: онлайн-платформаның иесі және/немесе операторы; жарнама орналастырудың техникалық операторы; digital/media платформасы; AI-құралдардың, аналитика мен ұсыныс жүйелерінің операторы болып табылады."
            },
            {
              "type": "p",
              "text": "Осы Оферта ҚР Азаматтық кодексіне, ҚР «Жарнама туралы» Заңына, ҚР «Онлайн-платформалар және онлайн-жарнама туралы» Заңына, ҚР «Дербес деректер және оларды қорғау туралы» Заңына және ҚР өзге де нормативтік құқықтық актілеріне сәйкес әзірленген."
            },
            {
              "type": "p",
              "text": "Оферта акцепті: платформада тіркелу; аккаунт жасау; өтінім ресімдеу; жарнамалық материалдарды жүктеу; электрондық құжаттарға қол қою; қызметтерді төлеу; «Келісемін», «Жалғастыру», «Төлеу», «Орналастыру» батырмаларын басу немесе Офертамен келісімді растайтын өзге әрекеттер."
            },
            {
              "type": "p",
              "text": "Акцепт сәтінен бастап осы Оферта электрондық нысанда жасалған шарт болып саналады."
            }
          ]
        },
        {
          "title": "2. Оферта мәні",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI Тапсырыс берушіге қызметтер ұсынады: онлайн-жарнаманы орналастыру және тарату; жарнамалық материалдарды көрсету; жарнама кампанияларын техникалық қамтамасыз ету; таргеттелген жарнама орналастыру; демеушілік контентті орналастыру; жарнама кампанияларының аналитикасы; жарнамалық материалдарды дайындау, бейімдеу және монтаждау (жеке тапсырыс бойынша)."
            },
            {
              "type": "p",
              "text": "Жарнама веб-сайттарда, мобильді қосымшаларда, AI-станцияларда, цифрлық экрандарда, интерактивті құрылғыларда, серіктес медиа-алаңдарда және CANNECT AI өзге де онлайн- және офлайн-тасымалдағыштарында орналастырылуы мүмкін."
            },
            {
              "type": "p",
              "text": "Орналастырудың нақты параметрлері өтініммен, медиажоспармен, жеке кабинетпен, электрондық келісумен, төлеуге арналған шотпен және техникалық тапсырмамен айқындалады."
            },
            {
              "type": "p",
              "text": "Тарифтер және токен моделі. Тіркелгеннен кейін Пайдаланушыға автоматты түрде Free тарифі беріледі, оның аясында Платформаның функционалдық мүмкіндіктерімен танысу үшін 5 (бес) тегін токен есептеледі. Тегін токендер тек CANNECT AI Платформасы аясында пайдалануға беріледі, ақшаға айырбасталмайды, қайтарылмайды және үшінші тұлғаларға берілмейді. Тегін токендер пайдаланылғаннан кейін Платформаның ақылы функцияларын одан әрі пайдалану токендерді сатып алу немесе тиісті тарифтік жоспарды қосу арқылы жүзеге асырылады. cannect.kz/pricing бетінде жарияланған тарифтік жоспарлар, токендердің құны, оларды есептеу, пайдалану, есептен шығару тәртібі, токендердің қолданылу мерзімі және өзге де шарттар осы Офертаның ажырамас бөлігі болып табылады. Тіркелу және тегін тарифті пайдалану кезінде Пайдаланушының дербес деректерін өңдеу CANNECT AI Құпиялылық саясатына сәйкес жүзеге асырылады."
            }
          ]
        },
        {
          "title": "3. Тапсырыс берушінің жарнамалық материалдар үшін жауапкершілігі",
          "blocks": [
            {
              "type": "p",
              "text": "Тапсырыс беруші мыналар үшін дербес және толық көлемде жауап береді: жарнамалық материалдардың мазмұны; ақпараттың дұрыстығы; жарнаманың ҚР заңнамасына сәйкестігі; қажетті лицензиялардың, рұқсаттардың, сертификаттардың және келісулердің болуы; жарнама және онлайн-жарнама туралы заңнаманы сақтау; үшінші тұлғалардың құқықтарын сақтау; авторлық және сабақтас құқықтарды сақтау; тауар белгілерін, брендтерді, суреттерді, аудио- және видеоматериалдарды пайдалану."
            },
            {
              "type": "p",
              "text": "Тапсырыс беруші жарнамалық материалдардың жалған ақпарат қамтымайтынына, ҚР заңнамасын бұзбайтынына, пайдаланушыларды жаңылыстырмайтынына, кәмелетке толмағандардың құқықтарын бұзбайтынына, құқыққа қарсы контент қамтымайтынына, моральдық және этикалық нормалар мен зияткерлік меншік құқықтарын бұзбайтынына кепілдік береді."
            },
            {
              "type": "p",
              "text": "Тапсырыс беруші ҚР заңнамасында көзделген барлық рұқсаттар мен келісімдерді дербес алады және жарнаманың «Жарнама туралы» Заңы, «Онлайн-платформалар және онлайн-жарнама туралы» Заңы, тұтынушылардың құқықтарын қорғау, дербес деректер және зияткерлік меншік туралы заңнама талаптарына сәйкестігі үшін толық жауап береді."
            }
          ]
        },
        {
          "title": "4. CANNECT AI жауапкершілігін шектеу",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI тек қана онлайн-платформа, техникалық оператор, media/digital платформа, жарнама орналастыру операторы және технологиялық инфрақұрылым жеткізушісі ретінде әрекет етеді. CANNECT AI Тапсырыс берушінің жарнамалық материалдарының авторы, бастамашысы немесе тапсырыс берушісі болып табылмайды."
            },
            {
              "type": "p",
              "text": "CANNECT AI жарнаманың мазмұны, мәліметтердің дұрыстығы, лицензиялардың немесе рұқсаттардың болмауы, авторлық құқықтар мен тауар белгілеріне құқықтардың бұзылуы, Тапсырыс берушінің заңнаманы бұзуы, Тапсырыс берушінің жарнамалық материалдарына байланысты мемлекеттік органдар мен үшінші тұлғалардың талаптары үшін жауап бермейді."
            },
            {
              "type": "p",
              "text": "Тапсырыс берушінің жарнамалық материалдарына байланысты мемлекеттік органдар немесе үшінші тұлғалар тарапынан талаптар, талап-арыздар, айыппұлдар, нұсқамалар не талаптар қойылған жағдайда, Тапсырыс беруші мұндай талаптарды дербес реттеуге, CANNECT AI-ға барлық залалды өтеуге, айыппұлдарды, сот шығындарын, өтемақыларды және өзге де шығындарды өтеуге міндеттенеді."
            }
          ]
        },
        {
          "title": "5. Онлайн-жарнаманы таңбалау",
          "blocks": [
            {
              "type": "p",
              "text": "Онлайн-жарнама ҚР заңнамасына сәйкес сәйкестендірілуі және таңбалануы тиіс. CANNECT AI техникалық белгілер, дисклеймерлер және «Жарнама», «Sponsored», «Ad» және өзге де белгілерді қосуға құқылы. Тапсырыс беруші жарнама белгілері мен таңбаларын жоймауға және жасырмауға міндеттенеді."
            },
            {
              "type": "p",
              "text": "Қазақстан Республикасының аумағындағы CANNECT AI-дың цифрлық экрандарында, AI-станцияларында және өзге де офлайн тасымалдағыштарында орналастырылатын жарнамалық материалдар Қазақстан Республикасының тілдер және жарнама туралы заңнамасының талаптарына сәйкес болуға тиіс. Заңнамада көзделген жағдайларда жарнамалық материалдар мемлекеттік тілде ақпаратты қамтуға тиіс. Қосымша орыс және өзге тілдерді пайдалануға Қазақстан Республикасы заңнамасының талаптары сақталған жағдайда жол беріледі. Көрсетілген талаптардың сақталуына жауапкершілікті Тапсырыс беруші (Жарнама беруші) көтереді."
            }
          ]
        },
        {
          "title": "6. Таргеттелген жарнама және профайлинг",
          "blocks": [
            {
              "type": "p",
              "text": "Таргеттелген жарнаманы орналастыру кезінде Тапсырыс беруші ҚР заңнамасы талаптарын сақтауға міндеттенеді. Тапсырыс берушіге нәсілдік немесе ұлттық тиесілілік, саяси көзқарастар, денсаулық жағдайы туралы деректерді, биометриялық деректерді және өзге де сезімтал дербес деректерді пайдалануға тыйым салынады."
            },
            {
              "type": "p",
              "text": "Таргеттелген жарнама кәмелетке толмаған деп сәйкестендірілген пайдаланушыларға таратылмауы тиіс. CANNECT AI жарнама орналастыруды оңтайландыру үшін ұсыныс жүйелерін, AI-алгоритмдерін және автоматтандырылған аналитика жүйелерін пайдалана алады."
            }
          ]
        },
        {
          "title": "7. Тыйым салынған контент",
          "blocks": [
            {
              "type": "p",
              "text": "Платформада жалған ақпарат, экстремистік материалдар, есірткі құралдарына байланысты, алаяқтық схемаларды қамтитын, порнографиялық сипаттағы, кәмелетке толмағандардың және үшінші тұлғалардың құқықтарын бұзатын, кемсітушілікті, зорлық-зомбылықты не өшпенділікті қоздыруды қамтитын немесе ҚР заңнамасын бұзатын жарнама мен контентті орналастыруға тыйым салынады."
            },
            {
              "type": "p",
              "text": "CANNECT AI жарнамалық материалдарды орналастырудың жол берілуін дербес айқындауға құқылы."
            }
          ]
        },
        {
          "title": "8. Модерация және контентті жою",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI автоматтандырылған, қолмен және AI-модерацияны, сондай-ақ жарнамалық материалдарды тексеруді жүзеге асыруға құқылы."
            },
            {
              "type": "p",
              "text": "CANNECT AI жарнамаға қол жеткізуді шектеуге, жарнамалық материалдарды жоюға, орналастыруды тоқтата тұруға, Тапсырыс берушінің аккаунтын бұғаттауға не жарнама орналастырудан бас тартуға құқылы. Негіздер: заңнаманы бұзу; мемлекеттік органның нұсқамасын алу; пайдаланушылардың шағымдары; құқыққа қарсы контент белгілері; платформаның ішкі ережелерін бұзу."
            }
          ]
        },
        {
          "title": "9. Ақпаратты сақтау және аналитика",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI жарнамалық материалдарды, логтарды, статистиканы, жарнама орналастыру деректерін және жарнама кампанияларының өзгерістер тарихын сақтауға құқылы. Онлайн-жарнама туралы ақпарат орналастыру аяқталғаннан кейін кемінде бір жыл сақталуы мүмкін."
            },
            {
              "type": "p",
              "text": "CANNECT AI платформаны жақсарту, аналитика, жарнама кампанияларын оңтайландыру және AI-алгоритмдерін дамыту үшін иесіздендірілген аналитика мен статистиканы пайдалана алады."
            }
          ]
        },
        {
          "title": "10. Пайдаланушылардың шағымдары",
          "blocks": [
            {
              "type": "p",
              "text": "Пайдаланушылар жарнамалық материалдарға шағым жіберуге құқылы. CANNECT AI Тапсырыс берушіге шағымдар туралы хабарлама жіберуге, жарнаманы өзгертуді немесе жоюды талап етуге және дауланатын контенттің таралуын шектеуге құқылы. Тапсырыс беруші CANNECT AI талаптарына дереу әрекет етуге міндетті."
            }
          ]
        },
        {
          "title": "11. Мемлекеттік органдармен өзара іс-қимыл",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI Қазақстан Республикасы мемлекеттік органдарының міндетті талаптарын орындауға құқылы, оның ішінде контентке қол жеткізуді шектеу, контентті жою, ақпаратты уәкілетті органдарға беру және сот актілері мен нұсқамаларын орындау."
            }
          ]
        },
        {
          "title": "12. Дербес деректер",
          "blocks": [
            {
              "type": "p",
              "text": "Дербес деректерді өңдеу ҚР заңнамасына сәйкес жүзеге асырылады. Тапсырыс беруші жарнама кампанияларында пайдаланылатын дербес деректерді берудің заңдылығына кепілдік береді. CANNECT AI ҚР заңнамасы шеңберінде cookies, device ID, аналитикалық және техникалық құралдарды пайдалана алады."
            }
          ]
        },
        {
          "title": "13. Зияткерлік меншік",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI платформасына, бағдарламалық жасақтамаға, AI-алгоритмдерге, интерфейстерге, дизайнға және аналитикалық жүйелерге барлық құқықтар CANNECT AI-ға немесе тиісті құқық иелеріне тиесілі. Жарнама орналастыру Тапсырыс берушіге CANNECT AI технологиялары мен бағдарламалық жасақтамасына құқық бермейді."
            }
          ]
        },
        {
          "title": "14. Электрондық өзара іс-қимыл",
          "blocks": [
            {
              "type": "p",
              "text": "Тараптар электрондық өтінімдердің, хат-хабардың, жеке кабинеттегі әрекеттердің, цифрлық логтардың, электрондық растаулардың және онлайн-төлемдердің заңды күшін мойындайды. Тапсырыс берушінің электрондық әрекеттері өз қолымен қойылған қолтаңбаның баламасы ретінде танылады."
            }
          ]
        },
        {
          "title": "15. Қорытынды ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Осы Оферта Қазақстан Республикасының заңнамасымен реттеледі. Барлық даулар келіссөздер арқылы, ал келісімге қол жеткізілмеген жағдайда CANNECT AI тіркелген жер бойынша сот тәртібімен шешілуге жатады."
            },
            {
              "type": "p",
              "text": "CANNECT AI осы Оферта шарттарын платформада немесе сайтта жаңа редакцияны жариялау арқылы біржақты тәртіппен өзгертуге құқылы. Жаңа редакция жарияланғаннан кейін платформаны әрі қарай пайдалану Тапсырыс берушінің өзгерістермен келісімін білдіреді. Офертаның өзекті редакциясы CANNECT AI ресми сайтында орналастырылады."
            }
          ]
        }
      ],
      "updatedValue": "2026 жылғы 14 мамыр (16.07.2026 ж. жағдай бойынша өзгерістер мен толықтырулармен)"
    },
    "tr": {
      "kicker": "Yasal",
      "title": "Kamu Teklifi",
      "updatedLabel": "Son güncelleme",
      "sections": [
        {
          "title": "1. Genel Hükümler",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Kamu Teklifi (“Teklif”), CANNECT.AI LLP (“CANNECT AI”, “Şirket”, “Platform”) ile gerçek ve/veya tüzel kişiler (“Müşteri”, “Reklamveren”) arasında, reklam ve bilgilendirme materyallerinin CANNECT AI çevrimiçi platformunda yerleştirilmesi, üretilmesi, dağıtılması, saklanması, gösterilmesi ve teknik desteğine ilişkin ilişkileri düzenler."
            },
            {
              "type": "p",
              "text": "CANNECT AI şu sıfatlarla hareket eder: çevrimiçi platformun sahibi ve/veya işleticisi; reklam yerleştirmenin teknik işleticisi; bir dijital/medya platformu; yapay zeka araçlarının, analitiğin ve öneri sistemlerinin işleticisi."
            },
            {
              "type": "p",
              "text": "Bu Teklif; KC Medeni Kanunu, “Reklamcılık Hakkında” KC Kanunu, “Çevrimiçi Platformlar ve Çevrimiçi Reklamcılık Hakkında” KC Kanunu, “Kişisel Veriler ve Bunların Korunması Hakkında” KC Kanunu ve KC'nin diğer düzenleyici hukuki düzenlemelerine uygun olarak hazırlanmıştır."
            },
            {
              "type": "p",
              "text": "Bu Teklifin kabulü şunlardır: platforma kayıt; hesap oluşturma; talep gönderme; reklam materyalleri yükleme; elektronik belgeleri imzalama; hizmetlerin ödemesini yapma; “Kabul ediyorum”, “Devam et”, “Öde”, “Yerleştir” düğmelerine tıklama veya Teklif'e onayı teyit eden diğer eylemler."
            },
            {
              "type": "p",
              "text": "Kabul anından itibaren bu Teklif, elektronik biçimde akdedilmiş bir sözleşme sayılır."
            }
          ]
        },
        {
          "title": "2. Teklifin Konusu",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI Müşteriye şu hizmetleri sağlar: çevrimiçi reklam yerleştirme ve dağıtma; reklam materyallerinin gösterimi; reklam kampanyalarının teknik desteği; hedefli reklam; sponsorlu içerik yerleştirme; reklam kampanyası analitiği; reklam materyallerinin üretimi, uyarlanması ve montajı (ayrı sipariş üzerine)."
            },
            {
              "type": "p",
              "text": "Reklamlar web sitelerinde, mobil uygulamalarda, yapay zeka istasyonlarında, dijital ekranlarda, etkileşimli cihazlarda, ortak medya platformlarında ve CANNECT AI'nın diğer çevrimiçi ve çevrimdışı mecralarında yerleştirilebilir."
            },
            {
              "type": "p",
              "text": "Belirli yerleştirme parametreleri bir talep, medya planı, kişisel hesap, elektronik anlaşma, fatura veya teknik şartname ile belirlenir."
            },
            {
              "type": "p",
              "text": "Tarifeler ve token modeli. Kayıt sonrasında Kullanıcıya otomatik olarak Free tarifesi tanımlanır; bu kapsamda Platformun işlevlerini tanımak üzere 5 (beş) ücretsiz token yüklenir. Ücretsiz tokenlar yalnızca CANNECT AI Platformu içinde kullanılabilir; nakde çevrilemez, iade edilemez ve üçüncü kişilere devredilemez. Ücretsiz tokenlar kullanıldıktan sonra Platformun ücretli özelliklerinin kullanımı, token satın alınması veya ilgili tarife planına abone olunması yoluyla sürdürülür. cannect.kz/pricing sayfasında yayımlanan tarife planları, token fiyatları, tokenların yüklenme, kullanım ve düşülme koşulları, geçerlilik süreleri ve diğer şartlar işbu Teklifin ayrılmaz parçasıdır. Kayıt ve ücretsiz tarife kullanımı sırasında Kullanıcının kişisel verileri CANNECT AI Gizlilik Politikasına uygun olarak işlenir."
            }
          ]
        },
        {
          "title": "3. Müşterinin Reklam Materyallerine İlişkin Sorumluluğu",
          "blocks": [
            {
              "type": "p",
              "text": "Müşteri şunlardan tek başına ve tam olarak sorumludur: reklam materyallerinin içeriği; bilgilerin doğruluğu; reklamın KC mevzuatına uygunluğu; gerekli lisansların, izinlerin, sertifikaların ve onayların bulunması; reklam ve çevrimiçi reklam hukukuna uyum; üçüncü tarafların haklarına saygı; telif hakları ve bağlantılı haklar; ticari markaların, markaların, görsellerin, ses ve video materyallerinin kullanımı."
            },
            {
              "type": "p",
              "text": "Müşteri, reklam materyallerinin yanlış bilgi içermediğini, KC mevzuatını ihlal etmediğini, kullanıcıları yanıltmadığını, reşit olmayanların haklarını ihlal etmediğini, yasa dışı içerik barındırmadığını, ahlaki ve etik normları ve fikri mülkiyet haklarını ihlal etmediğini garanti eder."
            },
            {
              "type": "p",
              "text": "Müşteri, KC mevzuatınca gerekli görülen tüm izinleri ve rızaları bağımsız olarak alır ve “Reklamcılık Hakkında” Kanun, “Çevrimiçi Platformlar ve Çevrimiçi Reklamcılık Hakkında” Kanun, tüketicinin korunması, kişisel veriler ve fikri mülkiyet mevzuatına uyumdan tam olarak sorumludur."
            }
          ]
        },
        {
          "title": "4. CANNECT AI Sorumluluğunun Sınırlandırılması",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI yalnızca çevrimiçi platform, teknik işletici, medya/dijital platform, reklam yerleştirme işleticisi ve teknolojik altyapı sağlayıcısı olarak hareket eder. CANNECT AI, Müşterinin reklam materyallerinin yazarı, başlatıcısı veya sipariş vereni değildir."
            },
            {
              "type": "p",
              "text": "CANNECT AI; reklam içeriğinden, bilgilerin doğruluğundan, lisans veya izinlerin bulunmamasından, telif hakkı veya ticari marka ihlalinden, Müşterinin yasa ihlallerinden ya da Müşterinin reklam materyalleriyle ilgili devlet kurumlarından ve üçüncü taraflardan gelen taleplerden sorumlu değildir."
            },
            {
              "type": "p",
              "text": "Müşterinin reklam materyalleriyle ilgili olarak devlet kurumlarından veya üçüncü taraflardan talepler, davalar, para cezaları, emirler veya talepler gelmesi halinde, Müşteri bu talepleri bağımsız olarak çözmeyi, CANNECT AI'nın tüm zararlarını tazmin etmeyi ve para cezalarını, yasal masrafları, tazminatları ve diğer giderleri karşılamayı taahhüt eder."
            }
          ]
        },
        {
          "title": "5. Çevrimiçi Reklamın Etiketlenmesi",
          "blocks": [
            {
              "type": "p",
              "text": "Çevrimiçi reklam, KC mevzuatına uygun olarak tanımlanabilir ve etiketlenmiş olmalıdır. CANNECT AI teknik işaretler, sorumluluk reddi beyanları ve “Reklam”, “Sponsored”, “Ad” veya diğer etiketleri ekleyebilir. Müşteri, reklam işaretlerini ve etiketlerini kaldırmamayı veya gizlememeyi taahhüt eder."
            },
            {
              "type": "p",
              "text": "Kazakistan Cumhuriyeti sınırları içinde CANNECT AI'ın dijital ekranlarında, AI istasyonlarında ve diğer çevrimdışı mecralarında yayınlanan reklam materyalleri, Kazakistan Cumhuriyeti'nin diller ve reklam mevzuatının gerekliliklerine uygun olmalıdır. Mevzuatta öngörülen hâllerde reklam materyalleri devlet dilinde bilgi içermelidir. Kazakistan Cumhuriyeti mevzuatının gerekliliklerine uyulması şartıyla ayrıca Rusça ve diğer dillerin kullanılmasına izin verilir. Bu gerekliliklere uyumdan Müşteri (Reklamveren) sorumludur."
            }
          ]
        },
        {
          "title": "6. Hedefli Reklam ve Profilleme",
          "blocks": [
            {
              "type": "p",
              "text": "Hedefli reklam yerleştirirken Müşteri KC mevzuatına uymayı taahhüt eder. Müşterinin; ırksal veya etnik köken, siyasi görüşler, sağlık durumu, biyometrik veriler ve diğer hassas kişisel verilere ilişkin verileri kullanması yasaktır."
            },
            {
              "type": "p",
              "text": "Hedefli reklam, reşit olmadığı tespit edilen kullanıcılara dağıtılmamalıdır. CANNECT AI, reklam yerleştirmeyi optimize etmek için öneri sistemlerini, yapay zeka algoritmalarını ve otomatik analitik sistemlerini kullanabilir."
            }
          ]
        },
        {
          "title": "7. Yasaklı İçerik",
          "blocks": [
            {
              "type": "p",
              "text": "Yanlış bilgi, aşırılıkçı materyaller, uyuşturucu maddelerle ilgili içerik, dolandırıcılık planları, pornografik içerik, reşit olmayanların veya üçüncü tarafların haklarını ihlal eden içerik, ayrımcılık, şiddet ya da nefret kışkırtması barındıran veya başka şekilde KC mevzuatını ihlal eden reklam ve içeriğin yerleştirilmesi yasaktır."
            },
            {
              "type": "p",
              "text": "CANNECT AI, reklam materyallerinin yerleştirilmesinin kabul edilebilirliğini bağımsız olarak belirleyebilir."
            }
          ]
        },
        {
          "title": "8. Moderasyon ve İçeriğin Kaldırılması",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI, reklam materyallerinin otomatik, manuel ve yapay zeka moderasyonunu ve incelemesini gerçekleştirebilir."
            },
            {
              "type": "p",
              "text": "CANNECT AI; reklama erişimi kısıtlayabilir, reklam materyallerini kaldırabilir, yerleştirmeyi askıya alabilir, Müşterinin hesabını bloke edebilir veya yerleştirmeyi reddedebilir. Gerekçeler şunlardır: yasa ihlali; bir devlet kurumundan gelen emir; kullanıcı şikayetleri; yasa dışı içerik belirtileri; platformun iç kurallarının ihlali."
            }
          ]
        },
        {
          "title": "9. Bilgi Saklama ve Analitik",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI; reklam materyallerini, günlükleri, istatistikleri, reklam yerleştirme verilerini ve reklam kampanyalarının değişiklik geçmişini saklayabilir. Çevrimiçi reklam bilgileri, yerleştirme sona erdikten sonra en az bir yıl saklanabilir."
            },
            {
              "type": "p",
              "text": "CANNECT AI; platformu iyileştirmek, analitik, reklam kampanyalarını optimize etmek ve yapay zeka algoritmalarını geliştirmek için anonimleştirilmiş analitik ve istatistikleri kullanabilir."
            }
          ]
        },
        {
          "title": "10. Kullanıcı Şikayetleri",
          "blocks": [
            {
              "type": "p",
              "text": "Kullanıcılar reklam materyalleri hakkında şikayet gönderebilir. CANNECT AI; Müşteriye şikayetleri bildirebilir, reklamda değişiklik veya reklamın kaldırılmasını talep edebilir ve tartışmalı içeriğin dağıtımını kısıtlayabilir. Müşteri, CANNECT AI'nın taleplerine derhal yanıt vermelidir."
            }
          ]
        },
        {
          "title": "11. Devlet Kurumlarıyla Etkileşim",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI, Kazakistan Cumhuriyeti devlet kurumlarının zorunlu taleplerine uyabilir; buna içeriğe erişimi kısıtlama, içeriği kaldırma, bilgileri yetkili kurumlara aktarma ve mahkeme kararları ile emirleri uygulama dahildir."
            }
          ]
        },
        {
          "title": "12. Kişisel Veriler",
          "blocks": [
            {
              "type": "p",
              "text": "Kişisel verilerin işlenmesi KC mevzuatına uygun olarak gerçekleştirilir. Müşteri, reklam kampanyalarında kullanılan kişisel verilerin aktarımının hukuka uygunluğunu garanti eder. CANNECT AI, KC mevzuatı çerçevesinde çerezleri, cihaz kimliğini, analitik ve teknik araçları kullanabilir."
            }
          ]
        },
        {
          "title": "13. Fikri Mülkiyet",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI platformuna, yazılımına, yapay zeka algoritmalarına, arayüzlerine, tasarımına ve analitik sistemlerine ilişkin tüm haklar CANNECT AI'ya veya ilgili hak sahiplerine aittir. Reklam yerleştirme, Müşteriye CANNECT AI teknolojileri ve yazılımı üzerinde herhangi bir hak vermez."
            }
          ]
        },
        {
          "title": "14. Elektronik Etkileşim",
          "blocks": [
            {
              "type": "p",
              "text": "Taraflar; elektronik taleplerin, yazışmaların, kişisel hesaptaki eylemlerin, dijital günlüklerin, elektronik teyitlerin ve çevrimiçi ödemelerin hukuki gücünü tanır. Müşterinin elektronik eylemleri, el yazısı imzanın eşdeğeri olarak kabul edilir."
            }
          ]
        },
        {
          "title": "15. Nihai Hükümler",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Teklif Kazakistan Cumhuriyeti mevzuatına tabidir. Tüm uyuşmazlıklar müzakere yoluyla, anlaşmaya varılamaması halinde CANNECT AI'nın kayıtlı olduğu yerdeki mahkemede çözülür."
            },
            {
              "type": "p",
              "text": "CANNECT AI, bu Teklifin şartlarını platformda veya web sitesinde yeni bir sürüm yayımlayarak tek taraflı olarak değiştirebilir. Yeni bir sürümün yayımlanmasından sonra platformun kullanılmaya devam edilmesi, Müşterinin değişiklikleri kabul ettiği anlamına gelir. Teklifin güncel sürümü resmi CANNECT AI web sitesinde yayımlanır."
            }
          ]
        }
      ],
      "updatedValue": "14 Mayıs 2026 (16.07.2026 itibarıyla yapılan değişiklik ve eklemelerle)"
    },
    "zh": {
      "kicker": "法律信息",
      "title": "公开要约",
      "updatedLabel": "更新于",
      "sections": [
        {
          "title": "1. 总则",
          "blocks": [
            {
              "type": "p",
              "text": "本公开要约（以下简称“要约”）规范 CANNECT.AI LLP（以下简称“CANNECT AI”、“公司”、“平台”）与自然人和/或法人（以下简称“客户”、“广告主”）之间，就在 CANNECT AI 在线平台上投放、制作、分发、存储、展示及技术支持广告和信息材料所产生的关系。"
            },
            {
              "type": "p",
              "text": "CANNECT AI 的角色为：在线平台的所有者和/或运营者；广告投放的技术运营者；数字/媒体平台；AI 工具、数据分析和推荐系统的运营者。"
            },
            {
              "type": "p",
              "text": "本要约依据哈萨克斯坦共和国《民法典》、哈萨克斯坦共和国《广告法》、哈萨克斯坦共和国《在线平台与在线广告法》、哈萨克斯坦共和国《个人数据及其保护法》以及哈萨克斯坦共和国其他规范性法律文件制定。"
            },
            {
              "type": "p",
              "text": "对本要约的承诺包括：在平台注册；创建账户；提交申请；上传广告材料；签署电子文件；支付服务费用；点击“同意”、“继续”、“支付”、“投放”按钮或其他确认同意本要约的行为。"
            },
            {
              "type": "p",
              "text": "自承诺之时起，本要约即视为以电子形式订立的合同。"
            }
          ]
        },
        {
          "title": "2. 要约标的",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI 向客户提供以下服务：在线广告投放与分发；广告材料展示；广告活动的技术支持；定向广告；赞助内容投放；广告活动数据分析；广告材料的制作、改编与剪辑（需另行下单）。"
            },
            {
              "type": "p",
              "text": "广告可在网站、移动应用、AI 站点、数字屏幕、互动设备、合作伙伴媒体平台以及 CANNECT AI 的其他线上和线下媒介上投放。"
            },
            {
              "type": "p",
              "text": "具体的投放参数由申请、媒体计划、个人账户、电子协议、付款发票或技术规格确定。"
            },
            {
              "type": "p",
              "text": "资费与代币模式。用户注册后将自动获得 Free（免费）套餐，其中包含 5（五）个免费代币，用于体验平台功能。免费代币仅可在 CANNECT AI 平台内使用，不可兑换为现金、不可退款、不可转让给第三方。免费代币用尽后，继续使用平台的付费功能需购买代币或开通相应的资费套餐。在 cannect.kz/pricing 页面公布的资费套餐、代币价格、代币的发放、使用与扣减规则、代币有效期及其他条款，构成本要约不可分割的组成部分。注册并使用免费套餐时，用户个人数据的处理依照 CANNECT AI 隐私政策进行。"
            }
          ]
        },
        {
          "title": "3. 客户对广告材料的责任",
          "blocks": [
            {
              "type": "p",
              "text": "客户独立并全面承担以下责任：广告材料的内容；信息的准确性；广告符合哈萨克斯坦共和国法律；具备所需的许可证、批准、证书和审批；遵守广告及在线广告法律；尊重第三方权利；著作权及邻接权；商标、品牌、图像、音频和视频材料的使用。"
            },
            {
              "type": "p",
              "text": "客户保证广告材料不含虚假信息，不违反哈萨克斯坦共和国法律，不误导用户，不侵害未成年人权利，不含非法内容，不违反道德与伦理规范，且不侵犯知识产权。"
            },
            {
              "type": "p",
              "text": "客户自行取得哈萨克斯坦共和国法律所要求的一切许可和同意，并就遵守《广告法》、《在线平台与在线广告法》以及消费者保护、个人数据和知识产权法律承担全部责任。"
            }
          ]
        },
        {
          "title": "4. CANNECT AI 的责任限制",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI 仅作为在线平台、技术运营者、媒体/数字平台、广告投放运营者及技术基础设施提供者。CANNECT AI 不是客户广告材料的作者、发起人或委托方。"
            },
            {
              "type": "p",
              "text": "CANNECT AI 对以下事项不承担责任：广告的内容、信息的准确性、缺少许可证或批准、侵犯著作权或商标权、客户的违法行为，以及国家机关和第三方就客户广告材料提出的主张。"
            },
            {
              "type": "p",
              "text": "如国家机关或第三方就客户广告材料提出索赔、诉讼、罚款、命令或要求，客户承诺自行解决此类主张，赔偿 CANNECT AI 的全部损失，并补偿罚款、诉讼费用、赔偿金及其他费用。"
            }
          ]
        },
        {
          "title": "5. 在线广告标识",
          "blocks": [
            {
              "type": "p",
              "text": "在线广告必须可识别并依据哈萨克斯坦共和国法律加以标识。CANNECT AI 可添加技术标记、免责声明以及“广告”、“Sponsored”、“Ad”或其他标识。客户承诺不删除或隐藏广告标记与标识。"
            },
            {
              "type": "p",
              "text": "在哈萨克斯坦共和国境内投放于 CANNECT AI 数字屏幕、AI 站点及其他线下载体的广告材料，必须符合哈萨克斯坦共和国关于语言和广告的法律要求。在法律规定的情形下，广告材料必须包含国语信息。在遵守哈萨克斯坦共和国法律要求的前提下，允许另行使用俄语及其他语言。遵守上述要求的责任由客户（广告主）承担。"
            }
          ]
        },
        {
          "title": "6. 定向广告与画像分析",
          "blocks": [
            {
              "type": "p",
              "text": "在投放定向广告时，客户承诺遵守哈萨克斯坦共和国法律。禁止客户使用关于种族或民族出身、政治观点、健康状况、生物识别数据及其他敏感个人数据。"
            },
            {
              "type": "p",
              "text": "定向广告不得向被识别为未成年人的用户分发。CANNECT AI 可使用推荐系统、AI 算法和自动化数据分析系统以优化广告投放。"
            }
          ]
        },
        {
          "title": "7. 禁止内容",
          "blocks": [
            {
              "type": "p",
              "text": "禁止投放含有以下内容的广告和内容：虚假信息、极端主义材料、与毒品相关的内容、欺诈方案、色情内容、侵害未成年人或第三方权利的内容、歧视、暴力或煽动仇恨的内容，或以其他方式违反哈萨克斯坦共和国法律的内容。"
            },
            {
              "type": "p",
              "text": "CANNECT AI 可自行判定广告材料投放的可行性。"
            }
          ]
        },
        {
          "title": "8. 审核与内容删除",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI 可对广告材料进行自动化、人工和 AI 审核与核查。"
            },
            {
              "type": "p",
              "text": "CANNECT AI 可限制对广告的访问、删除广告材料、暂停投放、封禁客户账户或拒绝投放。理由包括：违反法律；国家机关的命令；用户投诉；存在非法内容的迹象；违反平台内部规则。"
            }
          ]
        },
        {
          "title": "9. 信息存储与数据分析",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI 可存储广告材料、日志、统计数据、广告投放数据以及广告活动的变更历史。在线广告信息可在投放结束后至少保存一年。"
            },
            {
              "type": "p",
              "text": "CANNECT AI 可使用匿名化的数据分析和统计，以改进平台、进行数据分析、优化广告活动并开发 AI 算法。"
            }
          ]
        },
        {
          "title": "10. 用户投诉",
          "blocks": [
            {
              "type": "p",
              "text": "用户可就广告材料提交投诉。CANNECT AI 可就投诉通知客户、要求修改或删除广告，并限制争议内容的分发。客户必须及时响应 CANNECT AI 的要求。"
            }
          ]
        },
        {
          "title": "11. 与国家机关的互动",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI 可履行哈萨克斯坦共和国国家机关的强制性要求，包括限制对内容的访问、删除内容、向授权机关传递信息，以及执行法院裁定和命令。"
            }
          ]
        },
        {
          "title": "12. 个人数据",
          "blocks": [
            {
              "type": "p",
              "text": "个人数据的处理依据哈萨克斯坦共和国法律进行。客户保证在广告活动中所用个人数据传输的合法性。CANNECT AI 可在哈萨克斯坦共和国法律框架内使用 cookies、device ID、分析和技术工具。"
            }
          ]
        },
        {
          "title": "13. 知识产权",
          "blocks": [
            {
              "type": "p",
              "text": "CANNECT AI 平台、软件、AI 算法、界面、设计和数据分析系统的全部权利归 CANNECT AI 或相应权利人所有。投放广告不向客户授予对 CANNECT AI 技术和软件的任何权利。"
            }
          ]
        },
        {
          "title": "14. 电子互动",
          "blocks": [
            {
              "type": "p",
              "text": "双方承认电子申请、往来函件、个人账户内操作、数字日志、电子确认和在线支付的法律效力。客户的电子操作被认定为等同于亲笔签名。"
            }
          ]
        },
        {
          "title": "15. 最终条款",
          "blocks": [
            {
              "type": "p",
              "text": "本要约受哈萨克斯坦共和国法律管辖。一切争议应通过协商解决，协商不成的，由 CANNECT AI 注册地法院诉讼解决。"
            },
            {
              "type": "p",
              "text": "CANNECT AI 可通过在平台或网站发布新版本，单方面修改本要约的条款。在新版本发布后继续使用平台即表示客户同意相关变更。本要约的现行版本发布于 CANNECT AI 官方网站。"
            }
          ]
        }
      ],
      "updatedValue": "2026年5月14日（截至2026年7月16日的修订与补充）"
    },
    "ar": {
      "kicker": "قانوني",
      "title": "العرض العام",
      "updatedLabel": "آخر تحديث",
      "sections": [
        {
          "title": "1. أحكام عامة",
          "blocks": [
            {
              "type": "p",
              "text": "ينظّم هذا العرض العام (\"العرض\") العلاقة بين CANNECT.AI LLP (\"CANNECT AI\"، \"الشركة\"، \"المنصة\") والأفراد و/أو الكيانات القانونية (\"العميل\"، \"المعلن\") فيما يتعلق بإطلاق وإنتاج وتوزيع وتخزين وعرض والدعم التقني للمواد الإعلانية والمعلوماتية على منصة CANNECT AI الإلكترونية."
            },
            {
              "type": "p",
              "text": "تعمل CANNECT AI بصفتها: مالكة و/أو مشغّلة للمنصة الإلكترونية؛ ومشغّلة تقنية لإطلاق الإعلانات؛ ومنصة رقمية/إعلامية؛ ومشغّلة لأدوات الذكاء الاصطناعي وأنظمة التحليلات والتوصيات."
            },
            {
              "type": "p",
              "text": "أُعدّ هذا العرض وفقًا للقانون المدني لجمهورية كازاخستان، وقانون جمهورية كازاخستان \"بشأن الإعلان\"، وقانون جمهورية كازاخستان \"بشأن المنصات الإلكترونية والإعلان الإلكتروني\"، وقانون جمهورية كازاخستان \"بشأن البيانات الشخصية وحمايتها\"، وغيرها من الأنظمة القانونية المعمول بها في جمهورية كازاخستان."
            },
            {
              "type": "p",
              "text": "يُعدّ قبولًا لهذا العرض: التسجيل في المنصة؛ إنشاء الحساب؛ تقديم طلب؛ رفع المواد الإعلانية؛ توقيع المستندات الإلكترونية؛ دفع مقابل الخدمات؛ النقر على أزرار \"موافق\" أو \"متابعة\" أو \"دفع\" أو \"نشر\"، أو غيرها من الأفعال التي تؤكد الموافقة على العرض."
            },
            {
              "type": "p",
              "text": "اعتبارًا من لحظة القبول، يُعدّ هذا العرض عقدًا مُبرمًا في شكل إلكتروني."
            }
          ]
        },
        {
          "title": "2. موضوع العرض",
          "blocks": [
            {
              "type": "p",
              "text": "تقدّم CANNECT AI للعميل الخدمات التالية: إطلاق وتوزيع الإعلانات الإلكترونية؛ عرض المواد الإعلانية؛ الدعم التقني للحملات الإعلانية؛ الإعلان المستهدف؛ نشر المحتوى المموّل؛ تحليلات الحملات الإعلانية؛ إنتاج المواد الإعلانية وتكييفها وتحريرها (بناءً على طلب منفصل)."
            },
            {
              "type": "p",
              "text": "يمكن نشر الإعلانات على المواقع الإلكترونية وتطبيقات الهاتف المحمول ومحطات الذكاء الاصطناعي والشاشات الرقمية والأجهزة التفاعلية والمنصات الإعلامية الشريكة وغيرها من الوسائط الإلكترونية وغير المتصلة التابعة لـ CANNECT AI."
            },
            {
              "type": "p",
              "text": "تُحدَّد معايير النشر المحددة بموجب طلب أو خطة إعلامية أو الحساب الشخصي أو اتفاق إلكتروني أو فاتورة أو مواصفات تقنية."
            },
            {
              "type": "p",
              "text": "التعريفات ونموذج الرموز (التوكن). عند التسجيل، يُمنح المستخدم تلقائيًا خطة Free التي تشمل 5 (خمسة) رموز مجانية للتعرّف على إمكانات المنصة. تُمنح الرموز المجانية للاستخدام داخل منصة CANNECT AI حصريًا، ولا تخضع للاستبدال بالنقد أو الاسترداد أو النقل إلى أطراف ثالثة. بعد استنفاد الرموز المجانية، يتم استخدام الميزات المدفوعة للمنصة عبر شراء الرموز أو الاشتراك في خطة التعرفة المناسبة. إن خطط التعرفة وأسعار الرموز وقواعد منحها واستخدامها وخصمها ومدد صلاحيتها وسائر الشروط المنشورة على صفحة cannect.kz/pricing تُعد جزءًا لا يتجزأ من هذا العرض. وعند التسجيل واستخدام الخطة المجانية، تُعالج البيانات الشخصية للمستخدم وفقًا لسياسة الخصوصية الخاصة بـ CANNECT AI."
            }
          ]
        },
        {
          "title": "3. مسؤولية العميل عن المواد الإعلانية",
          "blocks": [
            {
              "type": "p",
              "text": "يتحمّل العميل وحده المسؤولية الكاملة عن: محتوى المواد الإعلانية؛ ودقة المعلومات؛ وامتثال الإعلان لتشريعات جمهورية كازاخستان؛ وتوافر التراخيص والتصاريح والشهادات والموافقات المطلوبة؛ والامتثال لقانون الإعلان والإعلان الإلكتروني؛ واحترام حقوق الغير؛ وحقوق النشر والحقوق المجاورة؛ واستخدام العلامات التجارية والماركات والصور والمواد الصوتية والمرئية."
            },
            {
              "type": "p",
              "text": "يضمن العميل أن المواد الإعلانية لا تحتوي على معلومات كاذبة، ولا تنتهك تشريعات جمهورية كازاخستان، ولا تُضلّل المستخدمين، ولا تنتهك حقوق القُصّر، ولا تتضمّن محتوى غير قانوني، ولا تخالف القيم الأخلاقية والأدبية، ولا تنتهك حقوق الملكية الفكرية."
            },
            {
              "type": "p",
              "text": "يحصل العميل بنفسه على جميع التصاريح والموافقات التي تتطلبها تشريعات جمهورية كازاخستان، ويتحمّل المسؤولية الكاملة عن الامتثال لقانون \"الإعلان\"، وقانون \"المنصات الإلكترونية والإعلان الإلكتروني\"، وتشريعات حماية المستهلك والبيانات الشخصية والملكية الفكرية."
            }
          ]
        },
        {
          "title": "4. تحديد مسؤولية CANNECT AI",
          "blocks": [
            {
              "type": "p",
              "text": "تعمل CANNECT AI فقط بصفتها منصة إلكترونية ومشغّلًا تقنيًا ومنصة إعلامية/رقمية ومشغّلًا لإطلاق الإعلانات ومزوّدًا للبنية التحتية التقنية. وليست CANNECT AI مؤلِّفة المواد الإعلانية للعميل ولا الجهة التي بادرت بها أو طلبتها."
            },
            {
              "type": "p",
              "text": "لا تتحمّل CANNECT AI المسؤولية عن محتوى الإعلان أو دقة المعلومات أو غياب التراخيص أو التصاريح أو انتهاك حقوق النشر أو العلامات التجارية أو مخالفات العميل للقانون أو المطالبات الصادرة عن الجهات الحكومية والغير المتعلقة بالمواد الإعلانية للعميل."
            },
            {
              "type": "p",
              "text": "في حال ورود مطالبات أو دعاوى أو غرامات أو أوامر أو مطالب من الجهات الحكومية أو الغير تتعلق بالمواد الإعلانية للعميل، يتعهّد العميل بتسوية هذه المطالبات بنفسه، وتعويض CANNECT AI عن جميع الخسائر، ورد الغرامات والتكاليف القانونية والتعويضات وغيرها من النفقات."
            }
          ]
        },
        {
          "title": "5. وسم الإعلان الإلكتروني",
          "blocks": [
            {
              "type": "p",
              "text": "يجب أن يكون الإعلان الإلكتروني قابلًا للتمييز وموسومًا وفقًا لتشريعات جمهورية كازاخستان. ويجوز لـ CANNECT AI إضافة إشارات تقنية وإخلاءات مسؤولية ووسوم مثل \"إعلان\" أو \"مموّل\" أو \"Ad\" أو غيرها. ويتعهّد العميل بعدم إزالة أو إخفاء إشارات ووسوم الإعلان."
            },
            {
              "type": "p",
              "text": "يجب أن تمتثل المواد الإعلانية المعروضة على الشاشات الرقمية ومحطات الذكاء الاصطناعي وسائر الوسائط غير المتصلة التابعة لـ CANNECT AI داخل جمهورية كازاخستان لمتطلبات تشريعات جمهورية كازاخستان بشأن اللغات والإعلان. وفي الحالات التي ينص عليها القانون، يجب أن تتضمن المواد الإعلانية معلومات باللغة الرسمية للدولة. ويُسمح إضافةً إلى ذلك باستخدام اللغة الروسية وغيرها من اللغات شريطة استيفاء متطلبات تشريعات جمهورية كازاخستان. ويتحمل العميل (المعلن) مسؤولية الامتثال لهذه المتطلبات."
            }
          ]
        },
        {
          "title": "6. الإعلان المستهدف وإنشاء الملفات التعريفية",
          "blocks": [
            {
              "type": "p",
              "text": "عند إطلاق إعلان مستهدف، يتعهّد العميل بالامتثال لتشريعات جمهورية كازاخستان. ويُحظر على العميل استخدام البيانات المتعلقة بالأصل العِرقي أو الإثني أو الآراء السياسية أو الحالة الصحية أو البيانات البيومترية وغيرها من البيانات الشخصية الحساسة."
            },
            {
              "type": "p",
              "text": "لا يجوز توزيع الإعلان المستهدف على المستخدمين المُصنّفين كقُصّر. ويجوز لـ CANNECT AI استخدام أنظمة التوصية وخوارزميات الذكاء الاصطناعي وأنظمة التحليلات الآلية لتحسين إطلاق الإعلانات."
            }
          ]
        },
        {
          "title": "7. المحتوى المحظور",
          "blocks": [
            {
              "type": "p",
              "text": "يُحظر نشر الإعلانات والمحتوى إذا كان يتضمّن معلومات كاذبة أو مواد متطرفة أو محتوى متعلقًا بالمواد المخدرة أو مخططات احتيالية أو محتوى إباحيًا أو محتوى ينتهك حقوق القُصّر أو الغير أو التمييز أو العنف أو التحريض على الكراهية، أو يخالف بأي شكل آخر تشريعات جمهورية كازاخستان."
            },
            {
              "type": "p",
              "text": "يجوز لـ CANNECT AI أن تحدد بنفسها مدى جواز نشر المواد الإعلانية."
            }
          ]
        },
        {
          "title": "8. المراجعة وإزالة المحتوى",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز لـ CANNECT AI إجراء مراجعة آلية ويدوية وبالذكاء الاصطناعي وفحص للمواد الإعلانية."
            },
            {
              "type": "p",
              "text": "يجوز لـ CANNECT AI تقييد الوصول إلى الإعلان أو إزالة المواد الإعلانية أو تعليق النشر أو حظر حساب العميل أو رفض النشر. وتشمل الأسباب: مخالفة القانون؛ أمر من جهة حكومية؛ شكاوى المستخدمين؛ مؤشرات على محتوى غير قانوني؛ مخالفة القواعد الداخلية للمنصة."
            }
          ]
        },
        {
          "title": "9. تخزين المعلومات والتحليلات",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز لـ CANNECT AI تخزين المواد الإعلانية والسجلات والإحصاءات وبيانات نشر الإعلانات وسجل تغييرات الحملات الإعلانية. ويجوز الاحتفاظ بمعلومات الإعلان الإلكتروني لمدة سنة واحدة على الأقل بعد انتهاء النشر."
            },
            {
              "type": "p",
              "text": "يجوز لـ CANNECT AI استخدام التحليلات والإحصاءات مجهولة الهوية لتحسين المنصة ولأغراض التحليلات وتحسين الحملات الإعلانية وتطوير خوارزميات الذكاء الاصطناعي."
            }
          ]
        },
        {
          "title": "10. شكاوى المستخدمين",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز للمستخدمين تقديم شكاوى بشأن المواد الإعلانية. ويجوز لـ CANNECT AI إخطار العميل بالشكاوى، وطلب إجراء تغييرات على الإعلان أو إزالته، وتقييد توزيع المحتوى المتنازع عليه. ويجب على العميل الاستجابة الفورية لمتطلبات CANNECT AI."
            }
          ]
        },
        {
          "title": "11. التعامل مع الجهات الحكومية",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز لـ CANNECT AI الامتثال للمتطلبات الإلزامية للجهات الحكومية في جمهورية كازاخستان، بما في ذلك تقييد الوصول إلى المحتوى وإزالته ونقل المعلومات إلى الجهات المخوّلة وإنفاذ الأحكام والأوامر القضائية."
            }
          ]
        },
        {
          "title": "12. البيانات الشخصية",
          "blocks": [
            {
              "type": "p",
              "text": "تُعالَج البيانات الشخصية وفقًا لتشريعات جمهورية كازاخستان. ويضمن العميل مشروعية نقل البيانات الشخصية المستخدمة في الحملات الإعلانية. ويجوز لـ CANNECT AI استخدام ملفات تعريف الارتباط ومعرّف الجهاز والأدوات التحليلية والتقنية ضمن إطار تشريعات جمهورية كازاخستان."
            }
          ]
        },
        {
          "title": "13. الملكية الفكرية",
          "blocks": [
            {
              "type": "p",
              "text": "جميع الحقوق المتعلقة بمنصة CANNECT AI والبرمجيات وخوارزميات الذكاء الاصطناعي والواجهات والتصميم وأنظمة التحليلات تعود إلى CANNECT AI أو إلى أصحاب الحقوق المعنيين. ولا يمنح نشر الإعلان العميل أي حقوق في تقنيات CANNECT AI وبرمجياتها."
            }
          ]
        },
        {
          "title": "14. التعامل الإلكتروني",
          "blocks": [
            {
              "type": "p",
              "text": "يقرّ الطرفان بالقوة القانونية للطلبات الإلكترونية والمراسلات والإجراءات في الحساب الشخصي والسجلات الرقمية والتأكيدات الإلكترونية والمدفوعات عبر الإنترنت. وتُعدّ الأفعال الإلكترونية للعميل بمثابة التوقيع الخطي."
            }
          ]
        },
        {
          "title": "15. أحكام ختامية",
          "blocks": [
            {
              "type": "p",
              "text": "يخضع هذا العرض لتشريعات جمهورية كازاخستان. وتُحسم جميع النزاعات عبر التفاوض، وفي حال تعذّر الاتفاق، أمام المحكمة في مكان تسجيل CANNECT AI."
            },
            {
              "type": "p",
              "text": "يجوز لـ CANNECT AI تعديل شروط هذا العرض من جانب واحد بنشر نسخة جديدة على المنصة أو الموقع الإلكتروني. ويُعدّ استمرار استخدام المنصة بعد نشر نسخة جديدة موافقةً من العميل على التغييرات. ويُنشر الإصدار الحالي من العرض على الموقع الرسمي لـ CANNECT AI."
            }
          ]
        }
      ],
      "updatedValue": "14 مايو 2026 (بصيغته المعدّلة والمكمّلة حتى 16.07.2026)"
    }
  },
  "payment": {
    "en": {
      "kicker": "Legal",
      "title": "Payment Rules",
      "updatedLabel": "Version",
      "sections": [
        {
          "title": "1. General",
          "blocks": [
            {
              "type": "p",
              "text": "These Payment Rules define the terms for paying for services of the CANNect.kz platform."
            },
            {
              "type": "p",
              "text": "Payment for services means full and unconditional acceptance of the Public Offer terms."
            },
            {
              "type": "p",
              "text": "The agreement is deemed concluded upon receipt of funds in the Provider's settlement account or confirmation of payment by the payment system."
            }
          ]
        },
        {
          "title": "2. Tariffs",
          "blocks": [
            {
              "type": "p",
              "text": "Service prices are determined by the tariffs published on CANNect.kz."
            },
            {
              "type": "p",
              "text": "The Provider may change tariffs unilaterally."
            },
            {
              "type": "p",
              "text": "Tariff changes do not apply to a subscription period already paid for."
            }
          ]
        },
        {
          "title": "3. Payment methods",
          "blocks": [
            {
              "type": "p",
              "text": "The User may pay for services by:"
            },
            {
              "type": "list",
              "items": [
                "bank card",
                "bank transfer",
                "invoice",
                "payment services and acquiring providers connected to the platform"
              ]
            }
          ]
        },
        {
          "title": "4. Subscription",
          "blocks": [
            {
              "type": "p",
              "text": "Services are provided under a recurring subscription model."
            },
            {
              "type": "p",
              "text": "The following billing periods are available:"
            },
            {
              "type": "list",
              "items": [
                "monthly",
                "quarterly",
                "yearly",
                "other periods set by the tariffs"
              ]
            },
            {
              "type": "p",
              "text": "After payment the User receives access to platform functionality for the corresponding paid period."
            }
          ]
        },
        {
          "title": "5. Auto-renewal",
          "blocks": [
            {
              "type": "p",
              "text": "When the selected tariff provides for it, the subscription may renew automatically."
            },
            {
              "type": "p",
              "text": "The User may disable auto-renewal at any time before the next billing date."
            }
          ]
        },
        {
          "title": "6. Late payment",
          "blocks": [
            {
              "type": "p",
              "text": "If payment is not received, the Provider may:"
            },
            {
              "type": "list",
              "items": [
                "restrict access to the platform",
                "suspend the provision of services",
                "discontinue access to specific features",
                "delete the account after the data retention period expires"
              ]
            },
            {
              "type": "p",
              "text": "Access restrictions do not relieve the User from the obligation to pay for services already rendered."
            }
          ]
        },
        {
          "title": "7. Taxes and documents",
          "blocks": [
            {
              "type": "p",
              "text": "All prices are in Kazakhstani tenge (KZT)."
            },
            {
              "type": "p",
              "text": "Where applicable, prices include taxes in accordance with Kazakhstani legislation."
            },
            {
              "type": "p",
              "text": "Upon the User's request, the Provider issues closing documents electronically."
            }
          ]
        },
        {
          "title": "8. Proof of payment",
          "blocks": [
            {
              "type": "p",
              "text": "Proof of payment includes:"
            },
            {
              "type": "list",
              "items": [
                "electronic receipt",
                "bank statement",
                "payment order",
                "payment system confirmation"
              ]
            }
          ]
        },
        {
          "title": "9. Refunds",
          "blocks": [
            {
              "type": "p",
              "text": "Refunds are made in accordance with the CANNect.kz Refund Policy."
            },
            {
              "type": "p",
              "text": "Refunds are processed only after the User's corresponding application has been reviewed."
            }
          ]
        },
        {
          "title": "10. Final provisions",
          "blocks": [
            {
              "type": "p",
              "text": "All settlements between the parties are made in Kazakhstani tenge."
            },
            {
              "type": "p",
              "text": "In matters not regulated by these Payment Rules, the parties are guided by the legislation of the Republic of Kazakhstan."
            },
            {
              "type": "p",
              "text": "The current version of the document is published on CANNect.kz."
            }
          ]
        }
      ]
    },
    "ru": {
      "kicker": "Правовая информация",
      "title": "Порядок оплаты услуг",
      "updatedLabel": "Версия",
      "sections": [
        {
          "title": "1. Общие положения",
          "blocks": [
            {
              "type": "p",
              "text": "Настоящий Порядок оплаты определяет условия оплаты услуг сервиса CANNect.kz."
            },
            {
              "type": "p",
              "text": "Оплата услуг означает полное и безоговорочное согласие Пользователя с условиями Публичной оферты."
            },
            {
              "type": "p",
              "text": "Договор считается заключённым с момента поступления оплаты на расчётный счёт Исполнителя либо подтверждения платёжной системой факта оплаты."
            }
          ]
        },
        {
          "title": "2. Тарифы",
          "blocks": [
            {
              "type": "p",
              "text": "Стоимость услуг определяется тарифами, опубликованными на сайте CANNect.kz."
            },
            {
              "type": "p",
              "text": "Исполнитель вправе изменять тарифы в одностороннем порядке."
            },
            {
              "type": "p",
              "text": "Изменение тарифов не распространяется на уже оплаченный период подписки."
            }
          ]
        },
        {
          "title": "3. Способы оплаты",
          "blocks": [
            {
              "type": "p",
              "text": "Пользователь вправе оплатить услуги следующими способами:"
            },
            {
              "type": "list",
              "items": [
                "банковской картой",
                "банковским переводом",
                "по выставленному счёту",
                "через платёжные сервисы и эквайринг, подключённые к Сервису"
              ]
            }
          ]
        },
        {
          "title": "4. Подписка",
          "blocks": [
            {
              "type": "p",
              "text": "Услуги предоставляются по модели периодической подписки."
            },
            {
              "type": "p",
              "text": "Возможны следующие периоды оплаты:"
            },
            {
              "type": "list",
              "items": [
                "ежемесячно",
                "ежеквартально",
                "ежегодно",
                "иные периоды, предусмотренные тарифами"
              ]
            },
            {
              "type": "p",
              "text": "После оплаты Пользователь получает доступ к функционалу Сервиса на соответствующий оплаченный период."
            }
          ]
        },
        {
          "title": "5. Автоматическое продление",
          "blocks": [
            {
              "type": "p",
              "text": "Если предусмотрено выбранным тарифом, подписка может продлеваться автоматически."
            },
            {
              "type": "p",
              "text": "Пользователь вправе отключить автоматическое продление до даты очередного списания."
            }
          ]
        },
        {
          "title": "6. Просрочка оплаты",
          "blocks": [
            {
              "type": "p",
              "text": "При отсутствии оплаты Исполнитель вправе:"
            },
            {
              "type": "list",
              "items": [
                "ограничить доступ к Сервису",
                "приостановить оказание услуг",
                "прекратить доступ к отдельным функциям",
                "удалить учётную запись после истечения срока хранения данных"
              ]
            },
            {
              "type": "p",
              "text": "Ограничение доступа не освобождает Пользователя от исполнения обязательств по оплате ранее оказанных услуг."
            }
          ]
        },
        {
          "title": "7. Налоги и документы",
          "blocks": [
            {
              "type": "p",
              "text": "Все цены указываются в тенге Республики Казахстан."
            },
            {
              "type": "p",
              "text": "Если применимо, стоимость услуг включает налоги в соответствии с законодательством Республики Казахстан."
            },
            {
              "type": "p",
              "text": "По запросу Пользователя Исполнитель предоставляет закрывающие документы в электронном виде."
            }
          ]
        },
        {
          "title": "8. Подтверждение оплаты",
          "blocks": [
            {
              "type": "p",
              "text": "Подтверждением оплаты являются:"
            },
            {
              "type": "list",
              "items": [
                "электронная квитанция",
                "банковская выписка",
                "платёжное поручение",
                "подтверждение платёжной системы"
              ]
            }
          ]
        },
        {
          "title": "9. Возвраты",
          "blocks": [
            {
              "type": "p",
              "text": "Возврат денежных средств осуществляется в соответствии с Политикой возврата денежных средств CANNect.kz."
            },
            {
              "type": "p",
              "text": "Возврат производится только после рассмотрения соответствующего заявления Пользователя."
            }
          ]
        },
        {
          "title": "10. Заключительные положения",
          "blocks": [
            {
              "type": "p",
              "text": "Все расчёты между Сторонами осуществляются в тенге Республики Казахстан."
            },
            {
              "type": "p",
              "text": "Во всём, что не урегулировано настоящим Порядком оплаты, Стороны руководствуются законодательством Республики Казахстан."
            },
            {
              "type": "p",
              "text": "Актуальная редакция документа публикуется на сайте CANNect.kz."
            }
          ]
        }
      ]
    },
    "kk": {
      "kicker": "Құқықтық ақпарат",
      "title": "Қызметтерге төлем тәртібі",
      "updatedLabel": "Редакция",
      "sections": [
        {
          "title": "1. Жалпы ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Осы Төлем тәртібі CANNect.kz сервисі қызметтеріне төлем шарттарын айқындайды."
            },
            {
              "type": "p",
              "text": "Қызметтерге төлем Пайдаланушының Жария оферта шарттарымен толық және сөзсіз келісімін білдіреді."
            },
            {
              "type": "p",
              "text": "Шарт төлемнің Орындаушының есеп шотына түскен сәтінен немесе төлем жүйесінің төлем фактісін растаған сәтінен бастап жасалған болып саналады."
            }
          ]
        },
        {
          "title": "2. Тарифтер",
          "blocks": [
            {
              "type": "p",
              "text": "Қызметтер құны CANNect.kz сайтында жарияланған тарифтермен айқындалады."
            },
            {
              "type": "p",
              "text": "Орындаушы тарифтерді бір жақты тәртіппен өзгертуге құқылы."
            },
            {
              "type": "p",
              "text": "Тарифтердің өзгеруі төленген жазылым кезеңіне қолданылмайды."
            }
          ]
        },
        {
          "title": "3. Төлем тәсілдері",
          "blocks": [
            {
              "type": "p",
              "text": "Пайдаланушы қызметтерді келесі тәсілдермен төлеуге құқылы:"
            },
            {
              "type": "list",
              "items": [
                "банк картасымен",
                "банк аударымымен",
                "қойылған шот бойынша",
                "Сервиске қосылған төлем сервистері мен эквайринг арқылы"
              ]
            }
          ]
        },
        {
          "title": "4. Жазылым",
          "blocks": [
            {
              "type": "p",
              "text": "Қызметтер кезеңдік жазылым моделі бойынша ұсынылады."
            },
            {
              "type": "p",
              "text": "Төлемнің келесі кезеңдері мүмкін:"
            },
            {
              "type": "list",
              "items": [
                "ай сайын",
                "тоқсан сайын",
                "жыл сайын",
                "тарифтерде көзделген өзге кезеңдер"
              ]
            },
            {
              "type": "p",
              "text": "Төлемнен кейін Пайдаланушы тиісті төленген кезеңге Сервистің функционалына қол жеткізеді."
            }
          ]
        },
        {
          "title": "5. Автоматты түрде ұзарту",
          "blocks": [
            {
              "type": "p",
              "text": "Таңдалған тарифпен көзделген жағдайда жазылым автоматты түрде ұзартылуы мүмкін."
            },
            {
              "type": "p",
              "text": "Пайдаланушы кезекті есептен шығарудың алдында автоматты ұзартуды өшіруге құқылы."
            }
          ]
        },
        {
          "title": "6. Төлемнің мерзімі өтуі",
          "blocks": [
            {
              "type": "p",
              "text": "Төлем болмаған жағдайда Орындаушы құқылы:"
            },
            {
              "type": "list",
              "items": [
                "Сервиске қол жеткізуді шектеуге",
                "қызметтер көрсетуді тоқтата тұруға",
                "жекелеген функцияларға қол жеткізуді тоқтатуға",
                "деректерді сақтау мерзімі біткеннен кейін есептік жазбаны жоюға"
              ]
            },
            {
              "type": "p",
              "text": "Қол жеткізуді шектеу Пайдаланушыны бұрын көрсетілген қызметтерге төлем бойынша міндеттемелерден босатпайды."
            }
          ]
        },
        {
          "title": "7. Салықтар және құжаттар",
          "blocks": [
            {
              "type": "p",
              "text": "Барлық бағалар Қазақстан Республикасының теңгесінде көрсетіледі."
            },
            {
              "type": "p",
              "text": "Қолданылса, қызметтер құны Қазақстан Республикасының заңнамасына сәйкес салықтарды қамтиды."
            },
            {
              "type": "p",
              "text": "Пайдаланушының сұрауы бойынша Орындаушы жабылатын құжаттарды электрондық түрде ұсынады."
            }
          ]
        },
        {
          "title": "8. Төлемнің расталуы",
          "blocks": [
            {
              "type": "p",
              "text": "Төлемнің растамасы:"
            },
            {
              "type": "list",
              "items": [
                "электрондық түбіртек",
                "банк үзіндісі",
                "төлем тапсырмасы",
                "төлем жүйесінің растамасы"
              ]
            }
          ]
        },
        {
          "title": "9. Қайтарулар",
          "blocks": [
            {
              "type": "p",
              "text": "Ақшаны қайтару CANNect.kz Қайтару саясатына сәйкес жүзеге асырылады."
            },
            {
              "type": "p",
              "text": "Қайтару Пайдаланушының тиісті өтінішін қарағаннан кейін ғана жүргізіледі."
            }
          ]
        },
        {
          "title": "10. Қорытынды ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Тараптар арасындағы барлық есеп айырысулар Қазақстан Республикасының теңгесінде жүзеге асырылады."
            },
            {
              "type": "p",
              "text": "Осы Төлем тәртібімен реттелмегеннің бәрінде Тараптар Қазақстан Республикасының заңнамасын басшылыққа алады."
            },
            {
              "type": "p",
              "text": "Құжаттың өзекті редакциясы CANNect.kz сайтында жарияланады."
            }
          ]
        }
      ]
    },
    "tr": {
      "kicker": "Yasal",
      "title": "Ödeme Kuralları",
      "updatedLabel": "Sürüm",
      "sections": [
        {
          "title": "1. Genel",
          "blocks": [
            {
              "type": "p",
              "text": "Bu Ödeme Kuralları, CANNect.kz platformunun hizmetlerine ödeme yapma şartlarını tanımlar."
            },
            {
              "type": "p",
              "text": "Hizmetlere ödeme yapılması, Kamu Teklifi şartlarının tam ve koşulsuz kabulü anlamına gelir."
            },
            {
              "type": "p",
              "text": "Sözleşme, fonların Sağlayıcının takas hesabına ulaşması veya ödemenin ödeme sistemi tarafından teyit edilmesi üzerine akdedilmiş sayılır."
            }
          ]
        },
        {
          "title": "2. Tarifeler",
          "blocks": [
            {
              "type": "p",
              "text": "Hizmet fiyatları CANNect.kz üzerinde yayımlanan tarifelerle belirlenir."
            },
            {
              "type": "p",
              "text": "Sağlayıcı tarifeleri tek taraflı olarak değiştirebilir."
            },
            {
              "type": "p",
              "text": "Tarife değişiklikleri, halihazırda ödenmiş bir abonelik dönemine uygulanmaz."
            }
          ]
        },
        {
          "title": "3. Ödeme yöntemleri",
          "blocks": [
            {
              "type": "p",
              "text": "Kullanıcı hizmetlere şu yollarla ödeme yapabilir:"
            },
            {
              "type": "list",
              "items": [
                "banka kartı",
                "banka havalesi",
                "fatura",
                "platforma bağlı ödeme hizmetleri ve acquiring sağlayıcıları"
              ]
            }
          ]
        },
        {
          "title": "4. Abonelik",
          "blocks": [
            {
              "type": "p",
              "text": "Hizmetler, tekrarlayan abonelik modeli kapsamında sağlanır."
            },
            {
              "type": "p",
              "text": "Aşağıdaki faturalama dönemleri mevcuttur:"
            },
            {
              "type": "list",
              "items": [
                "aylık",
                "üç aylık",
                "yıllık",
                "tarifelerde belirlenen diğer dönemler"
              ]
            },
            {
              "type": "p",
              "text": "Ödemeden sonra Kullanıcı, ilgili ödenmiş döneme ait platform işlevselliğine erişim kazanır."
            }
          ]
        },
        {
          "title": "5. Otomatik yenileme",
          "blocks": [
            {
              "type": "p",
              "text": "Seçilen tarife öngördüğünde abonelik otomatik olarak yenilenebilir."
            },
            {
              "type": "p",
              "text": "Kullanıcı, bir sonraki faturalama tarihinden önce istediği zaman otomatik yenilemeyi devre dışı bırakabilir."
            }
          ]
        },
        {
          "title": "6. Gecikmiş ödeme",
          "blocks": [
            {
              "type": "p",
              "text": "Ödeme alınmaması halinde Sağlayıcı şunları yapabilir:"
            },
            {
              "type": "list",
              "items": [
                "platforma erişimi kısıtlamak",
                "hizmet sağlamayı askıya almak",
                "belirli özelliklere erişimi durdurmak",
                "veri saklama süresi dolduktan sonra hesabı silmek"
              ]
            },
            {
              "type": "p",
              "text": "Erişim kısıtlamaları, Kullanıcıyı halihazırda sunulmuş hizmetlerin ödemesini yapma yükümlülüğünden kurtarmaz."
            }
          ]
        },
        {
          "title": "7. Vergiler ve belgeler",
          "blocks": [
            {
              "type": "p",
              "text": "Tüm fiyatlar Kazakistan tengesi (KZT) cinsindendir."
            },
            {
              "type": "p",
              "text": "Uygulanabilir olduğu yerlerde fiyatlar Kazakistan mevzuatına uygun olarak vergileri içerir."
            },
            {
              "type": "p",
              "text": "Kullanıcının talebi üzerine Sağlayıcı kapanış belgelerini elektronik olarak düzenler."
            }
          ]
        },
        {
          "title": "8. Ödeme kanıtı",
          "blocks": [
            {
              "type": "p",
              "text": "Ödeme kanıtı şunları içerir:"
            },
            {
              "type": "list",
              "items": [
                "elektronik makbuz",
                "banka ekstresi",
                "ödeme talimatı",
                "ödeme sistemi teyidi"
              ]
            }
          ]
        },
        {
          "title": "9. İadeler",
          "blocks": [
            {
              "type": "p",
              "text": "İadeler CANNect.kz İade Politikası'na uygun olarak yapılır."
            },
            {
              "type": "p",
              "text": "İadeler yalnızca Kullanıcının ilgili başvurusu incelendikten sonra işleme alınır."
            }
          ]
        },
        {
          "title": "10. Nihai hükümler",
          "blocks": [
            {
              "type": "p",
              "text": "Taraflar arasındaki tüm hesaplaşmalar Kazakistan tengesi cinsinden yapılır."
            },
            {
              "type": "p",
              "text": "Bu Ödeme Kuralları ile düzenlenmeyen konularda taraflar Kazakistan Cumhuriyeti mevzuatına göre hareket eder."
            },
            {
              "type": "p",
              "text": "Belgenin güncel sürümü CANNect.kz üzerinde yayımlanır."
            }
          ]
        }
      ]
    },
    "zh": {
      "kicker": "法律信息",
      "title": "付款规则",
      "updatedLabel": "版本",
      "sections": [
        {
          "title": "1. 总则",
          "blocks": [
            {
              "type": "p",
              "text": "本付款规则规定了支付 CANNect.kz 平台服务费用的相关条款。"
            },
            {
              "type": "p",
              "text": "支付服务费用即表示完全且无条件地接受公开要约的各项条款。"
            },
            {
              "type": "p",
              "text": "自款项到达服务商的结算账户，或支付系统确认付款之时起，协议视为已订立。"
            }
          ]
        },
        {
          "title": "2. 资费",
          "blocks": [
            {
              "type": "p",
              "text": "服务价格按 CANNect.kz 上公布的资费确定。"
            },
            {
              "type": "p",
              "text": "服务商有权单方面变更资费。"
            },
            {
              "type": "p",
              "text": "资费变更不适用于已付费的订阅期。"
            }
          ]
        },
        {
          "title": "3. 付款方式",
          "blocks": [
            {
              "type": "p",
              "text": "用户可通过以下方式支付服务费用："
            },
            {
              "type": "list",
              "items": [
                "银行卡",
                "银行转账",
                "按开具的账单",
                "接入本平台的支付服务及收单机构"
              ]
            }
          ]
        },
        {
          "title": "4. 订阅",
          "blocks": [
            {
              "type": "p",
              "text": "服务以周期性订阅模式提供。"
            },
            {
              "type": "p",
              "text": "可选的计费周期如下："
            },
            {
              "type": "list",
              "items": [
                "按月",
                "按季度",
                "按年",
                "资费规定的其他周期"
              ]
            },
            {
              "type": "p",
              "text": "付款后，用户在相应的已付费期间内获得平台功能的使用权限。"
            }
          ]
        },
        {
          "title": "5. 自动续订",
          "blocks": [
            {
              "type": "p",
              "text": "若所选资费有此规定，订阅可自动续订。"
            },
            {
              "type": "p",
              "text": "用户可在下一计费日期之前随时关闭自动续订。"
            }
          ]
        },
        {
          "title": "6. 逾期付款",
          "blocks": [
            {
              "type": "p",
              "text": "如未收到付款，服务商有权："
            },
            {
              "type": "list",
              "items": [
                "限制对平台的访问",
                "暂停提供服务",
                "终止对特定功能的访问",
                "在数据保留期届满后删除账户"
              ]
            },
            {
              "type": "p",
              "text": "访问限制不免除用户对已提供服务的付款义务。"
            }
          ]
        },
        {
          "title": "7. 税费与单据",
          "blocks": [
            {
              "type": "p",
              "text": "所有价格均以哈萨克斯坦坚戈（KZT）计价。"
            },
            {
              "type": "p",
              "text": "如适用，价格已按哈萨克斯坦法律规定含税。"
            },
            {
              "type": "p",
              "text": "应用户要求，服务商以电子形式出具结算单据。"
            }
          ]
        },
        {
          "title": "8. 付款凭证",
          "blocks": [
            {
              "type": "p",
              "text": "付款凭证包括："
            },
            {
              "type": "list",
              "items": [
                "电子收据",
                "银行对账单",
                "付款指令",
                "支付系统确认"
              ]
            }
          ]
        },
        {
          "title": "9. 退款",
          "blocks": [
            {
              "type": "p",
              "text": "退款按 CANNect.kz 退款政策办理。"
            },
            {
              "type": "p",
              "text": "退款仅在审核用户的相应申请后办理。"
            }
          ]
        },
        {
          "title": "10. 最终条款",
          "blocks": [
            {
              "type": "p",
              "text": "双方之间的所有结算均以哈萨克斯坦坚戈进行。"
            },
            {
              "type": "p",
              "text": "对于本付款规则未予规定的事项，双方应遵循哈萨克斯坦共和国的法律。"
            },
            {
              "type": "p",
              "text": "文件的现行版本发布于 CANNect.kz。"
            }
          ]
        }
      ]
    },
    "ar": {
      "kicker": "قانوني",
      "title": "قواعد الدفع",
      "updatedLabel": "الإصدار",
      "sections": [
        {
          "title": "1. عام",
          "blocks": [
            {
              "type": "p",
              "text": "تحدّد قواعد الدفع هذه شروط دفع مقابل خدمات منصة CANNect.kz."
            },
            {
              "type": "p",
              "text": "يعني دفع مقابل الخدمات قبولًا كاملًا وغير مشروط لشروط العرض العام."
            },
            {
              "type": "p",
              "text": "يُعدّ الاتفاق مُبرمًا عند استلام الأموال في حساب التسوية الخاص بمزوّد الخدمة أو عند تأكيد الدفع من قِبل نظام الدفع."
            }
          ]
        },
        {
          "title": "2. التعرفات",
          "blocks": [
            {
              "type": "p",
              "text": "تُحدَّد أسعار الخدمات وفقًا للتعرفات المنشورة على CANNect.kz."
            },
            {
              "type": "p",
              "text": "يجوز لمزوّد الخدمة تغيير التعرفات من جانب واحد."
            },
            {
              "type": "p",
              "text": "لا تنطبق تغييرات التعرفات على فترة اشتراك تم دفعها بالفعل."
            }
          ]
        },
        {
          "title": "3. طرق الدفع",
          "blocks": [
            {
              "type": "p",
              "text": "يجوز للمستخدم الدفع مقابل الخدمات عن طريق:"
            },
            {
              "type": "list",
              "items": [
                "بطاقة مصرفية",
                "تحويل مصرفي",
                "فاتورة",
                "خدمات الدفع ومزوّدي الاستحواذ المتصلين بالمنصة"
              ]
            }
          ]
        },
        {
          "title": "4. الاشتراك",
          "blocks": [
            {
              "type": "p",
              "text": "تُقدَّم الخدمات وفق نموذج اشتراك متكرر."
            },
            {
              "type": "p",
              "text": "تتوفر فترات الفوترة التالية:"
            },
            {
              "type": "list",
              "items": [
                "شهري",
                "ربع سنوي",
                "سنوي",
                "فترات أخرى تحددها التعرفات"
              ]
            },
            {
              "type": "p",
              "text": "بعد الدفع، يحصل المستخدم على إمكانية الوصول إلى وظائف المنصة طوال الفترة المدفوعة المقابلة."
            }
          ]
        },
        {
          "title": "5. التجديد التلقائي",
          "blocks": [
            {
              "type": "p",
              "text": "عندما تنص التعرفة المختارة على ذلك، قد يتجدّد الاشتراك تلقائيًا."
            },
            {
              "type": "p",
              "text": "يجوز للمستخدم تعطيل التجديد التلقائي في أي وقت قبل تاريخ الفوترة التالي."
            }
          ]
        },
        {
          "title": "6. التأخر في الدفع",
          "blocks": [
            {
              "type": "p",
              "text": "في حال عدم استلام الدفع، يجوز لمزوّد الخدمة:"
            },
            {
              "type": "list",
              "items": [
                "تقييد الوصول إلى المنصة",
                "تعليق تقديم الخدمات",
                "إيقاف الوصول إلى ميزات محددة",
                "حذف الحساب بعد انتهاء فترة الاحتفاظ بالبيانات"
              ]
            },
            {
              "type": "p",
              "text": "لا تُعفي قيود الوصول المستخدم من الالتزام بدفع مقابل الخدمات التي تم تقديمها بالفعل."
            }
          ]
        },
        {
          "title": "7. الضرائب والمستندات",
          "blocks": [
            {
              "type": "p",
              "text": "جميع الأسعار بالتنغي الكازاخستاني (KZT)."
            },
            {
              "type": "p",
              "text": "عند الاقتضاء، تشمل الأسعار الضرائب وفقًا للتشريعات الكازاخستانية."
            },
            {
              "type": "p",
              "text": "بناءً على طلب المستخدم، يُصدر مزوّد الخدمة المستندات الختامية إلكترونيًا."
            }
          ]
        },
        {
          "title": "8. إثبات الدفع",
          "blocks": [
            {
              "type": "p",
              "text": "يشمل إثبات الدفع:"
            },
            {
              "type": "list",
              "items": [
                "إيصال إلكتروني",
                "كشف حساب مصرفي",
                "أمر دفع",
                "تأكيد من نظام الدفع"
              ]
            }
          ]
        },
        {
          "title": "9. الاسترداد",
          "blocks": [
            {
              "type": "p",
              "text": "يتم الاسترداد وفقًا لسياسة الاسترداد الخاصة بـ CANNect.kz."
            },
            {
              "type": "p",
              "text": "لا يُعالَج الاسترداد إلا بعد مراجعة طلب المستخدم المقابل."
            }
          ]
        },
        {
          "title": "10. أحكام ختامية",
          "blocks": [
            {
              "type": "p",
              "text": "تُجرى جميع التسويات بين الطرفين بالتنغي الكازاخستاني."
            },
            {
              "type": "p",
              "text": "في المسائل التي لا تنظّمها قواعد الدفع هذه، يسترشد الطرفان بتشريعات جمهورية كازاخستان."
            },
            {
              "type": "p",
              "text": "يُنشر الإصدار الحالي من المستند على CANNect.kz."
            }
          ]
        }
      ]
    }
  },
  "refund": {
    "en": {
      "kicker": "Legal",
      "title": "Refund Policy",
      "updatedLabel": "Version",
      "sections": [
        {
          "title": "1. General",
          "blocks": [
            {
              "type": "p",
              "text": "This Refund Policy governs the procedure for refunding funds to users of the CANNect.kz service (\"Service\")."
            },
            {
              "type": "p",
              "text": "The Policy is an integral part of the Public Offer and applies to all Users of the Service."
            },
            {
              "type": "p",
              "text": "The Service provides software under a SaaS (Software as a Service) model over the Internet."
            },
            {
              "type": "p",
              "text": "Users of the Service are individual entrepreneurs and legal entities."
            }
          ]
        },
        {
          "title": "2. General refund rules",
          "blocks": [
            {
              "type": "p",
              "text": "Subscription payment grants the User the right to access the functionality of the Service for the paid period."
            },
            {
              "type": "p",
              "text": "Once access to the functionality has been granted, the service is deemed to have started."
            },
            {
              "type": "p",
              "text": "Funds for services already rendered are non-refundable."
            },
            {
              "type": "p",
              "text": "The User may withdraw from further use of the Service at any time by sending written notice to the Provider's e-mail address."
            },
            {
              "type": "p",
              "text": "When the User withdraws from the Service, a refund is issued for the unused subscription period, less:"
            },
            {
              "type": "list",
              "items": [
                "the cost of services actually rendered",
                "bank fees",
                "payment-system fees",
                "other documented costs associated with rendering the services"
              ]
            }
          ]
        },
        {
          "title": "3. Cases of full refund",
          "blocks": [
            {
              "type": "p",
              "text": "A full refund is issued in the following cases:"
            },
            {
              "type": "list",
              "items": [
                "inability to provide access to the Service through the Provider's fault",
                "duplicate charge",
                "technical error of the payment system confirmed by the Provider",
                "termination of services on the Provider's initiative"
              ]
            }
          ]
        },
        {
          "title": "4. Cases of partial refund",
          "blocks": [
            {
              "type": "p",
              "text": "If the User withdraws from using the Service after the start of the paid period, a refund is issued in proportion to the unused subscription period."
            },
            {
              "type": "p",
              "text": "Example: subscription paid for 12 months; 3 months used; the refund is calculated for the remaining 9 months."
            },
            {
              "type": "p",
              "text": "The minimum settlement period is one calendar month."
            }
          ]
        },
        {
          "title": "5. Cases where no refund is issued",
          "blocks": [
            {
              "type": "p",
              "text": "No refund is issued if:"
            },
            {
              "type": "list",
              "items": [
                "the User has actually used the Service during the paid period",
                "the User has violated the terms of the Offer",
                "access has been restricted due to a violation of Kazakhstani law",
                "the User has refused to use the Service without terminating the contract",
                "the lack of result from using the Service is due to the User's actions or inaction"
              ]
            }
          ]
        },
        {
          "title": "6. Refund request procedure",
          "blocks": [
            {
              "type": "p",
              "text": "To obtain a refund, the User sends a request to the Provider's e-mail address."
            },
            {
              "type": "p",
              "text": "The request must specify:"
            },
            {
              "type": "list",
              "items": [
                "organization or sole-proprietor name",
                "BIN/IIN",
                "contact details",
                "payment date",
                "payment amount",
                "grounds for refund",
                "bank details for the refund"
              ]
            }
          ]
        },
        {
          "title": "7. Refund timeline",
          "blocks": [
            {
              "type": "p",
              "text": "Requests are reviewed within 10 (ten) business days."
            },
            {
              "type": "p",
              "text": "If approved, the refund is issued within 15 (fifteen) business days to the bank account used for the original payment, or to another account specified by the User."
            }
          ]
        },
        {
          "title": "8. Final provisions",
          "blocks": [
            {
              "type": "p",
              "text": "Refunds are made in Kazakhstani tenge."
            },
            {
              "type": "p",
              "text": "All disputes related to refunds are resolved through negotiations."
            },
            {
              "type": "p",
              "text": "If no agreement is reached, the dispute is subject to consideration by the courts of the Republic of Kazakhstan at the Provider's place of registration."
            }
          ]
        }
      ]
    },
    "ru": {
      "kicker": "Правовая информация",
      "title": "Политика возврата денежных средств",
      "updatedLabel": "Версия",
      "sections": [
        {
          "title": "1. Общие положения",
          "blocks": [
            {
              "type": "p",
              "text": "Настоящая Политика возврата денежных средств регулирует порядок возврата денежных средств Пользователям сервиса CANNect.kz (далее — «Сервис»)."
            },
            {
              "type": "p",
              "text": "Политика является неотъемлемой частью Публичной оферты и применяется ко всем Пользователям, использующим Сервис."
            },
            {
              "type": "p",
              "text": "Сервис предоставляет программное обеспечение по модели SaaS (Software as a Service) посредством сети Интернет."
            },
            {
              "type": "p",
              "text": "Пользователями Сервиса являются индивидуальные предприниматели и юридические лица."
            }
          ]
        },
        {
          "title": "2. Общие правила возврата",
          "blocks": [
            {
              "type": "p",
              "text": "Оплата подписки предоставляет Пользователю право доступа к функционалу Сервиса на оплаченный период."
            },
            {
              "type": "p",
              "text": "После предоставления доступа к функционалу Сервиса услуга считается начавшей оказываться."
            },
            {
              "type": "p",
              "text": "Денежные средства за уже оказанные услуги возврату не подлежат."
            },
            {
              "type": "p",
              "text": "Пользователь вправе отказаться от дальнейшего использования Сервиса в любое время путём направления письменного уведомления на электронную почту Исполнителя."
            },
            {
              "type": "p",
              "text": "При отказе Пользователя от Сервиса возврат осуществляется за неиспользованный период подписки за вычетом:"
            },
            {
              "type": "list",
              "items": [
                "стоимости фактически оказанных услуг",
                "банковских комиссий",
                "комиссий платёжных систем",
                "иных документально подтверждённых расходов, связанных с оказанием услуг"
              ]
            }
          ]
        },
        {
          "title": "3. Случаи полного возврата",
          "blocks": [
            {
              "type": "p",
              "text": "Полный возврат денежных средств осуществляется в следующих случаях:"
            },
            {
              "type": "list",
              "items": [
                "невозможность предоставления доступа к Сервису по вине Исполнителя",
                "двойное списание денежных средств",
                "техническая ошибка платёжной системы, подтверждённая Исполнителем",
                "прекращение оказания услуг по инициативе Исполнителя"
              ]
            }
          ]
        },
        {
          "title": "4. Случаи частичного возврата",
          "blocks": [
            {
              "type": "p",
              "text": "Если Пользователь отказался от использования Сервиса после начала оплаченного периода, возврат производится пропорционально неиспользованному периоду подписки."
            },
            {
              "type": "p",
              "text": "Пример: подписка оплачена на 12 месяцев; использовано 3 месяца; возврат рассчитывается за оставшиеся 9 месяцев."
            },
            {
              "type": "p",
              "text": "Минимальный расчётный период составляет один календарный месяц."
            }
          ]
        },
        {
          "title": "5. Случаи, когда возврат не производится",
          "blocks": [
            {
              "type": "p",
              "text": "Возврат денежных средств не осуществляется, если:"
            },
            {
              "type": "list",
              "items": [
                "Пользователь фактически использовал Сервис в течение оплаченного периода",
                "Пользователь нарушил условия Оферты",
                "доступ был ограничен вследствие нарушения законодательства Республики Казахстан",
                "Пользователь самостоятельно отказался от использования Сервиса без расторжения договора",
                "отсутствие результата использования Сервиса связано с действиями или бездействием Пользователя"
              ]
            }
          ]
        },
        {
          "title": "6. Порядок подачи заявления на возврат",
          "blocks": [
            {
              "type": "p",
              "text": "Для возврата Пользователь направляет заявление на электронную почту Исполнителя."
            },
            {
              "type": "p",
              "text": "В заявлении необходимо указать:"
            },
            {
              "type": "list",
              "items": [
                "наименование организации или ИП",
                "БИН/ИИН",
                "контактные данные",
                "дату платежа",
                "сумму платежа",
                "основание возврата",
                "банковские реквизиты для возврата"
              ]
            }
          ]
        },
        {
          "title": "7. Сроки возврата",
          "blocks": [
            {
              "type": "p",
              "text": "Заявление рассматривается в течение 10 (десяти) рабочих дней."
            },
            {
              "type": "p",
              "text": "В случае принятия положительного решения возврат осуществляется в течение 15 (пятнадцати) рабочих дней на банковский счёт, с которого была произведена оплата, либо на иной счёт, указанный Пользователем."
            }
          ]
        },
        {
          "title": "8. Заключительные положения",
          "blocks": [
            {
              "type": "p",
              "text": "Возврат денежных средств осуществляется в тенге Республики Казахстан."
            },
            {
              "type": "p",
              "text": "Все споры, связанные с возвратом денежных средств, разрешаются путём переговоров."
            },
            {
              "type": "p",
              "text": "При невозможности достижения соглашения спор подлежит рассмотрению в судебных органах Республики Казахстан по месту регистрации Исполнителя."
            }
          ]
        }
      ]
    },
    "kk": {
      "kicker": "Құқықтық ақпарат",
      "title": "Ақшаны қайтару саясаты",
      "updatedLabel": "Редакция",
      "sections": [
        {
          "title": "1. Жалпы ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Осы Ақшаны қайтару саясаты CANNect.kz сервисі (бұдан әрі — «Сервис») Пайдаланушыларына ақшаны қайтару тәртібін реттейді."
            },
            {
              "type": "p",
              "text": "Саясат Жария офертаның ажырамас бөлігі болып табылады және Сервисті пайдаланатын барлық Пайдаланушыларға қолданылады."
            },
            {
              "type": "p",
              "text": "Сервис бағдарламалық жасақтаманы Интернет желісі арқылы SaaS (Software as a Service) моделі бойынша ұсынады."
            },
            {
              "type": "p",
              "text": "Сервис Пайдаланушылары — жеке кәсіпкерлер және заңды тұлғалар."
            }
          ]
        },
        {
          "title": "2. Қайтарудың жалпы ережелері",
          "blocks": [
            {
              "type": "p",
              "text": "Жазылымға төлем Пайдаланушыға төленген кезеңде Сервистің функционалына қол жеткізу құқығын береді."
            },
            {
              "type": "p",
              "text": "Сервистің функционалына қол жеткізу берілгеннен кейін қызмет көрсетіле бастаған болып саналады."
            },
            {
              "type": "p",
              "text": "Көрсетілген қызметтер үшін ақша қайтарылмайды."
            },
            {
              "type": "p",
              "text": "Пайдаланушы Орындаушының электрондық поштасына жазбаша хабарлама жіберу арқылы кез келген уақытта Сервисті ары қарай пайдаланудан бас тартуға құқылы."
            },
            {
              "type": "p",
              "text": "Пайдаланушы Сервистен бас тартқан кезде қайтару пайдаланылмаған жазылым кезеңі үшін мынадай шегеріммен жүзеге асырылады:"
            },
            {
              "type": "list",
              "items": [
                "іс жүзінде көрсетілген қызметтердің құны",
                "банк комиссиялары",
                "төлем жүйелерінің комиссиялары",
                "қызметтер көрсетуге байланысты өзге құжатпен расталған шығындар"
              ]
            }
          ]
        },
        {
          "title": "3. Толық қайтару жағдайлары",
          "blocks": [
            {
              "type": "p",
              "text": "Ақшаны толық қайтару мынадай жағдайларда жүзеге асырылады:"
            },
            {
              "type": "list",
              "items": [
                "Орындаушының кінәсінен Сервиске қол жеткізу мүмкін болмаған кезде",
                "ақшаның қос есептен шығарылуы",
                "Орындаушы растаған төлем жүйесінің техникалық қатесі",
                "қызметтердің Орындаушы бастамасымен тоқтатылуы"
              ]
            }
          ]
        },
        {
          "title": "4. Ішінара қайтару жағдайлары",
          "blocks": [
            {
              "type": "p",
              "text": "Егер Пайдаланушы төленген кезең басталғаннан кейін Сервисті пайдаланудан бас тартса, қайтару пайдаланылмаған жазылым кезеңіне барабар жүзеге асырылады."
            },
            {
              "type": "p",
              "text": "Мысал: жазылым 12 айға төленген; 3 ай пайдаланылған; қайтару қалған 9 айға есептеледі."
            },
            {
              "type": "p",
              "text": "Ең төменгі есеп айырысу кезеңі — бір күнтізбелік ай."
            }
          ]
        },
        {
          "title": "5. Қайтару жүргізілмейтін жағдайлар",
          "blocks": [
            {
              "type": "p",
              "text": "Ақша қайтарылмайды, егер:"
            },
            {
              "type": "list",
              "items": [
                "Пайдаланушы төленген кезеңде Сервисті іс жүзінде пайдаланса",
                "Пайдаланушы Оферта шарттарын бұзса",
                "Қазақстан Республикасы заңнамасын бұзу салдарынан қол жеткізу шектелсе",
                "Пайдаланушы шартты бұзбай Сервисті пайдаланудан өзі бас тартса",
                "Сервисті пайдаланудан нәтиже болмауы Пайдаланушының әрекетімен немесе әрекетсіздігімен байланысты болса"
              ]
            }
          ]
        },
        {
          "title": "6. Қайтару туралы өтініш беру тәртібі",
          "blocks": [
            {
              "type": "p",
              "text": "Қайтару үшін Пайдаланушы Орындаушының электрондық поштасына өтініш жібереді."
            },
            {
              "type": "p",
              "text": "Өтініште көрсету керек:"
            },
            {
              "type": "list",
              "items": [
                "ұйымның немесе ЖК атауы",
                "БСН/ЖСН",
                "байланыс деректері",
                "төлем күні",
                "төлем сомасы",
                "қайтару негізі",
                "қайтарудың банктік деректемелері"
              ]
            }
          ]
        },
        {
          "title": "7. Қайтару мерзімдері",
          "blocks": [
            {
              "type": "p",
              "text": "Өтініш 10 (он) жұмыс күні ішінде қаралады."
            },
            {
              "type": "p",
              "text": "Оң шешім қабылданған жағдайда қайтару 15 (он бес) жұмыс күні ішінде төлем жасалған банк шотына немесе Пайдаланушы көрсеткен өзге шотқа жүзеге асырылады."
            }
          ]
        },
        {
          "title": "8. Қорытынды ережелер",
          "blocks": [
            {
              "type": "p",
              "text": "Ақшаны қайтару Қазақстан Республикасының теңгесінде жүзеге асырылады."
            },
            {
              "type": "p",
              "text": "Ақшаны қайтаруға байланысты барлық даулар келіссөздер арқылы шешіледі."
            },
            {
              "type": "p",
              "text": "Келісімге қол жеткізілмеген жағдайда дау Орындаушының тіркелген орны бойынша Қазақстан Республикасының сот органдарында қаралуға жатады."
            }
          ]
        }
      ]
    },
    "tr": {
      "kicker": "Yasal",
      "title": "İade Politikası",
      "updatedLabel": "Sürüm",
      "sections": [
        {
          "title": "1. Genel",
          "blocks": [
            {
              "type": "p",
              "text": "Bu İade Politikası, CANNect.kz hizmetinin (“Hizmet”) kullanıcılarına fon iadesi prosedürünü düzenler."
            },
            {
              "type": "p",
              "text": "Politika, Kamu Teklifi'nin ayrılmaz bir parçasıdır ve Hizmetin tüm Kullanıcılarına uygulanır."
            },
            {
              "type": "p",
              "text": "Hizmet, yazılımı İnternet üzerinden SaaS (Software as a Service) modeliyle sağlar."
            },
            {
              "type": "p",
              "text": "Hizmetin Kullanıcıları, bireysel girişimciler ve tüzel kişilerdir."
            }
          ]
        },
        {
          "title": "2. Genel iade kuralları",
          "blocks": [
            {
              "type": "p",
              "text": "Abonelik ödemesi, Kullanıcıya ödenen dönem boyunca Hizmetin işlevselliğine erişim hakkı verir."
            },
            {
              "type": "p",
              "text": "İşlevselliğe erişim sağlandıktan sonra hizmet başlamış sayılır."
            },
            {
              "type": "p",
              "text": "Halihazırda sunulmuş hizmetler için ödenen fonlar iade edilemez."
            },
            {
              "type": "p",
              "text": "Kullanıcı, Sağlayıcının e-posta adresine yazılı bildirim göndererek istediği zaman Hizmeti daha fazla kullanmaktan vazgeçebilir."
            },
            {
              "type": "p",
              "text": "Kullanıcı Hizmetten vazgeçtiğinde, kullanılmayan abonelik dönemi için aşağıdakiler düşülerek iade yapılır:"
            },
            {
              "type": "list",
              "items": [
                "fiilen sunulan hizmetlerin maliyeti",
                "banka ücretleri",
                "ödeme sistemi ücretleri",
                "hizmetlerin sunumuyla ilişkili diğer belgelenmiş maliyetler"
              ]
            }
          ]
        },
        {
          "title": "3. Tam iade halleri",
          "blocks": [
            {
              "type": "p",
              "text": "Aşağıdaki hallerde tam iade yapılır:"
            },
            {
              "type": "list",
              "items": [
                "Sağlayıcının kusuru nedeniyle Hizmete erişim sağlanamaması",
                "mükerrer tahsilat",
                "Sağlayıcı tarafından teyit edilen ödeme sistemi teknik hatası",
                "hizmetlerin Sağlayıcının inisiyatifiyle sonlandırılması"
              ]
            }
          ]
        },
        {
          "title": "4. Kısmi iade halleri",
          "blocks": [
            {
              "type": "p",
              "text": "Kullanıcı, ödenen dönemin başlangıcından sonra Hizmeti kullanmaktan vazgeçerse, iade kullanılmayan abonelik dönemiyle orantılı olarak yapılır."
            },
            {
              "type": "p",
              "text": "Örnek: abonelik 12 ay için ödenmiş; 3 ay kullanılmış; iade kalan 9 ay için hesaplanır."
            },
            {
              "type": "p",
              "text": "Asgari hesaplama dönemi bir takvim ayıdır."
            }
          ]
        },
        {
          "title": "5. İade yapılmayan haller",
          "blocks": [
            {
              "type": "p",
              "text": "Aşağıdaki durumlarda iade yapılmaz:"
            },
            {
              "type": "list",
              "items": [
                "Kullanıcının ödenen dönem boyunca Hizmeti fiilen kullanmış olması",
                "Kullanıcının Teklif şartlarını ihlal etmiş olması",
                "erişimin Kazakistan yasalarının ihlali nedeniyle kısıtlanmış olması",
                "Kullanıcının sözleşmeyi feshetmeden Hizmeti kullanmaktan vazgeçmiş olması",
                "Hizmetin kullanımından sonuç alınamamasının Kullanıcının eylem veya eylemsizliğinden kaynaklanması"
              ]
            }
          ]
        },
        {
          "title": "6. İade talep prosedürü",
          "blocks": [
            {
              "type": "p",
              "text": "İade almak için Kullanıcı, Sağlayıcının e-posta adresine bir talep gönderir."
            },
            {
              "type": "p",
              "text": "Talepte şunlar belirtilmelidir:"
            },
            {
              "type": "list",
              "items": [
                "kuruluş veya bireysel girişimci adı",
                "BIN/IIN",
                "iletişim bilgileri",
                "ödeme tarihi",
                "ödeme tutarı",
                "iade gerekçesi",
                "iade için banka bilgileri"
              ]
            }
          ]
        },
        {
          "title": "7. İade süresi",
          "blocks": [
            {
              "type": "p",
              "text": "Talepler 10 (on) iş günü içinde incelenir."
            },
            {
              "type": "p",
              "text": "Onaylanması halinde iade, ilk ödemenin yapıldığı banka hesabına veya Kullanıcının belirttiği başka bir hesaba 15 (on beş) iş günü içinde yapılır."
            }
          ]
        },
        {
          "title": "8. Nihai hükümler",
          "blocks": [
            {
              "type": "p",
              "text": "İadeler Kazakistan tengesi cinsinden yapılır."
            },
            {
              "type": "p",
              "text": "İadelerle ilgili tüm uyuşmazlıklar müzakere yoluyla çözülür."
            },
            {
              "type": "p",
              "text": "Anlaşmaya varılamaması halinde uyuşmazlık, Sağlayıcının kayıtlı olduğu yerdeki Kazakistan Cumhuriyeti mahkemeleri tarafından incelenir."
            }
          ]
        }
      ]
    },
    "zh": {
      "kicker": "法律信息",
      "title": "退款政策",
      "updatedLabel": "版本",
      "sections": [
        {
          "title": "1. 总则",
          "blocks": [
            {
              "type": "p",
              "text": "本退款政策规范向 CANNect.kz 服务（以下简称“服务”）用户退还款项的程序。"
            },
            {
              "type": "p",
              "text": "本政策是公开要约不可分割的组成部分，适用于服务的所有用户。"
            },
            {
              "type": "p",
              "text": "本服务通过互联网以 SaaS（软件即服务）模式提供软件。"
            },
            {
              "type": "p",
              "text": "本服务的用户为个体工商户和法人实体。"
            }
          ]
        },
        {
          "title": "2. 一般退款规则",
          "blocks": [
            {
              "type": "p",
              "text": "支付订阅费用使用户获得在已付费期间内访问服务功能的权利。"
            },
            {
              "type": "p",
              "text": "一旦授予功能访问权限，即视为服务已开始提供。"
            },
            {
              "type": "p",
              "text": "已提供服务所对应的款项不予退还。"
            },
            {
              "type": "p",
              "text": "用户有权随时通过向服务提供方的电子邮箱发送书面通知，放弃继续使用本服务。"
            },
            {
              "type": "p",
              "text": "用户放弃使用本服务时，按未使用的订阅期间办理退款，并扣除以下费用："
            },
            {
              "type": "list",
              "items": [
                "实际已提供服务的费用",
                "银行手续费",
                "支付系统手续费",
                "与提供服务相关的其他有据可查的费用"
              ]
            }
          ]
        },
        {
          "title": "3. 全额退款的情形",
          "blocks": [
            {
              "type": "p",
              "text": "在以下情形下办理全额退款："
            },
            {
              "type": "list",
              "items": [
                "因服务提供方的过错而无法提供服务访问权限",
                "重复扣款",
                "经服务提供方确认的支付系统技术错误",
                "服务提供方主动终止提供服务"
              ]
            }
          ]
        },
        {
          "title": "4. 部分退款的情形",
          "blocks": [
            {
              "type": "p",
              "text": "如果用户在已付费期间开始后放弃使用本服务，则按未使用的订阅期间按比例办理退款。"
            },
            {
              "type": "p",
              "text": "示例：订阅已支付 12 个月；已使用 3 个月；退款按剩余 9 个月计算。"
            },
            {
              "type": "p",
              "text": "最低结算周期为一个日历月。"
            }
          ]
        },
        {
          "title": "5. 不予退款的情形",
          "blocks": [
            {
              "type": "p",
              "text": "在以下情况下不予退款："
            },
            {
              "type": "list",
              "items": [
                "用户在已付费期间内实际使用了本服务",
                "用户违反了要约条款",
                "因违反哈萨克斯坦法律而限制了访问权限",
                "用户在未解除合同的情况下自行放弃使用本服务",
                "使用本服务未取得成效系因用户的作为或不作为所致"
              ]
            }
          ]
        },
        {
          "title": "6. 退款申请程序",
          "blocks": [
            {
              "type": "p",
              "text": "为办理退款，用户应向服务提供方的电子邮箱发送申请。"
            },
            {
              "type": "p",
              "text": "申请中须注明："
            },
            {
              "type": "list",
              "items": [
                "组织或个体工商户名称",
                "BIN/IIN（企业/个人识别号）",
                "联系方式",
                "付款日期",
                "付款金额",
                "退款理由",
                "用于退款的银行账户信息"
              ]
            }
          ]
        },
        {
          "title": "7. 退款期限",
          "blocks": [
            {
              "type": "p",
              "text": "申请将在 10 个（十个）工作日内予以审核。"
            },
            {
              "type": "p",
              "text": "如经批准，退款将在 15 个（十五个）工作日内退至原付款所使用的银行账户，或退至用户指定的其他账户。"
            }
          ]
        },
        {
          "title": "8. 最终条款",
          "blocks": [
            {
              "type": "p",
              "text": "退款以哈萨克斯坦坚戈办理。"
            },
            {
              "type": "p",
              "text": "所有与退款相关的争议均通过协商解决。"
            },
            {
              "type": "p",
              "text": "若无法达成协议，争议应提交服务提供方注册地所在的哈萨克斯坦共和国法院审理。"
            }
          ]
        }
      ]
    },
    "ar": {
      "kicker": "قانوني",
      "title": "سياسة الاسترداد",
      "updatedLabel": "الإصدار",
      "sections": [
        {
          "title": "1. عام",
          "blocks": [
            {
              "type": "p",
              "text": "تنظّم سياسة الاسترداد هذه إجراءات رد الأموال لمستخدمي خدمة CANNect.kz (\"الخدمة\")."
            },
            {
              "type": "p",
              "text": "تُعدّ السياسة جزءًا لا يتجزأ من العرض العام وتنطبق على جميع مستخدمي الخدمة."
            },
            {
              "type": "p",
              "text": "تقدّم الخدمة برمجيات وفق نموذج SaaS (البرمجيات كخدمة) عبر الإنترنت."
            },
            {
              "type": "p",
              "text": "مستخدمو الخدمة هم رواد الأعمال الأفراد والكيانات القانونية."
            }
          ]
        },
        {
          "title": "2. قواعد الاسترداد العامة",
          "blocks": [
            {
              "type": "p",
              "text": "يمنح دفع الاشتراك المستخدم حق الوصول إلى وظائف الخدمة طوال الفترة المدفوعة."
            },
            {
              "type": "p",
              "text": "بمجرد منح الوصول إلى الوظائف، تُعدّ الخدمة قد بدأت."
            },
            {
              "type": "p",
              "text": "الأموال المقابلة للخدمات التي تم تقديمها بالفعل غير قابلة للاسترداد."
            },
            {
              "type": "p",
              "text": "يجوز للمستخدم العدول عن مواصلة استخدام الخدمة في أي وقت بإرسال إشعار خطي إلى عنوان البريد الإلكتروني لمزوّد الخدمة."
            },
            {
              "type": "p",
              "text": "عند عدول المستخدم عن الخدمة، يُرد المبلغ عن فترة الاشتراك غير المستخدمة، مخصومًا منه:"
            },
            {
              "type": "list",
              "items": [
                "تكلفة الخدمات التي تم تقديمها فعليًا",
                "الرسوم المصرفية",
                "رسوم نظام الدفع",
                "التكاليف الموثّقة الأخرى المرتبطة بتقديم الخدمات"
              ]
            }
          ]
        },
        {
          "title": "3. حالات الاسترداد الكامل",
          "blocks": [
            {
              "type": "p",
              "text": "يُرد المبلغ بالكامل في الحالات التالية:"
            },
            {
              "type": "list",
              "items": [
                "تعذّر توفير الوصول إلى الخدمة بسبب خطأ من مزوّد الخدمة",
                "خصم مكرر",
                "خطأ تقني في نظام الدفع مؤكَّد من مزوّد الخدمة",
                "إنهاء الخدمات بمبادرة من مزوّد الخدمة"
              ]
            }
          ]
        },
        {
          "title": "4. حالات الاسترداد الجزئي",
          "blocks": [
            {
              "type": "p",
              "text": "إذا عدل المستخدم عن استخدام الخدمة بعد بدء الفترة المدفوعة، يُرد المبلغ بما يتناسب مع فترة الاشتراك غير المستخدمة."
            },
            {
              "type": "p",
              "text": "مثال: تم دفع اشتراك لمدة 12 شهرًا؛ واستُخدمت 3 أشهر؛ يُحتسب الاسترداد عن الأشهر التسعة المتبقية."
            },
            {
              "type": "p",
              "text": "الحد الأدنى لفترة التسوية هو شهر تقويمي واحد."
            }
          ]
        },
        {
          "title": "5. الحالات التي لا يُرد فيها المبلغ",
          "blocks": [
            {
              "type": "p",
              "text": "لا يُرد المبلغ في الحالات التالية:"
            },
            {
              "type": "list",
              "items": [
                "استخدم المستخدم الخدمة فعليًا خلال الفترة المدفوعة",
                "خالف المستخدم شروط العرض",
                "تم تقييد الوصول بسبب مخالفة القانون الكازاخستاني",
                "امتنع المستخدم عن استخدام الخدمة دون إنهاء العقد",
                "كان غياب النتيجة من استخدام الخدمة ناتجًا عن أفعال المستخدم أو تقاعسه"
              ]
            }
          ]
        },
        {
          "title": "6. إجراءات طلب الاسترداد",
          "blocks": [
            {
              "type": "p",
              "text": "للحصول على الاسترداد، يرسل المستخدم طلبًا إلى عنوان البريد الإلكتروني لمزوّد الخدمة."
            },
            {
              "type": "p",
              "text": "يجب أن يحدّد الطلب:"
            },
            {
              "type": "list",
              "items": [
                "اسم المنظمة أو رائد الأعمال الفرد",
                "رقم التعريف التجاري/الفردي (BIN/IIN)",
                "بيانات الاتصال",
                "تاريخ الدفع",
                "مبلغ الدفع",
                "أسباب الاسترداد",
                "التفاصيل المصرفية للاسترداد"
              ]
            }
          ]
        },
        {
          "title": "7. مدة الاسترداد",
          "blocks": [
            {
              "type": "p",
              "text": "تُراجَع الطلبات خلال 10 (عشرة) أيام عمل."
            },
            {
              "type": "p",
              "text": "في حال الموافقة، يُرد المبلغ خلال 15 (خمسة عشر) يوم عمل إلى الحساب المصرفي المستخدم في الدفع الأصلي، أو إلى حساب آخر يحدده المستخدم."
            }
          ]
        },
        {
          "title": "8. أحكام ختامية",
          "blocks": [
            {
              "type": "p",
              "text": "يتم الاسترداد بالتنغي الكازاخستاني."
            },
            {
              "type": "p",
              "text": "تُحسم جميع النزاعات المتعلقة بالاسترداد عبر التفاوض."
            },
            {
              "type": "p",
              "text": "في حال تعذّر الاتفاق، يخضع النزاع لنظر محاكم جمهورية كازاخستان في مكان تسجيل مزوّد الخدمة."
            }
          ]
        }
      ]
    }
  }
};
